/**
 * i18n mínimo pero con andamiaje real para crecer.
 *
 * - `es` es la fuente de verdad (define las claves disponibles).
 * - `pt` es un diccionario parcial: lo que falte cae a `es` (fallback).
 * - Claves planas con prefijo por pantalla (login.*, register.*, home.*) para
 *   evitar strings sueltos por el código y facilitar el volcado a PT-BR.
 *
 * Interpolación simple con `{var}`: t('home.greeting', { name: 'Ana' }).
 *
 * El locale no cambia en caliente todavía (Fase 2 traerá el selector ES|PT);
 * por ahora se puede fijar con setLocale() al bootear.
 */

export type Locale = 'es' | 'pt';

// --- Diccionario base (español) — define el set de claves -------------------
const es = {
  // Comunes / marca
  'common.appName': 'Tembiapo',
  'common.tagline': 'Gestión de proyectos y tareas, sin complicaciones.',
  'common.support': 'Soporte',
  'common.poweredBy': 'desarrollado por Evoluado.com',
  'common.email': 'Email',
  'common.password': 'Contraseña',
  'common.loading': 'Cargando…',
  'common.retry': 'Reintentar',
  'common.genericError': 'Algo salió mal. Probá de nuevo en un momento.',
  'common.required': 'Este campo es obligatorio.',
  'common.emailInvalid': 'Ingresá un email válido.',
  'common.cancel': 'Cancelar',
  'common.save': 'Guardar',
  'ui.password.show': 'Mostrar contraseña',
  'ui.password.hide': 'Ocultar contraseña',
  'common.saving': 'Guardando…',
  'common.delete': 'Eliminar',
  'common.deleting': 'Eliminando…',
  'common.back': 'Volver',
  'common.close': 'Cerrar',
  'common.none': 'Sin asignar',
  'common.optional': 'Opcional',

  // Login
  'login.title': 'Ingresá a tu tembiapo',
  'login.subtitle': 'Gestioná tus proyectos y tu equipo.',
  'login.emailPlaceholder': 'vos@empresa.com',
  'login.passwordPlaceholder': 'Tu contraseña',
  'login.submit': 'Ingresar',
  'login.submitting': 'Ingresando…',
  'login.noAccount': '¿Todavía no tenés cuenta?',
  'login.createAccount': 'Creá tu empresa gratis',
  'login.error.invalid': 'Email o contraseña incorrectos.',
  'login.error.tooMany': 'Demasiados intentos. Probá de nuevo en unos minutos.',
  'login.forgot': '¿Olvidaste tu contraseña?',

  // Recuperar contraseña (olvidé mi contraseña)
  'forgot.title': 'Recuperá tu contraseña',
  'forgot.subtitle': 'Ingresá tu email y te enviamos un enlace para restablecerla.',
  'forgot.emailLabel': 'Email',
  'forgot.submit': 'Enviar enlace',
  'forgot.sent':
    'Si hay una cuenta con ese email, te enviamos un enlace para restablecer la contraseña. Revisá tu correo.',
  'forgot.backToLogin': 'Volver a ingresar',

  // Restablecer contraseña (desde el enlace del email)
  'reset.title': 'Elegí una nueva contraseña',
  'reset.subtitle': 'Creá una contraseña nueva para tu cuenta.',
  'reset.newPassword': 'Nueva contraseña',
  'reset.confirmPassword': 'Confirmá la contraseña',
  'reset.mismatch': 'Las contraseñas no coinciden.',
  'reset.submit': 'Guardar contraseña',
  'reset.success': 'Tu contraseña se actualizó.',
  'reset.expired': 'El enlace venció o ya se usó. Pedí uno nuevo.',
  'reset.invalid': 'El enlace no es válido. Pedí uno nuevo.',
  'reset.toLogin': 'Ingresar',

  // Login con biometría (sólo app nativa)
  'auth.biometric.cta': 'Ingresar con Face ID / huella',
  'auth.biometric.verifying': 'Verificando…',
  'auth.biometric.or': 'o',
  'auth.biometric.title': 'Ingresá con biometría',
  'auth.biometric.reason': 'Confirmá tu identidad para entrar a Tembiapo.',

  // Login con Google (OAuth) + reCAPTCHA v3
  'auth.google.separator': 'o',
  'auth.google.error': 'No pudimos ingresar con Google. Probá de nuevo.',
  'auth.google.inactive': 'Tu cuenta no está activa. Escribinos a soporte.',
  'auth.captcha.failed': 'No pudimos verificar la seguridad. Recargá la página e intentá de nuevo.',

  // Registro
  'register.title': 'Creá tu empresa en Tembiapo',
  'register.subtitle': 'Elegí tu rubro y tu plan. Empezás en segundos.',
  'register.freeBadge': '30 días gratis · sin tarjeta',
  'register.quickSubtitle': 'Creá tu cuenta en segundos. Elegís tu rubro y plan después.',
  'register.quickReassure': '30 días gratis · sin tarjeta · cancelás cuando quieras',
  'register.terms.pre': 'Acepto los',
  'register.terms.tos': 'Términos y Condiciones',
  'register.terms.and': 'y la',
  'register.terms.privacy': 'Política de Privacidad',
  'register.terms.post': ', y doy mi consentimiento para el tratamiento de mis datos.',
  'register.terms.error': 'Debés aceptar los Términos y la Política de Privacidad para continuar.',
  'onboarding.vertical.title': '¿A qué se dedica tu empresa?',
  'onboarding.vertical.subtitle': 'Elegí tu rubro y te armamos los tableros y plantillas para vos.',
  'onboarding.vertical.skip': 'Elegir después',
  'register.step.vertical': 'Elegí tu rubro',
  'register.step.verticalHint': 'Precargamos proyectos y plantillas según tu actividad.',
  'register.step.plan': 'Elegí tu plan',
  'register.step.planHint': 'Empezás con 30 días gratis: hoy no pagás nada, el cobro empieza recién a los 30 días. Podés cambiar de plan cuando quieras.',
  'register.step.planPending': 'Elegí primero tu rubro para ver los planes.',
  'register.step.account': 'Datos de tu empresa y tu cuenta',
  'register.billing.monthly': 'Mensual',
  'register.billing.yearly': 'Anual',
  'register.billing.yearlyHint': '2 meses de regalo',
  'register.plan.projects': '{n} proyectos',
  'register.plan.users': '{n} usuarios',
  'register.plan.perMonth': '/mes',
  'register.plan.perYear': '/año',
  'register.plan.trialNote': 'Hoy pagás {zero} · se cobra a los 30 días',
  'register.plan.choose': 'Elegir',
  'register.plan.chosen': 'Elegido',
  'register.company.name': 'Nombre de la empresa',
  'register.company.namePlaceholder': 'Mi Empresa S.A.',
  'register.admin.firstName': 'Nombre',
  'register.admin.lastName': 'Apellido',
  'register.admin.passwordPlaceholder': 'Mínimo 8 caracteres',
  'register.admin.passwordConfirm': 'Repetir contraseña',
  'register.password.weak': 'Débil',
  'register.password.fair': 'Aceptable',
  'register.password.strong': 'Fuerte',
  'register.password.tooShort': 'Usá al menos 8 caracteres.',
  'register.password.needMix': 'Combiná letras y números.',
  'register.password.mismatch': 'Las contraseñas no coinciden.',
  'register.submit': 'Crear mi empresa',
  'register.submitting': 'Creando tu empresa…',
  'register.selectPlanFirst': 'Elegí un plan para continuar.',
  'register.error.emailTaken': 'Ese email ya está registrado. ¿Querés ingresar?',
  'register.haveAccount': '¿Ya tenés cuenta?',
  'register.goLogin': 'Ingresá',
  'register.loadError': 'No pudimos cargar los rubros y planes.',

  // Home / dashboard
  'home.greeting': 'Hola, {name}',
  'home.subtitle': 'Este es el tablero de {company}.',
  'home.userMenu.logout': 'Cerrar sesión',
  'home.userMenu.account': 'Mi cuenta',
  'home.trial.active': 'Estás en tu prueba gratis: te quedan {days} días.',
  'home.trial.lastDays': '¡Tu prueba gratis termina en {days} días!',
  'home.trial.lastDay': '¡Tu prueba gratis termina hoy!',
  'home.trial.expired': 'Tu prueba gratis terminó. Activá tu plan para seguir trabajando.',
  'home.trial.cta': 'Activar mi plan',
  'home.empty.title': 'Creá tu primer proyecto',
  'home.empty.body': 'Un proyecto es tu tablero de trabajo. Arrancá desde una plantilla de tu rubro o desde cero.',
  'home.empty.cta': 'Crear proyecto',
  'home.empty.soon': 'Disponible en el próximo milestone',
  'home.empty.memberTitle': 'Todavía no hay proyectos',
  'home.empty.memberBody': 'Esperá que un administrador te asigne tareas.',
  'home.quick.title': 'Acciones rápidas',
  'home.quick.newProjectDesc': 'Armá un tablero desde una plantilla de tu rubro o desde cero.',
  'home.quick.invite': 'Invitar miembros',
  'home.quick.inviteDesc': 'Sumá a tu equipo para trabajar juntos.',
  'home.quick.pair': 'Conectar app móvil',
  'home.quick.pairDesc': 'Generá el QR para vincular la app en tu teléfono.',
  'home.comments.title': 'Últimos comentarios',
  'home.comments.empty': 'Sin comentarios aún.',
  'home.activity.title': 'Últimos cambios de estado',
  'home.activity.empty': 'Sin actividad reciente.',
  'home.team.title': 'Equipo',
  'home.team.empty': 'Todavía no hay miembros activos.',
  'home.team.invite': 'Invitar',
  'home.team.viewAll': 'Ver todos',
  'home.team.emailAria': 'Enviar email a {name}',
  'home.team.phoneAria': 'Llamar a {name}',
  'home.readonly.locked': 'Activá tu plan para volver a editar.',

  // Errores de sesión
  'session.expired': 'Tu sesión expiró. Ingresá de nuevo.',

  // Navegación
  'nav.projects': 'Proyectos',
  'nav.home': 'Inicio',
  'nav.reports': 'Reportes',
  'nav.myTasks': 'Mis tareas',
  'nav.agenda': 'Agenda',

  // Calendario combinado (feature calendar_agenda)
  'agendaPage.title': 'Agenda',
  'agendaPage.subtitle': 'Todos los eventos con hora: los míos y los del equipo.',
  'agendaPage.mine': 'Míos',
  'agendaPage.team': 'Del equipo',
  'agendaPage.noFeature': 'La agenda con horarios no está activada en tu cuenta.',
  'agendaPage.noEventsDay': 'Sin eventos este día.',

  // Buscador global
  'search.open': 'Buscar',
  'search.title': 'Buscar',
  'search.placeholder': 'Buscar proyectos y tareas…',
  // Alta rápida de tarea (FAB móvil)
  'quickTask.button': 'Nueva tarea',
  'quickTask.title': 'Nueva tarea rápida',
  'quickTask.project': 'Proyecto',
  'quickTask.column': 'Columna',
  'quickTask.titleLabel': 'Título',
  'quickTask.assignMe': 'Asignármela a mí',
  'quickTask.create': 'Crear tarea',
  'quickTask.titleRequired': 'Escribí un título.',
  'quickTask.columnRequired': 'Elegí una columna.',
  'quickTask.error': 'No se pudo crear la tarea. Probá de nuevo.',
  'search.hint': 'Escribí para buscar proyectos y tareas.',
  'search.empty': 'Sin resultados.',
  'search.projects': 'Proyectos',
  'search.tasks': 'Tareas',
  'search.close': 'Cerrar',

  // Agenda / evento (feature calendar_agenda)
  'agenda.title': 'Agenda',
  'agenda.date': 'Fecha del evento',
  'agenda.time': 'Hora',
  'agenda.until': 'Hasta (opcional)',
  'agenda.location': 'Lugar',
  'agenda.locationPh': 'Juzgado, sala, dirección…',
  'agenda.meetingUrl': 'Link de sala virtual',
  'agenda.subscribe': 'Suscribir agenda',
  'agenda.subscribeTitle': 'Suscribí tu agenda',
  'agenda.subscribeHint':
    'Pegá esta dirección en Google Calendar, Apple o Outlook para ver tus audiencias y eventos en tu calendario, siempre actualizados.',
  'agenda.subscribeSteps':
    'En Google Calendar: “Otros calendarios” → “Suscribirse a un calendario” → pegá la URL.',
  'agenda.copy': 'Copiar',
  'agenda.copied': 'Copiado',
  // Conflictos/cercanía de horario
  'agenda.conflictTitle': 'Hora comprometida',
  'agenda.nearTitle': 'Muy cerca de otro evento',
  'agenda.you': 'vos',
  'agenda.someone': 'alguien del equipo',
  'agenda.nearGap': '{n} min de diferencia',

  // Datos del caso (campos personalizados en el calendario, feature calendar_agenda)
  'caseData.title': 'Datos del caso',
  'caseData.hint':
    'Completá los datos de este proyecto (juzgado, expediente, etc.). Marcá los que quieras ver en el calendario.',
  'caseData.showInCalendar': 'Mostrar en el calendario',

  // Importar nota de Plaud (grabadora IA) — Fase 1: pegar la nota como tarea
  'plaud.button': 'Importar de Plaud',
  'plaud.title': 'Importar nota de Plaud',
  'plaud.hint':
    'Pegá la nota de tu grabación de Plaud (resumen y/o transcripción). Se crea una tarea con todo el texto en la descripción, para que quede en el caso.',
  'plaud.titleLabel': 'Título de la tarea',
  'plaud.columnLabel': 'Columna',
  'plaud.noteLabel': 'Nota / transcripción',
  'plaud.notePlaceholder': 'Pegá acá el resumen y la transcripción de Plaud…',
  'plaud.create': 'Crear tarea',
  'plaud.defaultTitlePrefix': 'Reunión —',
  'plaud.noteRequired': 'Pegá la nota o transcripción.',
  'plaud.columnRequired': 'Elegí una columna.',
  'plaud.saveError': 'No se pudo crear la tarea. Probá de nuevo.',

  // Asistente IA (bot de ayuda flotante)
  'assistant.open': 'Abrir asistente',
  'assistant.close': 'Cerrar asistente',
  'assistant.title': 'Asistente Tembiapo',
  'assistant.subtitle': 'Te ayudo con tus tareas',
  'assistant.greeting': '¡Hola! Puedo consultar tus tareas y proyectos, y crear o mover tareas por vos. ¿En qué te doy una mano?',
  'assistant.placeholder': 'Escribí tu mensaje…',
  'assistant.send': 'Enviar',
  'assistant.thinking': 'Pensando…',
  'assistant.error': 'No pude procesar eso ahora. Probá de nuevo.',
  'assistant.proposed': 'Preparé esta acción para vos:',
  'assistant.done': 'Listo ✅',
  'assistant.cancelled': 'Cancelado, no hice ningún cambio.',
  'assistant.confirmTitle': 'Confirmá esta acción',
  'assistant.confirm': 'Confirmar',
  'assistant.cancel': 'Cancelar',
  'assistant.disclaimer': 'Asistente con IA en fase piloto. Revisá los datos antes de confirmar.',
  'assistant.sug.pending': '¿Qué tengo pendiente?',
  'assistant.sug.progress': '¿Cómo van mis proyectos?',
  'assistant.sug.create': 'Crear una tarea',
  'assistant.counter': 'Gratis hoy: {n} · Créditos: {c}',
  'assistant.buyTitle': 'Se acabaron tus consultas de hoy.',
  'assistant.buyCta': 'Comprar créditos',
  'assistant.buyAskAdmin': 'Se acabaron las consultas. Pedile a un administrador que compre créditos de IA.',

  // Créditos de IA (facturación)
  'credits.title': 'Créditos de IA',
  'credits.subtitle': 'Consultas del asistente. Cada usuario tiene {n} gratis por día; agotadas, se usan créditos de la empresa.',
  'credits.balance': 'Créditos disponibles',
  'credits.freeTodayLabel': 'Tu uso gratis de hoy',
  'credits.freeToday': '{used} de {total}',
  'credits.pack': '{n} créditos',
  'credits.buy': 'Comprar',
  'credits.buyError': 'No pudimos iniciar la compra. Probá de nuevo.',
  'credits.none': 'No hay paquetes disponibles por ahora.',
  'credits.cardDisabled': 'La compra con tarjeta no está habilitada por ahora.',
  'credits.return.confirmed': '¡Créditos acreditados! Ya podés usarlos con el asistente.',
  'credits.return.rejected': 'El pago no se completó. No se acreditaron créditos.',
  'credits.return.verifying': 'Verificando tu pago…',
  'credits.freeSub': 'consultas gratis hoy',
  'credits.personal': 'Tus créditos',
  'credits.company': 'Créditos de la empresa',
  'credits.scope.title': '¿Para quién comprás?',
  'credits.scope.personal': 'Solo para mí',
  'credits.scope.company': 'Para toda la empresa',
  'credits.scope.personalHint': 'Los usás solo vos.',
  'credits.scope.companyHint': 'Los puede usar cualquier miembro del equipo.',
  'credits.scope.memberNote': 'Los créditos que compres son solo para vos. Un administrador puede comprar para todo el equipo.',
  'credits.scope.adminOnly': 'Solo un administrador puede comprar créditos para toda la empresa.',
  'credits.perCredit': '{price} por crédito',
  'credits.buyForMe': 'Comprar para mí',
  'credits.buyForCompany': 'Comprar para la empresa',
  'credits.cardNotice': 'La compra es con tarjeta. El pago lo procesa la pasarela segura de Evoluado.',
  'credits.linkFromBilling': 'Comprá créditos para vos o para todo el equipo, y mirá tu saldo.',

  'mytasks.title': 'Mis tareas',
  'mytasks.subtitle': 'Todo lo asignado a vos, de todos los proyectos.',
  'mytasks.empty': 'No tenés tareas asignadas',
  'mytasks.emptyHint': 'Cuando te asignen una tarea o una subtarea, va a aparecer acá.',
  'mytasks.showDone': 'Ver hechas ({n})',
  'mytasks.viaSubtask': 'vía subtarea',
  'mytasks.today': 'Hoy',
  'mytasks.thisWeek': 'Esta semana',
  'mytasks.weekOf': 'Semana del {date}',
  'mytasks.bucket.overdue': 'Atrasadas',
  'mytasks.bucket.today': 'Hoy',
  'mytasks.bucket.week': 'Próximos 7 días',
  'mytasks.bucket.later': 'Más adelante',
  'mytasks.bucket.none': 'Sin fecha',

  // Menú móvil (sándwich)
  'menu.open': 'Abrir menú',
  'menu.close': 'Cerrar menú',
  'menu.sectionNav': 'Navegar',
  'menu.sectionAccount': 'Cuenta',
  'menu.language': 'Idioma',
  'menu.theme': 'Tema',

  // Proyectos (lista)
  'projects.title': 'Proyectos',
  'projects.subtitle': 'Tus tableros de trabajo.',
  'projects.new': 'Nuevo proyecto',
  'projects.loadError': 'No pudimos cargar tus proyectos.',
  'projects.taskCount': '{n} tareas',
  'projects.taskCountOne': '1 tarea',
  'projects.taskCountZero': 'Sin tareas',
  'projects.empty.title': 'Creá tu primer proyecto',
  'projects.empty.body':
    'Un proyecto es tu tablero de trabajo. Arrancá desde una plantilla de tu rubro o desde cero.',
  'projects.empty.cta': 'Crear proyecto',
  'projects.empty.memberTitle': 'Todavía no hay proyectos',
  'projects.empty.memberBody': 'Esperá que un administrador te asigne tareas.',
  'projects.open': 'Abrir tablero',

  // Acciones sobre un proyecto (renombrar / eliminar)
  'project.actions': 'Acciones del proyecto',
  'project.rename': 'Renombrar',
  'project.rename.title': 'Renombrar proyecto',
  'project.name': 'Nombre',
  'project.description': 'Descripción',
  'project.namePlaceholder': 'Rediseño del sitio',
  'project.descriptionPlaceholder': '¿De qué trata este proyecto?',
  'project.name.required': 'El nombre del proyecto es obligatorio.',
  'project.rename.error': 'No pudimos guardar los cambios. Probá de nuevo.',
  'project.delete': 'Eliminar',
  'project.delete.title': 'Eliminar proyecto',
  'project.delete.confirm':
    'Se eliminará el proyecto “{name}” y todas sus tareas, columnas y comentarios. Esta acción no se puede deshacer.',
  'project.delete.submit': 'Eliminar proyecto',
  'project.delete.error': 'No pudimos eliminar el proyecto. Probá de nuevo.',
  'project.delete.forbidden': 'Sólo un administrador de la empresa puede eliminar proyectos.',
  'project.notFound': 'Este proyecto ya no existe.',
  'project.favorite': 'Marcar como favorito',
  'project.unfavorite': 'Quitar de favoritos',
  'project.reorder': 'Arrastrar para reordenar',

  // Visibilidad del proyecto (control de acceso; se enforza en el backend)
  'project.visibility': 'Visibilidad',
  'project.visibility.public': 'Público',
  'project.visibility.private': 'Privado',
  'project.visibility.publicHint': 'Todo el equipo ve todas las tareas del proyecto.',
  'project.visibility.privateHint': 'Cada persona sólo ve las tareas donde está asignada. El administrador y quien creó el proyecto ven todas.',
  'project.visibility.badge': 'Privado',

  // Crear proyecto
  'projects.create.title': 'Nuevo proyecto',
  'projects.create.subtitle': 'Elegí una plantilla o empezá en blanco.',
  'projects.create.templateStep': 'Elegí una plantilla',
  'projects.create.detailsStep': 'Nombre del proyecto',
  'projects.create.blank': 'En blanco',
  'projects.create.blankHint': 'Un tablero vacío para armar a tu manera.',
  'projects.create.columnsPreview': 'Columnas',
  'projects.create.fields': '{n} campos',
  'projects.create.fieldsOne': '1 campo',
  'projects.create.name': 'Nombre',
  'projects.create.namePlaceholder': 'Rediseño del sitio',
  'projects.create.description': 'Descripción',
  'projects.create.descriptionPlaceholder': '¿De qué trata este proyecto?',
  'projects.create.submit': 'Crear proyecto',
  'projects.create.submitting': 'Creando…',
  'projects.create.loadError': 'No pudimos cargar las plantillas.',
  'projects.create.limitReached':
    'Alcanzaste el límite de proyectos de tu plan. Activá o mejorá tu plan para crear más.',
  'projects.create.limitCta': 'Ver mi plan',

  // Tablero (Kanban)
  'board.loadError': 'No pudimos cargar el tablero.',
  'board.moveError': 'No pudimos mover la tarea. Probá de nuevo.',
  'board.notFound': 'Este proyecto no existe o no tenés acceso.',
  'board.backToProjects': 'Proyectos',
  'board.empty': 'Este tablero todavía no tiene columnas.',
  'board.taskCount': '{n} tareas',
  'board.taskCountOne': '1 tarea',
  'board.column.count': '{n}',
  'board.column.tasks': 'Tareas en la columna',
  'board.column.wip': 'Límite de trabajo en curso: {n}',
  'board.column.wipReached': 'Llegaste al límite de {n}.',
  'board.column.wipOver': 'Te pasaste del límite ({n}).',
  'board.addTask': 'Agregar tarea',
  'board.addTaskPlaceholder': 'Título de la tarea…',
  'board.addTaskSubmit': 'Agregar',
  'board.addTaskHint': 'Enter para agregar · Esc para cerrar',
  'board.emptyColumn': 'Sin tareas todavía',
  'board.emptyColumnHint': 'Arrastrá tareas acá o agregá una nueva.',
  'board.readOnly.title': 'Tablero en solo lectura',
  'board.readOnly.body': 'Tu prueba gratis terminó. Activá tu plan para volver a mover y editar tareas.',
  'board.readOnly.cta': 'Activar mi plan',
  'board.spectator.title': 'Solo lectura',
  'board.spectator.body': 'No sos del equipo de este proyecto. Podés ver y comentar.',

  // Equipo del proyecto (quién puede editar)
  'projectTeam.button': 'Equipo',
  // Compartir por link de solo lectura (migr. 041)
  'share.button': 'Compartir',
  'share.title': 'Compartir en solo lectura',
  'share.subtitle': 'Generá un link para que alguien de afuera siga el avance, sin cuenta ni poder editar.',
  'share.generate': 'Generar link',
  'share.copy': 'Copiar',
  'share.copied': '¡Copiado!',
  'share.share': 'Compartir por WhatsApp…',
  'share.revoke': 'Revocar link',
  'share.hint': 'Cualquiera con el link puede VER (no editar ni comentar). Podés revocarlo cuando quieras.',
  // Métricas de los cards (proyecto/carpeta)
  'cardm.progressHint': '{done} de {total} en la etapa final',
  'cardm.members': '{n} personas trabajando',
  'cardm.overdue': '{n} atrasadas',
  'cardm.comments': '{n} mensajes',
  'projectTeam.title': 'Equipo del proyecto',
  'projectTeam.subtitle': 'Quién puede editar este proyecto. El resto sólo puede ver y comentar.',
  'projectTeam.members': 'Integrantes',
  'projectTeam.empty': 'Todavía no agregaste a nadie al equipo.',
  'projectTeam.loadError': 'No pudimos cargar el equipo.',
  'projectTeam.addLabel': 'Agregar al equipo',
  'projectTeam.addPlaceholder': 'Elegí un miembro…',
  'projectTeam.addSubmit': 'Agregar',
  'projectTeam.allAdded': 'Todos los miembros de la empresa ya están en el equipo.',
  'projectTeam.remove': 'Quitar del equipo',
  'projectTeam.removeConfirm': '¿Quitar a {name} del equipo? Sus tareas asignadas no se modifican.',
  'projectTeam.addError': 'No pudimos agregar a esa persona. Probá de nuevo.',
  'projectTeam.removeError': 'No pudimos quitar a esa persona. Probá de nuevo.',
  'projectTeam.error.forbidden': 'No tenés permiso para gestionar el equipo de este proyecto.',
  'projectTeam.error.notCompany': 'Esa persona no pertenece a tu empresa.',

  // Tarea (tarjeta y edición)
  'task.priority.LOW': 'Baja',
  'task.priority.MEDIUM': 'Media',
  'task.priority.HIGH': 'Alta',
  'task.priority.URGENT': 'Urgente',
  'task.edit.title': 'Editar tarea',
  'task.expand': 'Ver en pantalla completa',
  'task.collapse': 'Volver al panel lateral',
  'task.field.title': 'Título',
  'task.field.description': 'Descripción',
  'task.field.priority': 'Prioridad',
  'task.field.assignee': 'Responsable',
  'task.field.dueDate': 'Fecha límite',
  'task.overdue': 'Vencida',
  'task.dueToday': 'Vence hoy',
  'task.dueSoon': 'Vence pronto',
  'task.saveError': 'No pudimos guardar los cambios.',
  'task.deleteConfirm': '¿Eliminar esta tarea? No se puede deshacer.',

  // Crear proyecto — galería de plantillas
  'projects.create.freeBadge': '30 días gratis · sin tarjeta',
  'projects.create.blankBadge': 'Desde cero',
  'projects.create.pickTemplate': 'Elegí un punto de partida',
  'projects.create.columnsCount': '{n} columnas',

  // Switch de vista (Kanban / Lista / Calendario / Timeline)
  'view.label': 'Vista',
  'view.kanban': 'Kanban',
  'view.list': 'Lista',
  'view.calendar': 'Calendario',
  'view.timeline': 'Timeline',

  // Vista Calendario (mensual)
  'calendar.today': 'Hoy',
  'calendar.prevMonth': 'Mes anterior',
  'calendar.nextMonth': 'Mes siguiente',
  'calendar.moreCount': '+{n} más',
  'calendar.less': 'Ver menos',
  'calendar.noColumns': 'Este tablero todavía no tiene columnas.',
  'calendar.noDate.title': 'Sin fecha',
  'calendar.noDate.empty': 'Todas las tareas tienen fecha.',
  'calendar.dragHint': 'Arrastrá una tarea a un día para asignarle fecha.',
  'calendar.upcoming': 'Próximos eventos',
  'calendar.showUpcoming': 'Ver próximos',
  'calendar.noEvents': 'Sin eventos para mostrar.',
  'calendar.conflict': 'Conflicto',

  // Vista Timeline / Gantt simple
  'timeline.groupBy': 'Agrupar por',
  'timeline.group.column': 'Estado',
  'timeline.group.assignee': 'Responsable',
  'timeline.taskCol': 'Tarea',
  'timeline.today': 'Hoy',
  'timeline.range': 'Próximas {n} semanas',
  'timeline.hidden': '{n} tareas sin fecha o fuera de rango ocultas',
  'timeline.hiddenOne': '1 tarea sin fecha o fuera de rango oculta',
  'timeline.noColumns': 'Este tablero todavía no tiene columnas.',
  'timeline.empty': 'No hay tareas con fecha límite en este rango.',

  // Vista Lista/Tabla
  'list.search': 'Buscar',
  'list.searchPlaceholder': 'Buscar por título…',
  'list.filter.column': 'Estado',
  'list.filter.priority': 'Prioridad',
  'list.filter.assignee': 'Responsable',
  'list.filter.all': 'Todos',
  'list.group.label': 'Agrupar por',
  'list.group.column': 'Estado',
  'list.group.assignee': 'Responsable',
  'list.group.priority': 'Prioridad',
  'list.group.none': 'Sin agrupar',
  'list.col.title': 'Título',
  'list.col.status': 'Estado',
  'list.col.priority': 'Prioridad',
  'list.col.assignee': 'Responsable',
  'list.col.dueDate': 'Fecha límite',
  'list.col.subtasks': 'Subtareas',
  'list.col.actions': 'Acciones',
  'list.results': '{n} resultados',
  'list.resultsOne': '1 resultado',
  'list.resultsZero': 'Sin resultados',
  'list.sortBy': 'Ordenar por {col}',
  'list.subtasks': '{n} subtareas',
  'list.inSubtasks': 'en subtareas',
  'list.subtaskAssignees': 'Responsables de subtareas',
  'list.empty.title': 'Sin tareas todavía',
  'list.empty.body': 'Creá tu primera tarea con el alta rápida de arriba.',
  'list.noResults.title': 'Nada coincide con tu búsqueda',
  'list.noResults.body': 'Probá cambiar los filtros o el texto de búsqueda.',
  'list.clearFilters': 'Limpiar filtros',
  'list.quickAdd.placeholder': 'Nueva tarea…',
  'list.quickAdd.column': 'Columna destino',
  'list.quickAdd.submit': 'Agregar',
  'list.open': 'Abrir tarea',
  'list.noColumns': 'Este tablero todavía no tiene columnas.',

  // Navegación (menú de usuario)
  'nav.team': 'Equipo',
  'nav.profile': 'Perfil',
  'nav.connectors': 'Conectores',

  // Página "Conectores" (descargar la app + conector de IA)
  'connectors.title': 'Conectores',
  'connectors.subtitle':
    'Descargá la app móvil y conectá tu asistente de IA para usar Tembiapo desde donde estés.',

  // Genéricos de esta fase
  'common.copy': 'Copiar link',
  'common.copied': '¡Copiado!',
  'common.optionalLabel': '{label} (opcional)',
  'common.savedOk': 'Cambios guardados.',

  // Roles / estados
  'role.SUPER_ADMIN': 'Super admin',
  'role.COMPANY_ADMIN': 'Administrador',
  'role.MEMBER': 'Miembro',
  'status.ACTIVE': 'Activo',
  'status.SUSPENDED': 'Suspendido',
  'subBanner.blockedTitle': 'Tu cuenta está bloqueada',
  'subBanner.blockedDesc': 'Se bloqueó por falta de pago. Regularizá para recuperar el acceso.',
  'subBanner.trialTitle': 'Tu prueba gratis terminó',
  'subBanner.trialDesc': 'Elegí un plan y pagá para seguir usando Tembiapo.',
  'subBanner.pay': 'Pagar para desbloquear',
  'subBanner.askAdmin': 'Pedile a un administrador que regularice el pago.',
  'subBanner.update': 'Actualizar pago',
  'subBanner.grace.trialTitle': 'Tu prueba gratis terminó',
  'subBanner.grace.trialDesc':
    'Te quedan {days} días de gracia para seguir usando Tembiapo. Actualizá tu pago para no perder el acceso.',
  'subBanner.grace.dueTitle': 'Tu pago está vencido',
  'subBanner.grace.dueDesc':
    'Te quedan {days} días de gracia. Actualizá tu pago para mantener tu cuenta activa.',
  'subBanner.grace.lastDayTitle': 'Último día de gracia',
  'subBanner.grace.lastDayDesc': 'Actualizá tu pago hoy para no perder el acceso.',
  'status.INVITED': 'Invitado',
  'status.PENDING': 'Pendiente',

  // Equipo
  'team.title': 'Equipo',
  'team.subtitle': 'Gestioná quién trabaja en {company}.',
  'team.invite': 'Invitar',
  'team.loadError': 'No pudimos cargar tu equipo.',
  'team.restricted.title': 'Acceso restringido',
  'team.restricted.body': 'Solo los administradores pueden gestionar el equipo.',
  'team.members.title': 'Miembros',
  'team.members.count': '{n} miembros',
  'team.col.name': 'Nombre',
  'team.col.email': 'Email',
  'team.col.jobTitle': 'Cargo',
  'team.col.role': 'Rol',
  'team.col.status': 'Estado',
  'team.member.remove': 'Quitar',
  'team.member.removeConfirm':
    '¿Quitar a {name} de esta empresa? Perderá acceso a esta empresa. Su cuenta y sus otras empresas no se tocan.',
  'team.member.lastAdmin': 'No podés quitar al último administrador de la empresa.',
  'team.member.removeError': 'No pudimos quitar al miembro. Probá de nuevo.',
  'team.role.change': 'Cambiar rol',
  'team.role.changeError': 'No pudimos cambiar el rol. Probá de nuevo.',
  'team.col.actions': 'Acciones',
  'team.noJobTitle': '—',
  'team.pending.title': 'Invitaciones pendientes',
  'team.pending.count': '{n} pendientes',
  'team.pending.empty': 'No hay invitaciones pendientes.',
  'team.pending.expires': 'Vence el {date}',
  'team.pending.revoke': 'Revocar',
  'team.pending.revokeConfirm': '¿Revocar la invitación de {email}?',
  'team.invite.shareText': 'Te invito a sumarte a nuestro equipo en Tembiapo. Entrá con este link:',
  'team.pending.copyUnavailable': 'El link no está disponible. Reenviá la invitación para obtener uno nuevo.',

  // Modal de invitación
  'team.inviteModal.title': 'Invitar a tu equipo',
  'team.inviteModal.subtitle': 'Le enviaremos un link para completar su cuenta.',
  'team.inviteModal.email': 'Email',
  'team.inviteModal.emailPlaceholder': 'persona@empresa.com',
  'team.inviteModal.firstName': 'Nombre',
  'team.inviteModal.lastName': 'Apellido',
  'team.inviteModal.jobTitle': 'Cargo',
  'team.inviteModal.jobTitlePlaceholder': 'Diseñadora, Dev, PM…',
  'team.inviteModal.role': 'Rol',
  'team.inviteModal.submit': 'Enviar invitación',
  'team.inviteModal.submitting': 'Enviando…',
  'team.inviteModal.another': 'Invitar a otra persona',
  'team.inviteModal.done': 'Listo',
  'team.inviteModal.successTitle': '¡Invitación creada!',
  'team.inviteModal.successBody': 'Compartí este link con {email} para que complete su cuenta:',
  'team.error.emailTaken': 'Ese email ya pertenece a un miembro de tu equipo.',
  'team.error.alreadyInvited': 'Ya hay una invitación pendiente para ese email.',
  'team.error.limit': 'Alcanzaste el límite de usuarios de tu plan. Activá o mejorá tu plan para invitar a más personas.',
  'team.error.limitCta': 'Ver mi plan',

  // Aceptar invitación (pública)
  'invite.title': 'Sumate a {company}',
  'invite.subtitle': 'Completá tus datos y definí tu contraseña para empezar.',
  'invite.emailLabel': 'Vas a ingresar con',
  'invite.roleLabel': 'Rol asignado',
  'invite.firstName': 'Nombre',
  'invite.lastName': 'Apellido',
  'invite.jobTitle': 'Cargo',
  'invite.phone': 'Teléfono',
  'invite.password': 'Contraseña',
  'invite.passwordConfirm': 'Repetir contraseña',
  'invite.submit': 'Crear mi cuenta',
  'invite.submitting': 'Creando tu cuenta…',
  'invite.loading': 'Cargando invitación…',
  'invite.invalid.title': 'Invitación inválida',
  'invite.invalid.body': 'Este link no es válido. Pedile a tu equipo que te reenvíe la invitación.',
  'invite.expired.title': 'Invitación vencida',
  'invite.expired.body': 'Este link ya venció. Pedile a tu equipo una invitación nueva.',
  'invite.accepted.title': 'Invitación ya usada',
  'invite.accepted.body': 'Esta invitación ya fue aceptada. Ingresá con tu email y contraseña.',
  'invite.goLogin': 'Ir a ingresar',
  // Invitación para un email que YA tiene cuenta (usuario logueado se suma)
  'invite.join.subtitle': 'Aceptá la invitación para sumarte a {company} con tu cuenta actual.',
  'invite.join.cta': 'Aceptar invitación',
  'invite.join.joining': 'Sumándote…',
  'invite.join.wrongUser':
    'Esta invitación es para {invited}, pero estás usando {current}. Ingresá con la cuenta invitada para aceptarla.',
  'invite.join.loginAs': 'Ingresar como {email}',
  'invite.joined.title': 'Te sumaste a {company}',
  'invite.joined.body': 'Ya formás parte de esta empresa. ¿Querés cambiarte a ella ahora?',
  'invite.joined.switch': 'Cambiar a {company}',
  'invite.joined.stay': 'Seguir en mi empresa actual',
  // Aviso en /login cuando se llega desde una invitación (email con cuenta)
  'login.invite.notice': 'Ingresá con tu cuenta para aceptar la invitación.',

  // Verificación de email
  'verifyEmail.verifying.title': 'Verificando tu email…',
  'verifyEmail.verifying.body': 'Dame un segundo mientras confirmo tu dirección.',
  'verifyEmail.success.title': '¡Email verificado!',
  'verifyEmail.success.body': 'Tu dirección quedó confirmada. Ya podés usar Tembiapo con normalidad.',
  'verifyEmail.success.goApp': 'Ir a mi tembiapo',
  'verifyEmail.success.goLogin': 'Ir a ingresar',
  'verifyEmail.error.title': 'No pudimos verificar tu email',
  'verifyEmail.error.body': 'Este link no es válido o ya venció. Pedí uno nuevo desde la app e intentá otra vez.',
  'verifyEmail.error.goLogin': 'Ir a ingresar',
  // Banner en la app autenticada
  'verifyEmail.banner.text': 'Te enviamos un correo para verificar tu email. Revisá tu bandeja (y el spam).',
  'verifyEmail.banner.resend': 'Reenviar',
  'verifyEmail.banner.resending': 'Enviando…',
  'verifyEmail.banner.sent': '¡Correo reenviado! Revisá tu bandeja.',
  'verifyEmail.banner.tooMany': 'Recién te lo enviamos. Esperá un momento antes de reintentar.',
  'verifyEmail.banner.error': 'No pudimos reenviar el correo. Probá de nuevo en un momento.',
  'verifyEmail.banner.dismiss': 'Ocultar',

  // Gate suave de verificación (banner persistente en el shell autenticado)
  'verifyGate.body':
    'Verificá tu email para crear proyectos e invitar a tu equipo. Revisá tu bandeja (y la carpeta de spam).',
  'verifyGate.resend': 'Reenviar email',
  'verifyGate.sent': 'Te reenviamos el email ✓',
  'verifyGate.error': 'No pudimos reenviar. Probá de nuevo.',
  // Gate BLOQUEANTE (pantalla completa) — verificación de email obligatoria
  'verifyGate.title': 'Verificá tu email para continuar',
  'verifyGate.wallBody':
    'Para proteger tu cuenta, verificar tu email es obligatorio antes de usar TEMBIAPO. Te enviamos un enlace de verificación a:',
  'verifyGate.wallHint':
    'Abrí ese email y hacé clic en el enlace para activar tu cuenta. Revisá también la carpeta de spam o promociones.',
  'verifyGate.checked': 'Ya verifiqué',
  'verifyGate.stillPending':
    'Todavía no confirmamos tu email. Si ya hiciste clic en el enlace, esperá unos segundos y volvé a intentar.',
  'verifyGate.logout': 'Cerrar sesión',

  // Perfil
  'profile.title': 'Mi perfil',
  'profile.subtitle': 'Actualizá tus datos personales.',
  'profile.firstName': 'Nombre',
  'profile.lastName': 'Apellido',
  'profile.jobTitle': 'Cargo',
  'profile.department': 'Departamento / Área',
  'profile.nationalId': 'Documento',
  'profile.phone': 'Teléfono',
  'profile.locale': 'Idioma',
  'profile.locale.es': 'Español',
  'profile.locale.pt': 'Português (Brasil)',
  'profile.timezone': 'Zona horaria',
  'profile.emailLabel': 'Email',
  'profile.emailHint': 'Tu email de acceso no se puede cambiar acá.',
  'profile.submit': 'Guardar cambios',
  'profile.saveError': 'No pudimos guardar tus cambios.',
  'ai.title': 'Conectar con IA',
  'ai.subtitle': 'Generá un token para operar Tembiapo desde Claude, ChatGPT o Gemini: crear y asignar tareas, moverlas, comentar y más, por lenguaje natural.',
  'ai.generate': 'Generar token para IA',
  'ai.tokenTitle': 'Tu token (copialo ahora)',
  'ai.tokenWarning': 'Guardalo como una contraseña. No se vuelve a mostrar; si lo perdés, generá uno nuevo.',
  'ai.copy': 'Copiar',
  'ai.copied': '¡Copiado!',
  'ai.commandTitle': 'Comando para Claude Code',
  'ai.commandHint': 'Pegalo en tu terminal. Después pedile a tu IA: “listá mis proyectos” o “creá una tarea…”.',
  'ai.listTitle': 'Tokens activos',
  'ai.created': 'Creado {date}',
  'ai.lastUsed': 'último uso {date}',
  'ai.never': 'nunca',
  'ai.revoke': 'Revocar',
  'ai.error': 'No pudimos generar el token. Intentá de nuevo.',
  'ai.otherAIs': '¿Usás ChatGPT o Gemini?',
  'ai.docsLink': 'Ver la guía de conexión',
  'getApp.title': 'Descargá la app',
  'getApp.subtitle': 'Llevá Tembiapo en tu teléfono — disponible para iOS y Android.',
  'getApp.downloadOn': 'Descargá en el',
  'getApp.availableOn': 'Disponible en',
  'pairing.title': 'Conectar app móvil',
  'pairing.subtitle': 'Vinculá la app de Tembiapo escaneando este QR desde tu teléfono. No hace falta contraseña en el celular.',
  'pairing.generate': 'Generar código de vinculación',
  'pairing.regenerate': 'Generar uno nuevo',
  'pairing.step1': 'Abrí la app de Tembiapo en tu teléfono y tocá “Vincular dispositivo”.',
  'pairing.step2': 'Escaneá este QR (o ingresá el código de abajo).',
  'pairing.codeLabel': 'Código',
  'pairing.expiresIn': 'Vence en {time}',
  'pairing.expired': 'El código venció. Generá uno nuevo.',
  'pairing.error': 'No pudimos generar el código. Intentá de nuevo.',
  'password.title': 'Cambiar contraseña',
  'password.subtitle': 'Necesitás tu contraseña actual para cambiarla.',
  'password.current': 'Contraseña actual',
  'password.new': 'Nueva contraseña',
  'password.repeat': 'Repetir nueva contraseña',
  'password.submit': 'Cambiar contraseña',
  'password.saved': 'Contraseña actualizada.',
  'password.tooShort': 'La contraseña debe tener al menos 8 caracteres.',
  'password.mismatch': 'Las contraseñas no coinciden.',
  'password.invalidCurrent': 'La contraseña actual es incorrecta.',
  'password.same': 'La nueva contraseña debe ser distinta a la actual.',
  'password.error': 'No pudimos cambiar la contraseña. Probá de nuevo.',
  'profile.avatar.title': 'Foto de perfil',
  'profile.avatar.hint': 'JPG o PNG, hasta 5 MB.',
  'profile.avatar.change': 'Cambiar foto',
  'profile.appearance.title': 'Apariencia y preferencias',
  'profile.appearance.subtitle': 'Tema, idioma y avisos de la app.',
  'profile.appearance.theme': 'Tema (claro / oscuro)',
  'profile.appearance.language': 'Idioma',
  'profile.appearance.notifications': 'Preferencias de notificaciones',
  'profile.appearance.shareApp': 'Compartir Tembiapo (invitá a instalar)',
  'profile.appearance.shareAppText': 'Usá Tembiapo para organizar el trabajo de tu equipo, sin complicaciones.',
  'profile.avatar.uploading': 'Subiendo…',
  'profile.avatar.invalidType': 'El archivo debe ser una imagen.',
  'profile.avatar.tooBig': 'La imagen supera los 5 MB.',
  'profile.avatar.error': 'No pudimos subir tu foto. Probá de nuevo.',

  // Navegación / menú de usuario
  'nav.preferences': 'Notificaciones',

  // Preferencias de notificación (Fase 2/3)
  'prefs.title': 'Preferencias',
  'prefs.subtitle': 'Ajustá tu Tembiapo: página de inicio, guías y notificaciones.',
  // Preferencias de la app (migr. 040)
  'prefs.app.title': 'Preferencias de la app',
  'prefs.app.subtitle': 'Ajustá cómo arranca y se ve tu Tembiapo.',
  'prefs.app.homePage': 'Página de inicio',
  'prefs.app.homePageHint': 'Elegí qué pantalla ver primero cuando entrás.',
  'prefs.app.home.inicio': 'Inicio',
  'prefs.app.home.proyectos': 'Proyectos',
  'prefs.app.home.reportes': 'Reportes',
  'prefs.app.guide': 'Guía de proyectos',
  'prefs.app.guideHint': 'Mostrar el acordeón que explica cómo se organizan carpetas, proyectos y tareas.',
  // Guía de jerarquía en /proyectos
  'guide.title': '¿Cómo se organiza el trabajo?',
  'guide.subtitle': 'Un vistazo rápido al orden de las cosas en Tembiapo.',
  'guide.dismiss': 'Ocultar esta guía',
  'guide.footer': 'Podés volver a mostrarla cuando quieras desde Preferencias.',
  'guide.ex.folder': 'Marketing',
  'guide.ex.project': 'Campaña de lanzamiento',
  'guide.ex.task': 'Diseñar el flyer',
  'guide.ex.subtask': 'Elegir la paleta de colores',
  'guide.folder.title': '1. Carpetas — para agrupar',
  'guide.folder.body':
    'Son el primer nivel, opcional. Agrupan tus proyectos por área, cliente o tema (ej. “Marketing”, “Clientes”, “Interno”). Una carpeta contiene varios proyectos.',
  'guide.project.title': '2. Proyectos — tu espacio de trabajo',
  'guide.project.body':
    'Cada proyecto es un tablero con columnas (Kanban); también podés verlo como Lista, Calendario o Timeline. Vive dentro de una carpeta o suelto en la raíz.',
  'guide.task.title': '3. Tareas — lo que hay que hacer',
  'guide.task.body':
    'Dentro de un proyecto creás tareas: con responsable, prioridad, fecha de vencimiento, comentarios y archivos. Se mueven entre columnas a medida que avanzan.',
  'guide.subtask.title': '4. Subtareas — el checklist de una tarea',
  'guide.subtask.body':
    'Cada tarea puede tener subtareas: pasos más chicos, asignables y con su propia fecha. Sirven para dividir una tarea grande en partes.',
  'prefs.loadError': 'No pudimos cargar tus preferencias.',
  // Push por dispositivo
  'push.device.title': 'Notificaciones push en este dispositivo',
  'push.device.hint':
    'Recibí avisos aunque no tengas Tembiapo abierto, en este navegador o dispositivo.',
  'push.unsupported': 'Este navegador no soporta notificaciones push.',
  'push.denied':
    'Bloqueaste las notificaciones para este sitio. Habilitalas desde los ajustes del navegador para activarlas.',
  'push.unavailable': 'Las notificaciones push no están disponibles por ahora.',
  'push.error': 'No pudimos cambiar la configuración de push. Probá de nuevo.',
  // Matriz de preferencias
  'prefs.matrix.title': 'Qué querés recibir',
  'prefs.matrix.hint': 'Activá o desactivá cada tipo de aviso por canal.',
  'prefs.eventColumn': 'Evento',
  'prefs.soon': 'Pronto',
  'prefs.email.note': 'El envío de emails por evento estará disponible próximamente.',
  'prefs.turnAllOn': 'Encender todo',
  'prefs.turnAllOff': 'Apagar todo',
  'prefs.matrix.note':
    'Los emails llegan a tu correo según estas preferencias. Usá los interruptores de cada columna para prender o apagar todo un canal de una vez.',
  'prefs.channel.IN_APP': 'En la app',
  'prefs.channel.EMAIL': 'Email',
  'prefs.channel.PUSH': 'Push',
  'prefs.event.task_assigned': 'Te asignan una tarea',
  'prefs.event.subtask_assigned': 'Te asignan una subtarea',
  'prefs.event.task_due_soon': 'Tarea por vencer',
  'prefs.event.task_overdue': 'Tarea vencida',
  'prefs.event.event_upcoming': 'Audiencia/evento próximo',
  'prefs.event.daily_agenda': 'Resumen diario (agenda del día)',
  'prefs.event.weekly_digest': 'Resumen semanal',
  'prefs.event.mention': 'Te mencionan',
  'prefs.event.comment': 'Comentarios en tus tareas',
  'prefs.event.plan_expiring': 'Tu plan está por vencer',
  'prefs.event.trial_ending': 'Tu prueba está por terminar',
  'prefs.event.plan_expired': 'Tu plan venció',
  'prefs.event.account_blocked': 'Cuenta bloqueada',
  'prefs.event.payment_confirmed': 'Pago confirmado',
  'prefs.event.payment_rejected': 'Pago rechazado',
  'prefs.event.automation': 'Automatizaciones',
  'prefs.event.invitation_accepted': 'Aceptan tu invitación',

  // Comentarios
  'comments.title': 'Comentarios',
  'comments.empty': 'Sin comentarios todavía. Escribí el primero.',
  'comments.loadError': 'No pudimos cargar los comentarios.',
  'comments.placeholder': 'Escribí un comentario… usá @ para mencionar',
  'comments.hint': '@ para mencionar · ⌘/Ctrl + Enter para enviar',
  'comments.submit': 'Comentar',
  'comments.saveError': 'No pudimos publicar tu comentario.',

  // Adjuntos
  'attachments.title': 'Adjuntos',
  'attachments.empty': 'Sin archivos adjuntos.',
  'attachments.loadError': 'No pudimos cargar los adjuntos.',
  'attachments.dropHint': 'Arrastrá archivos acá o',
  'attachments.pick': 'Elegir archivos',
  'attachments.uploading': 'Subiendo…',
  'attachments.download': 'Descargar',
  'attachments.error.tooBig': 'El archivo es demasiado grande (máx. 25 MB).',
  'attachments.error.invalidType': 'Tipo de archivo no permitido.',
  'attachments.error.generic': 'No pudimos subir el archivo. Probá de nuevo.',
  'attachments.error.storageFull': 'Llegaste al límite de almacenamiento de tu plan. Liberá espacio o mejorá tu plan.',
  'attachment.link.add': 'Agregar link',
  'attachment.link.url': 'Enlace (URL)',
  'attachment.link.urlPlaceholder': 'https://drive.google.com/…',
  'attachment.link.name': 'Nombre (opcional)',
  'attachment.link.namePlaceholder': 'Documento de requisitos',
  'attachment.link.save': 'Agregar',
  'attachment.link.invalid': 'Pegá una URL válida que empiece con http:// o https://',
  'attachment.link.badge': 'Enlace externo',
  'attachment.link.open': 'Abrir enlace',
  'storage.title': 'Almacenamiento',
  'storage.usedOf': 'Usaste {used} de {limit}',
  'storage.full': 'Llegaste al límite de almacenamiento de tu plan.',
  'register.plan.storage': '{size} de archivos',
  'landing.plans.storage': '{size} de almacenamiento',

  // Tarea — solo lectura
  'task.readOnly': 'Solo lectura.',

  // Notificaciones
  'notifications.title': 'Notificaciones',
  'notifications.markAll': 'Marcar todas como leídas',
  'notifications.empty': 'Sin notificaciones.',
  'notifications.seeAll': 'Ver todas',
  'notifications.open': 'Abrir',

  // Facturación (empresa)
  'nav.billing': 'Facturación',
  'billing.title': 'Facturación',
  'billing.subtitle': 'Tu plan, tu suscripción y tus pagos.',
  'billing.loadError': 'No pudimos cargar tu facturación.',
  'billing.restricted.title': 'Acceso restringido',
  'billing.restricted.body': 'Solo los administradores pueden gestionar la facturación.',
  'billing.plan.title': 'Tu plan',
  'billing.plan.none': 'Sin plan asignado',
  'billing.plan.perMonth': '/mes',
  'billing.plan.perYear': '/año',
  'billing.change.title': 'Cambiar de plan',
  'billing.change.subtitle': 'Mejorá o bajá tu plan cuando quieras. El cambio es inmediato.',
  'billing.change.current': 'Plan actual',
  'billing.change.limits': '{projects} proyectos · {users} usuarios',
  'billing.change.switchTo': 'Cambiar a este plan',
  'billing.change.customNote':
    'Tenés un plan personalizado. Para cambiar de plan, escribinos y lo gestionamos con vos.',
  'billing.change.confirmTo': 'Vas a cambiar al plan {plan}.',
  'billing.change.prorationNote':
    'Se generará un pago pendiente de {amount} por la diferencia de los días que faltan del período. Lo pagás abajo, como cualquier pago.',
  'billing.change.downgradeNote':
    'La baja aplica de inmediato. No hay devolución ni crédito por el período ya pagado.',
  'billing.change.freeNote': 'Sin costo adicional: el cambio se aplica de inmediato.',
  'billing.change.confirm': 'Confirmar cambio',
  'billing.change.cancel': 'Cancelar',
  'billing.change.success': 'Listo, cambiaste al plan {plan}.',
  'billing.change.successProration':
    'Cambiaste al plan {plan}. Generamos un pago pendiente de {amount} por la diferencia — pagalo abajo.',
  'billing.change.error': 'No pudimos cambiar el plan. Intentá de nuevo.',
  'billing.change.overLimit':
    'No podés bajar a ese plan: superás sus límites (proyectos o usuarios en uso). Reducí el uso o elegí otro plan.',
  'billing.status.label': 'Estado',
  'billing.cycle.label': 'Ciclo de facturación',
  'billing.cycle.monthly': 'Mensual',
  'billing.cycle.semiannual': 'Semestral',
  'billing.cycle.yearly': 'Anual',
  'billing.period.end': 'Próximo vencimiento',
  // Precios por ciclo (modelo por multiplicador: 1/6/12 meses de servicio).
  'pricing.cycle.label': 'Ciclo de facturación',
  'pricing.cycle.monthly': '1 mes',
  'pricing.cycle.semiannual': '6 meses',
  'pricing.cycle.yearly': '1 año',
  'pricing.free.one': '1 mes gratis',
  'pricing.free.two': '2 meses gratis',
  'pricing.billed.semiannual': 'Facturado {total} cada 6 meses',
  'pricing.billed.yearly': 'Facturado {total} al año',
  'billing.trial.left': 'Te quedan {days} días de prueba.',
  'billing.trial.lastDay': 'Tu prueba termina hoy.',
  'billing.trial.expired': 'Tu prueba gratis terminó. Registrá un pago para reactivar tu cuenta.',
  'billing.pay.title': 'Registrar pago',
  'billing.pay.subtitle': 'Cargá tu comprobante de transferencia o depósito para que lo validemos.',
  'billing.pay.cardOnly': 'Pagá tu suscripción con tarjeta de forma segura.',
  'billing.pay.none': 'Por el momento no hay métodos de pago habilitados. Escribinos a soporte.',
  'billing.pay.method': 'Método de pago',
  'billing.pay.method.transfer': 'Transferencia bancaria',
  'billing.pay.method.deposit': 'Depósito',
  'billing.pay.method.qr': 'Pago con QR',
  'billing.pay.method.card': 'Tarjeta',
  'billing.pay.method.other': 'Otro',
  'billing.pay.amount': 'Monto',
  'billing.pay.reference': 'Referencia / N.º de operación',
  'billing.pay.cycle': 'Ciclo',
  'billing.pay.proof': 'Comprobante',
  'billing.pay.proofHint': 'Imagen o PDF, hasta 5 MB.',
  'billing.pay.submit': 'Enviar pago',
  'billing.pay.submitting': 'Enviando…',
  'billing.pay.success': 'Recibimos tu pago. Lo validaremos a la brevedad.',
  'billing.pay.error': 'No pudimos registrar tu pago. Probá de nuevo.',
  // Pago con tarjeta (hub Bancard vía pagos.evoluado.com)
  'billing.card.title': 'Pagar con tarjeta',
  'billing.card.subtitle': 'Pagá al instante con tarjeta de crédito o débito en un checkout seguro.',
  'billing.card.pay': 'Pagar con tarjeta',
  'billing.card.starting': 'Redirigiendo…',
  'billing.card.evoluadoNotice': 'El cargo aparecerá en tu extracto como EVOLUADO.',
  'billing.card.pygNotice': 'El pago se procesa en guaraníes (₲); el precio en tu moneda es solo una referencia. Tu banco lo convierte a tu moneda y puede sumar cargos por compra internacional (en Brasil, el IOF).',
  'billing.card.disabled': 'El pago con tarjeta no está disponible por ahora.',
  'billing.card.error': 'No pudimos iniciar el pago, probá de nuevo.',
  // Vuelta del checkout (?pago=<id>)
  'billing.return.verifying': 'Estamos verificando tu pago…',
  'billing.return.confirmed': '¡Pago confirmado! Tu suscripción está activa.',
  'billing.return.rejected': 'El pago no se completó. Podés intentar de nuevo.',
  'billing.return.dismiss': 'Cerrar',
  'billing.history.title': 'Historial de pagos',
  'billing.history.empty': 'Todavía no registraste pagos.',
  'billing.history.date': 'Fecha',
  'billing.history.amount': 'Monto',
  'billing.history.method': 'Método',
  'billing.history.status': 'Estado',
  'billing.history.proof': 'Comprobante',
  'billing.payStatus.PENDING': 'Pendiente',
  'billing.payStatus.CONFIRMED': 'Confirmado',
  'billing.payStatus.REJECTED': 'Rechazado',

  // Cómo pagar (datos de transferencia + QR configurados por el admin)
  'billing.howToPay.title': 'Cómo pagar',
  'billing.howToPay.subtitle': 'Transferí o depositá con estos datos y luego registrá tu pago abajo.',
  'billing.howToPay.empty': 'Todavía no hay datos de pago configurados. Escribinos por soporte para coordinar el pago.',
  'billing.howToPay.bank': 'Banco',
  'billing.howToPay.account': 'Número de cuenta',
  'billing.howToPay.alias': 'Alias',
  'billing.howToPay.holder': 'Titular',
  'billing.howToPay.document': 'RUC / CI',
  'billing.howToPay.instructions': 'Instrucciones',
  'billing.howToPay.copy': 'Copiar',
  'billing.howToPay.copied': 'Copiado',
  'billing.howToPay.qrLabel': 'Escaneá para pagar',
  'billing.howToPay.qrAlt': 'Código QR de pago',

  // Formas de pago — panel Super Admin
  'adminPay.title': 'Formas de pago',
  'adminPay.subtitle': 'Datos de transferencia y QR que verán las empresas al pagar.',
  'adminPay.loadError': 'No pudimos cargar las formas de pago.',
  'adminPay.toggles.title': 'Métodos habilitados',
  'adminPay.toggles.note': 'Elegí qué formas de pago ven las empresas en Facturación. Los cambios se aplican al instante.',
  'adminPay.toggle.transfer': 'Transferencia / depósito',
  'adminPay.toggle.transferNote': 'Pago manual: la empresa sube el comprobante y vos lo confirmás.',
  'adminPay.toggle.qr': 'Pago con QR',
  'adminPay.toggle.qrNote': 'Muestra la imagen del QR cargado abajo como opción de pago.',
  'adminPay.toggle.card': 'Tarjeta (pasarela)',
  'adminPay.toggle.cardNote': 'Cobro automático con tarjeta vía la pasarela. Requiere las credenciales del hub en Configuración.',
  'adminPay.section.transfer': 'Datos de transferencia',
  'adminPay.section.transferNote': 'Se muestran a todas las empresas en su pantalla de Facturación.',
  'adminPay.field.bank': 'Banco',
  'adminPay.hint.bank': 'Nombre del banco o financiera.',
  'adminPay.field.account': 'Número de cuenta',
  'adminPay.hint.account': 'Cuenta a la que transferir/depositar.',
  'adminPay.field.alias': 'Alias',
  'adminPay.hint.alias': 'Alias o apodo de la cuenta (si aplica).',
  'adminPay.field.holder': 'Titular',
  'adminPay.hint.holder': 'Nombre del titular de la cuenta.',
  'adminPay.field.document': 'RUC / CI',
  'adminPay.hint.document': 'Documento del titular (RUC o CI).',
  'adminPay.field.instructions': 'Instrucciones',
  'adminPay.hint.instructions': 'Texto libre con pasos o aclaraciones para el pago.',
  'adminPay.save': 'Guardar datos',
  'adminPay.saved': 'Datos guardados',
  'adminPay.saveError': 'No pudimos guardar los datos.',
  'adminPay.qr.title': 'QR de pago',
  'adminPay.qr.note': 'Imagen del código QR para pagos (billetera, banco, etc.).',
  'adminPay.qr.hint': 'PNG, JPG, WEBP o GIF, hasta 2 MB.',
  'adminPay.qr.upload': 'Subir QR',
  'adminPay.qr.replace': 'Reemplazar QR',
  'adminPay.qr.saved': 'QR actualizado',
  'adminPay.qr.errorType': 'El archivo debe ser una imagen.',
  'adminPay.qr.errorSize': 'La imagen supera el máximo de 2 MB.',
  'adminPay.qr.errorUpload': 'No pudimos subir la imagen. Probá de nuevo.',

  // Columnas — edición estructural (Ola 4a)
  'column.add': 'Agregar columna',
  'column.addTitle': 'Nueva columna',
  'column.addSubmit': 'Agregar',
  'column.settings.title': 'Editar columna',
  'column.menu': 'Opciones de columna',
  'column.moveLeft': 'Mover a la izquierda',
  'column.moveRight': 'Mover a la derecha',
  'column.name': 'Nombre',
  'column.namePlaceholder': 'Nombre de la columna',
  'column.color': 'Color',
  'column.wip': 'Límite de trabajo en curso',
  'column.wipPlaceholder': 'Sin límite',
  'column.wipHint': 'Máximo de tareas en esta columna. Dejalo vacío para no limitar.',
  'column.final': 'Columna final (cierra la tarea)',
  'column.finalHint':
    'Las tareas en esta columna se consideran terminadas: no cuentan como vencidas, no envían recordatorios y destraban las que dependían de ellas. Podés marcar varias (Hecho, Archivado, etc.).',
  'column.delete': 'Eliminar columna',
  'column.deleteConfirm': '¿Eliminar esta columna?',
  'column.deleteConfirmCta': 'Eliminar',
  'column.error.lastColumn': 'No podés eliminar la última columna del tablero.',
  'column.saveError': 'No pudimos guardar los cambios.',

  // Plantillas de tarea (Ola 4a)
  'taskTemplate.saveAs': 'Guardar como plantilla',
  'taskTemplate.saveAsTitle': 'Reutilizable en cualquier proyecto de tu cuenta.',
  'taskTemplate.thisProjectOnly': 'Solo este proyecto',
  'taskTemplate.nameLabel': 'Nombre de la plantilla',
  'taskTemplate.namePlaceholder': 'Ej: Reporte de bug',
  'taskTemplate.save': 'Guardar plantilla',
  'taskTemplate.saved': 'Plantilla guardada.',
  'taskTemplate.saveError': 'No pudimos guardar la plantilla.',
  'taskTemplate.fromTemplate': 'Desde plantilla',
  'taskTemplate.pickTitle': 'Crear tarea desde plantilla',
  'taskTemplate.empty': 'Todavía no tenés plantillas. Guardá una tarea como plantilla para reutilizarla.',
  'taskTemplate.loadError': 'No pudimos cargar las plantillas.',
  'taskTemplate.createError': 'No pudimos crear la tarea desde la plantilla.',
  'taskTemplate.use': 'Usar',
  'taskTemplate.delete': 'Eliminar plantilla',
  'taskTemplate.deleteConfirm': '¿Eliminar esta plantilla?',
  'taskTemplate.preview.priority': 'Prioridad: {priority}',
  'taskTemplate.preview.checklist': '{n} ítems de checklist',
  'taskTemplate.preview.checklistOne': '1 ítem de checklist',
  'taskTemplate.preview.noChecklist': 'Sin checklist',

  // Automatizaciones when-then (Fase 2)
  'automation.button': 'Automatizaciones',
  'automation.title': 'Automatizaciones',
  'automation.subtitle': 'Reglas "cuando pasa esto, hacé esto" para tu tablero.',
  'automation.loadError': 'No pudimos cargar las automatizaciones.',
  'automation.empty': 'Todavía no tenés automatizaciones. Creá tu primera regla abajo.',
  'automation.rulesTitle': 'Reglas',
  'automation.count': '{n} reglas',
  'automation.countOne': '1 regla',
  'automation.new': 'Nueva regla',
  'automation.newTitle': 'Crear regla',
  'automation.editTitle': 'Editar regla',
  'automation.name': 'Nombre de la regla',
  'automation.namePlaceholder': 'Ej: Asignar al mover a Hecho',
  'automation.whenLabel': 'Cuando…',
  'automation.thenLabel': 'Hacer…',
  'automation.event.label': 'Disparador',
  'automation.event.task_moved_to': 'Una tarea se mueve a una columna',
  'automation.event.task_created': 'Se crea una tarea',
  'automation.column.label': 'Columna destino',
  'automation.action.label': 'Acción',
  'automation.action.assign': 'Asignar a un miembro',
  'automation.action.set_priority': 'Cambiar la prioridad',
  'automation.action.notify': 'Notificar a un miembro',
  'automation.user.label': 'Miembro',
  'automation.priority.label': 'Prioridad',
  'automation.save': 'Guardar regla',
  'automation.edit': 'Editar regla',
  'automation.delete': 'Eliminar regla',
  'automation.deleteConfirm': '¿Eliminar esta regla? No se puede deshacer.',
  'automation.saveError': 'No pudimos guardar la regla.',
  'automation.active': 'Regla activa',
  'automation.inactive': 'Inactiva',
  'automation.activate': 'Activar',
  'automation.deactivate': 'Pausar',
  'automation.readOnly': 'Tu prueba gratis terminó. Podés ver tus reglas, pero no crearlas ni editarlas.',
  // Piezas para la descripción legible (el nombre se renderiza en negrita aparte)
  'automation.when.movedTo': 'Cuando una tarea se mueve a',
  'automation.when.created': 'Cuando se crea una tarea',
  'automation.do.assign': 'asignar a',
  'automation.do.setPriority': 'cambiar la prioridad a',
  'automation.do.notify': 'notificar a',
  'automation.entity.deleted': '(eliminada)',
  'automation.entity.someone': 'alguien',

  // Dependencias entre tareas (Fase 3)
  'dependency.title': 'Dependencias',
  'dependency.loadError': 'No pudimos cargar las dependencias.',
  'dependency.blockedBy': 'Bloqueada por',
  'dependency.blockedBy.empty': 'No depende de ninguna otra tarea.',
  'dependency.blocks': 'Bloquea a',
  'dependency.blocks.empty': 'No bloquea a ninguna otra tarea.',
  'dependency.addBlockedBy': 'Agregar tarea que la bloquea…',
  'dependency.addBlocks': 'Agregar tarea que esta bloquea…',
  'dependency.noCandidates': 'No hay otras tareas para vincular.',
  'dependency.remove': 'Quitar dependencia',
  'dependency.done': 'Bloqueante completado',
  'dependency.blocked': 'Bloqueada por otra tarea',
  'dependency.blockedByCount': 'Bloqueada por {n} tarea(s)',
  'dependency.error.cycle': 'No se puede: crearía un ciclo de dependencias.',
  'dependency.error.invalid': 'No pudimos agregar esa dependencia.',
  'dependency.error.generic': 'No pudimos actualizar las dependencias. Probá de nuevo.',

  // Subtareas (checklist asignable + obligatorias)
  'subtask.title': 'Subtareas',
  'subtask.add': 'Agregar subtarea',
  'subtask.placeholder': 'Nueva subtarea…',
  'subtask.required': 'Obligatoria',
  'subtask.markRequired': 'Marcar como obligatoria',
  'subtask.markOptional': 'Quitar obligatoria',
  'subtask.assignee': 'Responsable de la subtarea',
  'subtask.unassigned': 'Sin responsable',
  'subtask.dueDate': 'Fecha de vencimiento (no puede superar la de la tarea)',
  'subtask.dueClear': 'quitar',
  'subtask.delete': 'Eliminar subtarea',
  'subtask.toggleDone': 'Marcar como completada',
  'subtask.empty': 'Todavía no hay subtareas.',
  'subtask.loadError': 'No pudimos cargar las subtareas.',
  'subtask.saveError': 'No pudimos guardar. Probá de nuevo.',
  'subtask.requiredHint': 'Las subtareas obligatorias sin completar impiden mover la tarea a otra columna.',
  'subtask.blockedMove': 'No podés avanzar: hay subtareas obligatorias sin completar.',
  'subtask.blockedMoveNamed': 'No podés pasar a {column}: falta completar «{task}».',
  'subtask.blockedMoveNamedMore':
    'No podés pasar a {column}: falta completar «{task}» y {n} más.',
  'subtask.requiredForChip': 'Obligatoria · para {column}',
  'subtask.requiredForLabel': 'para pasar a',
  'subtask.columnSelect': 'Columna que gatea la obligatoria',
  'subtask.waive': 'Omitir',
  'subtask.waived': 'Omitida',
  'subtask.reactivate': 'Reactivar',
  'subtask.reason': 'Motivo',
  'subtask.reasonPlaceholder': 'Motivo para omitir…',
  'subtask.reasonRequired': 'Ingresá un motivo.',
  'subtask.waivedReason': 'Motivo: {reason}',
  'subtask.lockedAdvanced':
    'No podés desmarcar una subtarea obligatoria con la tarea avanzada. Movela a la primera columna para editarla.',
  'card.requiredOpen': '{n} obligatoria(s) pendiente(s)',
  'card.owner': 'Responsable principal',
  'card.inSubtask': 'Responsable de una subtarea',
  'card.comments': '{n} comentario(s)',
  'card.commentsUnread': '{n} comentario(s) · {u} sin leer',
  'card.attachments': '{n} archivo(s) adjunto(s)',

  // Filtros guardados (Fase 3)
  'savedFilter.label': 'Filtros guardados',
  'savedFilter.apply': 'Aplicar filtro guardado',
  'savedFilter.applyPlaceholder': 'Elegí un filtro…',
  'savedFilter.empty': 'Sin filtros guardados',
  'savedFilter.save': 'Guardar filtro',
  'savedFilter.nameLabel': 'Nombre del filtro',
  'savedFilter.namePlaceholder': 'Ej: Urgentes sin asignar',
  'savedFilter.delete': 'Eliminar filtro',
  'savedFilter.deleteConfirm': '¿Eliminar el filtro "{name}"?',
  'savedFilter.saveError': 'No pudimos guardar el filtro.',
  'savedFilter.deleteError': 'No pudimos eliminar el filtro.',

  // Reportes / Dashboard por empresa (Fase 3)
  'reports.title': 'Reportes',
  'reports.subtitle': 'El pulso de tu empresa: proyectos, tareas y equipo de un vistazo.',
  'reports.loadError': 'No pudimos cargar los reportes.',
  'reports.metric.projects': 'Proyectos',
  'reports.metric.projectsDetail': '{active} activos',
  'reports.metric.tasks': 'Tareas',
  'reports.metric.overdue': 'Vencidas',
  'reports.metric.completed': 'Completadas',
  'reports.metric.completedDetail': '{pct}% del total',
  'reports.metric.unassigned': 'Sin asignar',
  'reports.viewList': 'Ver lista',
  'reports.list.empty': 'No hay tareas para mostrar.',
  'reports.list.unassigned': 'Sin asignar',
  'reports.priority.title': 'Distribución por prioridad',
  'reports.priority.totalLabel': 'tareas',
  'reports.byAssignee.title': 'Por responsable',
  'reports.byAssignee.empty': 'Todavía no hay tareas asignadas.',
  'reports.byProject.title': 'Por proyecto',
  'reports.byProject.empty': 'Todavía no hay proyectos con tareas.',
  'reports.byProject.name': 'Proyecto',
  'reports.byProject.tasks': 'Tareas',
  'reports.byProject.progress': 'Completadas',
  'reports.byProject.overdue': 'Vencidas',
  'reports.empty.title': 'Sin datos para mostrar',
  'reports.empty.body':
    'Cuando crees proyectos y tareas, acá vas a ver el resumen de tu empresa.',
  'reports.empty.cta': 'Crear proyecto',

  // Landing pública (home del sitio) — bilingüe PY + BR
  'landing.nav.product': 'Producto',
  'landing.nav.plans': 'Planes',
  'landing.nav.apps': 'Apps',
  'landing.action.login': 'Ingresar',
  'landing.action.try': 'Probar gratis',
  'landing.action.start': 'Empezar gratis',
  'landing.action.how': 'Ver cómo funciona',
  'landing.langLabel': 'Idioma',

  'landing.hero.eyebrow': 'Paraguay · Brasil · Tembiapo',
  'landing.hero.titleBefore': 'Tu equipo, tus proyectos, en ',
  'landing.hero.titleAccent': 'un solo lugar',
  'landing.hero.titleAfter': '.',
  'landing.hero.lede':
    'Gestión de proyectos y tareas, sin complicaciones. Tableros listos según tu rubro, para empezar a trabajar hoy.',
  'landing.hero.trialBold': '30 días gratis',
  'landing.hero.trialRest': ' — sin tarjeta de crédito',
  'landing.hero.stat.verticals': 'rubros listos',
  'landing.hero.stat.views': 'formas de ver',
  'landing.hero.stat.trial': 'días gratis',

  'landing.board.todo': 'Por hacer',
  'landing.board.doing': 'En curso',
  'landing.board.done': 'Listo',
  'landing.board.tag.mkt': 'Marketing',
  'landing.board.tag.tech': 'Tech',
  'landing.board.card.landing': 'Diseñar landing de campaña',
  'landing.board.card.api': 'Definir esquema de API',
  'landing.board.card.billing': 'Endpoint de facturación',
  'landing.board.card.content': 'Calendario de contenidos',
  'landing.board.card.setup': 'Setup del proyecto',
  'landing.board.card.brief': 'Brief aprobado',

  'landing.strip.claim': 'Simple de usar. Poderoso por dentro.',
  'landing.strip.kanban': 'Tableros Kanban',
  'landing.strip.plans': 'Planes por rubro',
  'landing.strip.apps': 'Apps móviles',

  'landing.features.eyebrow': 'Todo lo que necesitás',
  'landing.features.title': 'Gestioná el trabajo como tu equipo piensa',
  'landing.features.subtitle':
    'Vistas visuales, plantillas por rubro y automatizaciones — sin manuales ni curvas de aprendizaje.',
  'landing.features.views.title': 'Vistas visuales',
  'landing.features.views.body': 'Kanban, lista, calendario y timeline. Arrastrá y soltá tus tareas.',
  'landing.features.agenda.title': 'Agenda con horarios',
  'landing.features.agenda.body':
    'Eventos con fecha, hora y lugar (audiencias, reuniones, citas, turnos), con recordatorio y sincronización con Google, Apple u Outlook.',
  'landing.features.plans.title': 'Planes por rubro',
  'landing.features.plans.body': 'Tecnología, marketing y más — con proyectos ya configurados para vos.',
  'landing.features.workflows.title': 'Workflows y plantillas',
  'landing.features.workflows.body':
    'Estados, transiciones y automatizaciones simples tipo cuando-entonces.',
  'landing.features.notifications.title': 'Notificaciones',
  'landing.features.notifications.body': 'En la app, por email y push — en web y en el celular.',
  'landing.features.files.title': 'Archivos e imágenes',
  'landing.features.files.body': 'Adjuntá documentos y fotos a cada tarea con arrastrar y soltar.',
  'landing.features.mobile.title': 'Apps Android e iPhone',
  'landing.features.mobile.body': 'Llevá tus proyectos en el bolsillo, con notificaciones nativas.',

  'landing.plans.eyebrow': 'Planes por vertical',
  'landing.plans.title': 'Elegí el plan de tu rubro',
  'landing.plans.subtitle': 'Cada plan trae tableros y flujos listos. Empezás con 30 días gratis.',
  'landing.plans.perMonth': '/mes',
  'landing.plans.badge': 'Más elegido',
  'landing.plans.tech.name': 'Tecnología',
  'landing.plans.tech.f1': 'Sprints, backlog y bugs',
  'landing.plans.tech.f2': 'Hasta 10 proyectos · 15 usuarios',
  'landing.plans.tech.f3': 'Story points y prioridades',
  'landing.plans.mkt.name': 'Marketing',
  'landing.plans.mkt.f1': 'Tableros por campaña y cliente',
  'landing.plans.mkt.f2': 'Hasta 15 proyectos · 25 usuarios',
  'landing.plans.mkt.f3': 'Calendario de contenidos',
  'landing.plans.pyme.name': 'PyMEs',
  'landing.plans.pyme.f1': 'Embudo de ventas simple',
  'landing.plans.pyme.f2': 'Operaciones y tareas del día a día',
  'landing.plans.pyme.f3': 'Compras y proveedores',
  'landing.plans.legal.name': 'Abogados',
  'landing.plans.legal.f1': 'Gestión de casos y expedientes',
  'landing.plans.legal.f2': 'Control de plazos y audiencias',
  'landing.plans.legal.f3': 'Revisión de contratos',
  'landing.plans.rrhh.name': 'Recursos Humanos',
  'landing.plans.rrhh.f1': 'Reclutamiento y selección',
  'landing.plans.rrhh.f2': 'Alta y baja de empleados',
  'landing.plans.rrhh.f3': 'Capacitaciones y desempeño',
  'landing.plans.custom.name': 'Personalizado',
  'landing.plans.custom.price': 'A medida',
  'landing.plans.custom.f1': 'Tu propia vertical y plantillas',
  'landing.plans.custom.f2': 'Proyectos y usuarios flexibles',
  'landing.plans.custom.f3': 'Add-ons y soporte dedicado',
  'landing.plans.customCta': 'Hablar con ventas',
  'landing.plans.footnote':
    'Precios de referencia en ₲ (Paraguay). Disponible también en R$ (Brasil) y USD.',

  'landing.apps.eyebrow': 'En todos lados',
  'landing.ai.badge': 'Nuevo · Conectá tu IA',
  'landing.ai.title': 'Pedile a tu IA que organice tu trabajo',
  'landing.ai.body':
    'Conectá Tembiapo con Claude, ChatGPT o Gemini y creá, asigná y ordená tareas por lenguaje natural — aparecen al instante en tus tableros. Con un solo comando.',
  'landing.ai.cta': 'Cómo conectarlo',
  'landing.apps.title': 'Web, Android e iPhone',
  'landing.apps.soon': 'Próximamente',
  'landing.apps.live': 'Ya disponible en Android e iPhone',
  'landing.apps.androidCta': 'Descargar en Google Play',
  'landing.apps.iosCta': 'Descargar en App Store',
  'landing.apps.iosSoon': 'iPhone · pronto',
  'landing.apps.body':
    'Descargá Tembiapo para Android y recibí notificaciones donde estés. Tus tableros, siempre sincronizados.',

  'landing.final.title': 'Empezá gratis hoy',
  'landing.final.body': '30 días sin costo ni tarjeta. Suscribí a tu empresa en minutos.',
  'landing.final.note': 'Al registrarte aceptás nuestros Términos y la Política de Privacidad.',

  'landing.footer.tagline': 'Gestión de proyectos simple, para equipos de Paraguay y Brasil.',
  'landing.footer.product': 'Producto',
  'landing.footer.link.features': 'Funciones',
  'landing.footer.link.plans': 'Planes',
  'landing.footer.link.apps': 'Apps móviles',
  'landing.footer.company': 'Empresa',
  'landing.footer.link.about': 'Sobre nosotros',
  'landing.footer.link.contact': 'Contacto',
  'landing.footer.link.support': 'Soporte',
  'landing.footer.legal': 'Legal',
  'landing.footer.link.privacy': 'Política de Privacidad',
  'landing.footer.link.terms': 'Términos y Condiciones',
  'landing.footer.link.cookies': 'Política de Cookies',
  'landing.footer.copyright': '© 2026 Tembiapo · des.evoluado.com/tembiapo',
  'landing.footer.madeIn': 'Hecho en Paraguay 🇵🇾 — presente en Brasil 🇧🇷 · LGPD & Ley 6534',
  'landing.footer.bancard': 'Pagá con tarjeta',

  // CTA reutilizable de páginas públicas
  'public.cta.title': 'Empezá gratis con Tembiapo',
  'public.cta.body': '30 días de prueba, sin tarjeta. Creá tu empresa y tus primeros tableros en minutos.',
  'public.cta.button': 'Empezar gratis',

  // Sobre nosotros (/sobre-nosotros)
  'about.eyebrow': 'Sobre nosotros',
  'about.title': 'Trabajo simple, para equipos que hacen que las cosas pasen',
  'about.lead':
    'Tembiapo (tembiapo = "trabajo" en guaraní) es una herramienta de gestión de proyectos y tareas pensada para equipos de Paraguay y Brasil. La construimos con una idea fija: lo mejor de las herramientas grandes, sin la complejidad.',
  'about.mission.title': 'Nuestra misión',
  'about.mission.body':
    'Ayudar a los equipos de Paraguay y Brasil a gestionar sus proyectos y tareas sin complicaciones. Creemos que una herramienta de trabajo se tiene que entender en cinco minutos, no en un curso: por eso cuidamos cada pantalla para que sea clara, rápida y en tu idioma.',
  'about.verticals.title': 'Plantillas por rubro',
  'about.verticals.body':
    'No arrancás de cero. Tembiapo trae tableros y flujos listos según tu actividad — equipos de tecnología (sprints, backlog y bugs), marketing (campañas, clientes y calendario de contenidos) y más. Elegís tu rubro al registrarte y ya tenés un punto de partida; después lo adaptás a tu manera de trabajar.',
  'about.values.title': 'En qué creemos',
  'about.values.body':
    '• Simple primero: si algo se puede hacer más fácil, lo hacemos más fácil.\n• Cerca tuyo: soporte real, en español y português, por WhatsApp.\n• Tus datos son tuyos: privacidad y seguridad desde el diseño, con cumplimiento de la Ley 6534/2020 (Paraguay) y la LGPD (Brasil).\n• Hecho en la región: pensado para cómo trabajan de verdad los equipos de Paraguay y Brasil.',
  'about.who.title': 'Quiénes somos',
  'about.who.body':
    'Tembiapo es un producto de GRUPO EVOLUADO EAS (marca Evoluado), agencia de tecnología con sede en Lambaré, Paraguay. Si querés escribirnos, estamos en hola@evoluado.com y por WhatsApp al +595 992 954 499.',

  // Contacto (/contacto)
  'contact.eyebrow': 'Contacto',
  'contact.title': 'Hablemos',
  'contact.lead':
    '¿Tenés una duda, querés una demo o necesitás una mano? Escribinos por el canal que te quede más cómodo. Respondemos en español y português.',
  'contact.whatsapp.title': 'WhatsApp',
  'contact.whatsapp.body': 'La vía más rápida. Escribinos y te respondemos en horario de atención.',
  'contact.whatsapp.value': '+595 992 954 499',
  'contact.email.title': 'Email',
  'contact.email.body': 'Para consultas comerciales, soporte o temas de facturación.',
  'contact.email.value': 'hola@evoluado.com',
  'contact.address.title': 'Dónde estamos',
  'contact.address.value':
    'GRUPO EVOLUADO EAS\nMayor Mazo 350, Lambaré\nDepartamento Central, Paraguay',
  'contact.hours.title': 'Horario y tiempo de respuesta',
  'contact.hours.body':
    'Atendemos de lunes a viernes, de 8:00 a 18:00 (hora de Paraguay). Solemos responder los mensajes dentro del mismo día hábil.',
  'contact.social.title': 'Redes',
  'contact.social.body':
    'Seguinos como @evoluado en Instagram y X, y encontranos en Facebook y LinkedIn como Evoluado.',

  // Soporte (/soporte)
  'support.eyebrow': 'Soporte',
  'support.title': 'Centro de ayuda',
  'support.lead':
    'Respuestas rápidas a las preguntas más comunes. Si no encontrás lo que buscás, escribinos por WhatsApp y te damos una mano.',
  'support.faq.title': 'Preguntas frecuentes',
  'support.faq.q1': '¿Cómo empiezo a usar Tembiapo?',
  'support.faq.a1':
    'Creá tu empresa desde "Probar gratis", elegí tu rubro y tu plan, y cargá tus datos. En segundos tenés tu espacio con tableros de ejemplo listos para trabajar.',
  'support.faq.q2': '¿La prueba gratis tiene costo o pide tarjeta?',
  'support.faq.a2':
    'No. La prueba dura 30 días, sin costo y sin tarjeta de crédito. Al terminar, activás un plan para seguir trabajando; mientras tanto no se te cobra nada.',
  'support.faq.q3': '¿Cómo invito a mi equipo?',
  'support.faq.a3':
    'Desde la sección Equipo tocá "Invitar" y cargá el email de cada persona. Le llega un link para completar su cuenta. Podés invitar hasta el límite de usuarios de tu plan.',
  'support.faq.q4': '¿Cómo registro un pago?',
  'support.faq.a4':
    'En Facturación elegís "Registrar pago", cargás el monto y adjuntás tu comprobante de transferencia o depósito. Nuestro equipo lo valida y activa tu plan.',
  'support.cta.title': '¿Seguís con dudas?',
  'support.cta.body':
    'Escribinos por WhatsApp o mirá todos nuestros medios de contacto. Estamos para ayudarte.',
  'support.cta.contact': 'Ver medios de contacto',

  // ==========================================================================
  // Centro de ayuda (/ayuda) — índice + artículos. Fuente de verdad en ES.
  // ==========================================================================
  'help.nav.help': 'Centro de ayuda',
  'help.index.eyebrow': 'Centro de ayuda',
  'help.index.title': 'Aprendé a sacarle todo el jugo a Tembiapo',
  'help.index.lead':
    'Guías cortas y prácticas, paso a paso, para configurar tu espacio y trabajar con tu equipo. ¿No encontrás lo que buscás? Escribinos por WhatsApp.',
  'help.search.placeholder': 'Buscar en la ayuda…',
  'help.search.empty':
    'No encontramos guías para “{q}”. Probá con otras palabras o escribinos por WhatsApp.',
  'help.search.results': '{n} resultado(s) para “{q}”',
  'help.backToIndex': 'Volver al Centro de ayuda',
  'help.article.related': 'Más de esta categoría',
  'help.article.allArticles': 'Ver todas las guías',
  'help.article.notFoundTitle': 'Guía no encontrada',
  'help.article.notFoundBody':
    'Puede que el enlace esté desactualizado. Volvé al Centro de ayuda para ver todas las guías disponibles.',
  'help.callout.tip': 'Tip',
  'help.callout.note': 'Nota',
  'help.callout.warn': 'Atención',
  'help.cta.title': '¿No resolviste tu duda?',
  'help.cta.body':
    'Nuestro equipo te responde por WhatsApp o por los demás medios de contacto. También podés mirar las preguntas frecuentes en Soporte.',
  'help.cta.support': 'Ir a Soporte',

  // Categorías
  'help.cat.empezar': 'Primeros pasos',
  'help.cat.empezar.desc': 'Creá tu empresa y dejá todo listo para trabajar.',
  'help.cat.proyectos': 'Proyectos y tableros',
  'help.cat.proyectos.desc': 'Plantillas, Kanban y todas las vistas de tu trabajo.',
  'help.cat.colaboracion': 'Equipo y colaboración',
  'help.cat.colaboracion.desc': 'Invitá gente, comentá, mencioná y mantené a todos al tanto.',
  'help.cat.productividad': 'Productividad',
  'help.cat.productividad.desc': 'Automatizaciones, dependencias y reportes.',
  'help.cat.cuenta': 'Cuenta y facturación',
  'help.cat.cuenta.desc': 'Activá tu plan y gestioná tu suscripción.',
  'help.cat.administracion': 'Administración',
  'help.cat.administracion.desc':
    'Guías del panel de Super Admin: planes, descuentos, SEO, anuncios y seguridad.',

  // --- Artículo: Primeros pasos ---------------------------------------------
  'help.a.app-movil.title': 'Conectar la app móvil',
  'help.a.app-movil.summary':
    'Llevá tus proyectos en el teléfono. La app se conecta vinculando el dispositivo con un QR (o código) desde tu panel web — sin contraseñas — y después entrás con tu huella o rostro.',
  'help.a.app-movil.s1.t': '¿Para qué sirve?',
  'help.a.app-movil.s1.b':
    'La app móvil de Tembiapo te deja ver y mover tus tareas, comentar y recibir notificaciones nativas desde el celular. Para tu seguridad ya no usa usuario y contraseña en el teléfono: se vincula una sola vez con tu cuenta y después se desbloquea con biometría (huella o rostro).',
  'help.a.app-movil.s2.t': 'Cómo conectarla (paso a paso)',
  'help.a.app-movil.s2.b':
    '• En la web, entrá a Inicio y tocá la acción rápida “Conectar app móvil” (o andá a tu Perfil → “Conectar app móvil”).\n\n• Se genera un código QR y un código de 6 dígitos.\n\n• Abrí la app en tu teléfono y, en la pantalla de inicio, escaneá el QR con la cámara. Si preferís, ingresá el código de 6 dígitos a mano.\n\n• ¡Listo! Ya quedás dentro de la app con tu empresa.',
  'help.a.app-movil.s3.t': 'Entrar con biometría',
  'help.a.app-movil.s3.b':
    'Después de vincular, la app queda protegida con la biometría de tu teléfono. Cada vez que la abrís te pide tu huella o rostro para desbloquear — rápido y seguro, sin escribir contraseñas.',
  'help.a.app-movil.c1':
    'El QR y el código sirven una sola vez y vencen a los 3 minutos. Si se te vence, generá uno nuevo desde la web con “Generar de nuevo”.',
  'help.a.app-movil.c2':
    '¿Todavía no tenés cuenta? El registro de la empresa se hace en la web (tembiapoapp.com); después vinculás la app.',
  'help.a.primeros-pasos.title': 'Primeros pasos',
  'help.a.primeros-pasos.summary':
    'Creá tu empresa, elegí tu rubro y hacé tu primer login. Todo listo en minutos.',
  'help.a.primeros-pasos.s1.t': 'Creá tu empresa',
  'help.a.primeros-pasos.s1.b':
    'Entrá a “Probar gratis” desde la landing y cargá el nombre de tu empresa, tu nombre y un email. Con eso creamos tu espacio de trabajo y tu usuario administrador. No necesitás tarjeta de crédito.',
  'help.a.primeros-pasos.s2.t': 'Elegí tu rubro',
  'help.a.primeros-pasos.s2.b':
    'En el registro elegís tu actividad: Tecnología, Marketing, PyME o Estudio jurídico, entre otras. Según el rubro precargamos proyectos y plantillas pensados para vos, así no arrancás desde cero.',
  'help.a.primeros-pasos.s3.t': 'La prueba de 30 días',
  'help.a.primeros-pasos.s3.b':
    'Tu cuenta arranca en modo prueba por 30 días, sin costo y sin tarjeta. Podés usar todas las funciones. Cuando falten pocos días te avisamos, y al vencer tu espacio queda en solo lectura hasta que actives un plan.',
  'help.a.primeros-pasos.s4.t': 'Tu primer login',
  'help.a.primeros-pasos.s4.b':
    'Ingresás con tu email y contraseña desde “Ingresar”. La primera vez te mostramos un tour de 30 segundos con lo básico. Podés repetirlo cuando quieras desde el menú de tu usuario.',
  'help.a.primeros-pasos.c1':
    'Invitá a tu equipo cuanto antes: trabajar juntos desde el día uno es la mejor forma de ver el valor de Tembiapo.',

  // --- Artículo: Crear un proyecto desde una plantilla ----------------------
  'help.a.crear-proyecto-plantilla.title': 'Crear un proyecto desde una plantilla',
  'help.a.crear-proyecto-plantilla.summary':
    'Elegí una plantilla de tu rubro o arrancá en blanco. Te contamos qué copia cada una.',
  'help.a.crear-proyecto-plantilla.s1.t': 'En blanco o desde plantilla',
  'help.a.crear-proyecto-plantilla.s1.b':
    'Al crear un proyecto elegís entre empezar en blanco —un tablero vacío con columnas básicas— o usar una plantilla. La plantilla te ahorra toda la configuración inicial y te deja listo para trabajar.',
  'help.a.crear-proyecto-plantilla.s2.t': 'La galería por rubro',
  'help.a.crear-proyecto-plantilla.s2.b':
    'La galería agrupa las plantillas según tu rubro. Cada tarjeta te muestra para qué sirve la plantilla y qué incluye, así elegís la que mejor se parece a tu forma de trabajar.',
  'help.a.crear-proyecto-plantilla.fig1.alt':
    'Pantalla de creación de proyecto con la galería de plantillas agrupadas por rubro',
  'help.a.crear-proyecto-plantilla.fig1.cap':
    'Galería de plantillas: elegí por rubro o empezá en blanco.',
  'help.a.crear-proyecto-plantilla.s3.t': 'Qué copia una plantilla',
  'help.a.crear-proyecto-plantilla.s3.b':
    '• Las columnas del tablero (los estados por los que pasa el trabajo).\n• Los campos de la tarea: prioridad, responsable, fechas y campos propios del rubro.\n• El flujo de trabajo sugerido y, en algunos casos, tareas de ejemplo para orientarte.',
  'help.a.crear-proyecto-plantilla.s4.t': 'Después de crear',
  'help.a.crear-proyecto-plantilla.s4.b':
    'Podés renombrar el proyecto, ajustar las columnas y sumar a tu equipo en cualquier momento. La plantilla es un punto de partida, no una jaula.',
  'help.a.crear-proyecto-plantilla.c1':
    'Si dudás, empezá con la plantilla más parecida a tu proceso y ajustala. Es más rápido que armar todo de cero.',

  // --- Artículo: El tablero Kanban ------------------------------------------
  'help.a.tablero-kanban.title': 'El tablero Kanban',
  'help.a.tablero-kanban.summary':
    'La vista estrella: columnas, tarjetas y arrastrar y soltar para mover tu trabajo.',
  'help.a.tablero-kanban.s1.t': 'Columnas y tarjetas',
  'help.a.tablero-kanban.s1.b':
    'El Kanban muestra tu trabajo en columnas (los estados) y cada tarea es una tarjeta. De un vistazo ves qué hay por hacer, qué está en curso y qué ya terminó.',
  'help.a.tablero-kanban.fig1.alt': 'Tablero Kanban con columnas y tarjetas de tareas',
  'help.a.tablero-kanban.fig1.cap':
    'Tablero Kanban: arrastrá las tarjetas entre columnas para cambiar su estado.',
  'help.a.tablero-kanban.s2.t': 'Agregar y mover tareas',
  'help.a.tablero-kanban.s2.b':
    'Tocá el botón + de una columna para crear una tarea. Para cambiarla de estado, arrastrala y soltala en otra columna (drag & drop). También podés reordenar tarjetas dentro de la misma columna.',
  'help.a.tablero-kanban.s3.t': 'Prioridad, responsable y fecha',
  'help.a.tablero-kanban.s3.b':
    '• Prioridad: baja, media, alta o urgente, con color para ubicarla rápido.\n• Responsable: asignás a un miembro del equipo y su avatar aparece en la tarjeta.\n• Fecha de vencimiento: las tarjetas vencidas o próximas a vencer se marcan para que no se te pase nada.',
  'help.a.tablero-kanban.s4.t': 'Editar columnas y límite WIP',
  'help.a.tablero-kanban.s4.b':
    'Podés agregar, renombrar, reordenar o cambiar el color de las columnas. Además, cada columna admite un límite WIP (trabajo en curso): si se supera, te avisa para no sobrecargar al equipo.',
  'help.a.tablero-kanban.s5.t': 'Indicador de dependencia',
  'help.a.tablero-kanban.s5.b':
    'Si una tarea está bloqueada por otra, la tarjeta muestra un indicador de dependencia. Así sabés qué no puede avanzar hasta que se libere aquello que la bloquea.',
  'help.a.tablero-kanban.c1':
    'El límite WIP es una guía, no un candado: te avisa, pero vos decidís. Menos tareas en curso a la vez suele significar terminar más rápido.',

  // --- Artículo: Otras vistas -----------------------------------------------
  'help.a.otras-vistas.title': 'Otras vistas: Lista, Calendario y Timeline',
  'help.a.otras-vistas.summary':
    'El mismo trabajo, distintas miradas. Elegí la vista que mejor te sirve en cada momento.',
  'help.a.otras-vistas.s1.t': 'Vista Lista / Tabla',
  'help.a.otras-vistas.s1.b':
    'La Lista muestra tus tareas en filas, ideal para revisar mucho de un vistazo.\n\n• Filtrá por responsable, prioridad, estado o fecha.\n• Ordená por cualquier columna.\n• Agrupá por estado, responsable o prioridad.\n• Guardá tus combinaciones como filtros guardados para reusarlas con un clic.',
  'help.a.otras-vistas.fig1.alt': 'Vista de Lista o Tabla con filtros y agrupación de tareas',
  'help.a.otras-vistas.fig1.cap': 'Vista Lista: filtrá, ordená, agrupá y guardá tus filtros.',
  'help.a.otras-vistas.s2.t': 'Vista Calendario',
  'help.a.otras-vistas.s2.b':
    'El Calendario ubica tus tareas por fecha de vencimiento. Arrastrá una tarea a otro día para cambiarle la fecha sin siquiera abrirla.',
  'help.a.otras-vistas.fig2.alt': 'Vista de Calendario con tareas ubicadas por fecha',
  'help.a.otras-vistas.fig2.cap': 'Calendario: arrastrá una tarea a otro día para reprogramarla.',
  'help.a.otras-vistas.s3.t': 'Vista Timeline / Gantt',
  'help.a.otras-vistas.s3.b':
    'El Timeline muestra la duración de las tareas en el tiempo y cómo se relacionan. Sirve para planificar y ver de un vistazo qué se solapa o qué depende de qué.',
  'help.a.otras-vistas.fig3.alt': 'Vista Timeline o Gantt con la duración de las tareas',
  'help.a.otras-vistas.fig3.cap': 'Timeline: planificá y visualizá dependencias en el tiempo.',
  'help.a.otras-vistas.c1':
    'Todas las vistas trabajan sobre los mismos datos: lo que cambiás en una se refleja en las demás al instante.',

  // --- Artículo: Tareas: comentarios, menciones y adjuntos ------------------
  'help.a.tareas-colaboracion.title': 'Tareas: comentarios, menciones y adjuntos',
  'help.a.tareas-colaboracion.summary':
    'Conversá dentro de cada tarea, sumá a quien haga falta y adjuntá archivos.',
  'help.a.tareas-colaboracion.s1.t': 'Comentarios',
  'help.a.tareas-colaboracion.s1.b':
    'Abrí una tarea y escribí en Comentarios para dejar contexto, decisiones o novedades. Todo queda registrado en orden cronológico, para que el equipo siempre tenga la historia completa.',
  'help.a.tareas-colaboracion.s2.t': 'Menciones con @',
  'help.a.tareas-colaboracion.s2.b':
    'Escribí @ seguido del email de un compañero para mencionarlo. Le llega una notificación con el enlace directo a la tarea, aunque no sea el responsable.',
  'help.a.tareas-colaboracion.s3.t': 'Adjuntos',
  'help.a.tareas-colaboracion.s3.b':
    'Subí archivos —documentos, imágenes, comprobantes— desde la tarea. Quedan asociados a ella para que el equipo los tenga siempre a mano.',
  'help.a.tareas-colaboracion.c1':
    'Usá las menciones en lugar de chats sueltos: el contexto queda pegado a la tarea y cualquiera lo encuentra después.',

  // --- Artículo: Equipo e invitaciones --------------------------------------
  'help.a.equipo-invitaciones.title': 'Equipo e invitaciones',
  'help.a.equipo-invitaciones.summary':
    'Invitá a tu equipo por email, asigná roles y controlá el límite de tu plan.',
  'help.a.equipo-invitaciones.s1.t': 'Invitar por email',
  'help.a.equipo-invitaciones.s1.b':
    'Desde Equipo tocá “Invitar” y cargá el email de cada persona. Le llega un enlace para crear su contraseña y sumarse a tu empresa.',
  'help.a.equipo-invitaciones.fig1.alt': 'Página de Equipo con la lista de miembros e invitaciones',
  'help.a.equipo-invitaciones.fig1.cap':
    'Página de Equipo: invitá, reenviá invitaciones y gestioná roles.',
  'help.a.equipo-invitaciones.s2.t': 'Roles: administrador y miembro',
  'help.a.equipo-invitaciones.s2.b':
    '• Administrador: gestiona el equipo, la facturación y la configuración, además de trabajar en los proyectos.\n• Miembro: trabaja en los proyectos —crea y mueve tareas, comenta— sin acceso a facturación ni a la gestión del equipo.',
  'help.a.equipo-invitaciones.s3.t': 'Aceptar una invitación',
  'help.a.equipo-invitaciones.s3.b':
    'La persona invitada abre el enlace del email, define su contraseña y ya entra a tu espacio. Si el enlace venció, reenviá la invitación desde Equipo.',
  'help.a.equipo-invitaciones.s4.t': 'Límite del plan',
  'help.a.equipo-invitaciones.s4.b':
    'Podés invitar hasta el máximo de usuarios de tu plan, contando las invitaciones pendientes. Si necesitás más, sumá un add-on o cambiá de plan desde Facturación.',
  'help.a.equipo-invitaciones.c1':
    'Las invitaciones pendientes cuentan para el límite. Si te quedaste sin cupos, cancelá las que no se usaron o ampliá tu plan.',

  // --- Artículo: Automatizaciones -------------------------------------------
  'help.a.conectar-ia.title': 'Conectar Tembiapo con tu IA (Claude, ChatGPT, Gemini)',
  'help.a.conectar-ia.summary':
    'Operá Tembiapo por lenguaje natural desde tu IA: crear y asignar tareas, moverlas, comentar y usar plantillas — todo dentro de tu empresa.',
  'help.a.conectar-ia.s1.t': '¿Qué es y para qué sirve?',
  'help.a.conectar-ia.s1.b':
    'Tembiapo se conecta con asistentes de IA mediante el estándar MCP. Una vez conectado, podés pedirle a tu IA cosas como “creá una tarea ‘Llamar al proveedor’ con prioridad alta en el proyecto Ventas”, “¿qué me vence esta semana?” o “mové la tarea X a Hecho”, y se ejecuta en tu Tembiapo. Todo queda scopeado a tu empresa (la IA no ve datos de otras).',
  'help.a.conectar-ia.s2.t': 'Paso 1: generá tu token',
  'help.a.conectar-ia.s2.b':
    'Entrá a tu Perfil → sección “Conectar con IA” → tocá “Generar token para IA”. Se muestra un token (empieza con tbmcp_) una sola vez: copialo y guardalo como si fuera una contraseña. Podés revocarlo cuando quieras desde la misma sección.',
  'help.a.conectar-ia.s3.t': 'Paso 2: conectá tu IA',
  'help.a.conectar-ia.s3.b':
    '• Claude Code (terminal): pegá el comando que te muestra la app —\n  claude mcp add tembiapo --scope user --transport http https://www.tembiapoapp.com/api/mcp --header "Authorization: Bearer TU_TOKEN"\n\n• Gemini CLI: gemini mcp add tembiapo --transport http https://www.tembiapoapp.com/api/mcp --header "Authorization: Bearer TU_TOKEN"\n\n• ChatGPT: en Configuración → Conectores, agregá un conector con la URL https://www.tembiapoapp.com/api/mcp y el header Authorization: Bearer TU_TOKEN.',
  'help.a.conectar-ia.c1':
    'Ejemplos para pedirle a tu IA: “listá mis proyectos”, “creá 5 tareas de onboarding en el proyecto Lanzamiento”, “asigná la tarea X a juan@empresa.com”, “comentá ‘revisar mañana’ en la tarea Y”.',
  'help.a.conectar-ia.c2':
    'Por seguridad, la IA NO borra tareas ni toca facturación/planes/miembros: si le pedís borrar algo, te devuelve el enlace para hacerlo desde la web. El token es personal y revocable.',
  'help.a.automatizaciones.title': 'Automatizaciones (cuando → entonces)',
  'help.a.automatizaciones.summary':
    'Dejá que Tembiapo haga las tareas repetitivas por vos con reglas simples.',
  'help.a.automatizaciones.s1.t': 'Cómo funcionan',
  'help.a.automatizaciones.s1.b':
    'Una automatización es una regla con dos partes: un disparador (cuando pasa algo) y una acción (entonces hacé esto). Se ejecutan solas, sin que tengas que acordarte.',
  'help.a.automatizaciones.s2.t': 'Ejemplos útiles',
  'help.a.automatizaciones.s2.b':
    '• Cuando una tarea pasa a “Hecho” → notificar al responsable del proyecto.\n• Cuando una tarea entra a “En revisión” → asignarla a quien revisa.\n• Cuando se marca como “Urgente” → subir su prioridad y avisar por notificación.',
  'help.a.automatizaciones.s3.t': 'Crearlas y activarlas',
  'help.a.automatizaciones.s3.b':
    'Definís el disparador, elegís la acción y la activás. Podés tener varias reglas por proyecto y desactivarlas cuando no las necesites.',
  'help.a.automatizaciones.c1':
    'Empezá con una o dos reglas simples: automatizá eso que hoy hacés a mano y siempre se te olvida.',

  // --- Artículo: Plantillas de tareas y dependencias ------------------------
  'help.a.plantillas-dependencias.title': 'Plantillas de tareas y dependencias',
  'help.a.plantillas-dependencias.summary':
    'Reutilizá tareas que se repiten y ordená qué va antes que qué.',
  'help.a.plantillas-dependencias.s1.t': 'Guardar una tarea como plantilla',
  'help.a.plantillas-dependencias.s1.b':
    'Si una tarea, con sus campos y su checklist, se repite seguido, guardala como plantilla. Después la creás de nuevo en un clic, ya configurada.',
  'help.a.plantillas-dependencias.s2.t': 'Dependencias: “bloqueada por”',
  'help.a.plantillas-dependencias.s2.b':
    'Marcá que una tarea está “bloqueada por” otra cuando no puede avanzar hasta que la primera termine. En el Kanban verás el indicador de dependencia en la tarjeta.',
  'help.a.plantillas-dependencias.s3.t': 'Por qué importa',
  'help.a.plantillas-dependencias.s3.b':
    'Las dependencias evitan que alguien arranque algo que todavía no se puede hacer, y en el Timeline te muestran el camino crítico del proyecto.',
  'help.a.plantillas-dependencias.c1':
    'Evitá dependencias circulares (A bloquea a B y B bloquea a A): nadie podría avanzar. Tembiapo te avisa si intentás crear una.',

  // --- Artículo: Notificaciones ---------------------------------------------
  'help.a.notificaciones.title': 'Notificaciones',
  'help.a.notificaciones.summary':
    'Enterate de lo importante por la campanita, email y push, a tu manera.',
  'help.a.notificaciones.s1.t': 'La campanita',
  'help.a.notificaciones.s1.b':
    'El ícono de campana en la barra superior te muestra menciones, asignaciones y vencimientos. El número indica cuántas notificaciones no leíste.',
  'help.a.notificaciones.s2.t': 'Email y push (PWA)',
  'help.a.notificaciones.s2.b':
    'Además de la campanita, podés recibir avisos por email y notificaciones push. Si instalás Tembiapo como app (PWA) en tu teléfono, las push te llegan aunque no la tengas abierta.',
  'help.a.notificaciones.s3.t': 'Preferencias',
  'help.a.notificaciones.s3.b':
    'Desde Preferencias elegís qué eventos te notifican y por qué canal. Así recibís solo lo que te importa, sin ruido.',
  'help.a.notificaciones.c1':
    'Instalá Tembiapo como app en tu celular para recibir push: es la forma más rápida de no perderte una mención o un vencimiento.',

  // --- Artículo: Reportes ---------------------------------------------------
  'help.a.reportes.title': 'Reportes',
  'help.a.reportes.summary':
    'Mirá cómo avanza el trabajo: por prioridad, responsable, proyecto y vencidas.',
  'help.a.reportes.s1.t': 'Qué vas a ver',
  'help.a.reportes.s1.b':
    'El panel de Reportes resume el estado de tu trabajo con métricas y gráficos, sin que tengas que armar planillas a mano.',
  'help.a.reportes.fig1.alt': 'Panel de Reportes con métricas y gráficos',
  'help.a.reportes.fig1.cap':
    'Dashboard de Reportes: métricas por prioridad, responsable y proyecto.',
  'help.a.reportes.s2.t': 'Métricas principales',
  'help.a.reportes.s2.b':
    '• Por prioridad: cuántas tareas hay en cada nivel.\n• Por responsable: la carga de cada persona del equipo.\n• Por proyecto: avance y volumen de trabajo.\n• Vencidas: lo que pasó su fecha y necesita atención.',
  'help.a.reportes.s3.t': 'Para qué sirve',
  'help.a.reportes.s3.b':
    'Usalos en tus reuniones para decidir dónde poner el foco, repartir mejor la carga y detectar cuellos de botella a tiempo.',

  // --- Artículo: Facturación: activar tu plan -------------------------------
  'help.a.facturacion.title': 'Facturación: activar tu plan',
  'help.a.facturacion.summary':
    'Registrá tu pago con comprobante y seguí el estado de tu suscripción.',
  'help.a.facturacion.s1.t': 'Cuándo activar',
  'help.a.facturacion.s1.b':
    'Durante la prueba de 30 días no pagás nada. Para seguir después del trial —o para ampliar límites antes— activás un plan desde Facturación.',
  'help.a.facturacion.s2.t': 'Registrar un pago',
  'help.a.facturacion.s2.b':
    'Elegí “Registrar pago”, indicá el monto y el plan, y adjuntá tu comprobante de transferencia o depósito. Nuestro equipo lo valida y activa tu suscripción.',
  'help.a.facturacion.s3.t': 'Estados de la suscripción',
  'help.a.facturacion.s3.b':
    '• Prueba: usás todo por 30 días, sin costo.\n• Pago pendiente: registraste el pago y lo estamos validando.\n• Activa: tu plan está al día.\n• Vencida: la prueba o el período terminó; tu espacio queda en solo lectura hasta regularizar.',
  'help.a.facturacion.c1':
    'Mientras validamos tu comprobante seguís trabajando normalmente. Si el trial venció, apenas confirmemos el pago se reactiva la escritura.',

  // --- Artículo: Plantillas por rubro ---------------------------------------
  'help.a.plantillas-por-rubro.title': 'Plantillas por rubro',
  'help.a.plantillas-por-rubro.summary':
    'Ejemplos de flujo listos para Tecnología, Marketing, PyMEs y Estudios jurídicos.',
  'help.a.plantillas-por-rubro.fig1.alt':
    'Galería de plantillas de proyecto agrupadas por rubro',
  'help.a.plantillas-por-rubro.fig1.cap':
    'Elegí la plantilla de tu rubro desde la galería al crear un proyecto.',
  'help.a.plantillas-por-rubro.s1.t': 'Tecnología',
  'help.a.plantillas-por-rubro.s1.b':
    'Pensado para equipos de software: un flujo tipo Backlog → En progreso → En revisión → Hecho, con prioridad, responsable y estimaciones. Ideal para sprints y para seguir features y bugs.',
  'help.a.plantillas-por-rubro.s2.t': 'Marketing',
  'help.a.plantillas-por-rubro.s2.b':
    'Para campañas y contenido: Ideas → En producción → En aprobación → Publicado. Sumá fechas de salida y un responsable por pieza para que nada se atrase.',
  'help.a.plantillas-por-rubro.s3.t': 'PyMEs — Pipeline de ventas',
  'help.a.plantillas-por-rubro.s3.b':
    'Seguimiento comercial: Prospecto → Contactado → Propuesta → Negociación → Ganado/Perdido. Cada tarjeta es una oportunidad con su valor y su responsable.',
  'help.a.plantillas-por-rubro.s4.t': 'Abogados — Gestión de casos',
  'help.a.plantillas-por-rubro.s4.b':
    'Para estudios jurídicos: seguí cada expediente por sus estados procesales (Ingreso → En trámite → Prueba → Sentencia → Archivado), con plazos y vencimientos clave para no perder ningún término, y los datos del expediente siempre a mano.',
  'help.a.plantillas-por-rubro.c1':
    'Cualquier plantilla es editable: tomá la más cercana a tu proceso y ajustá columnas, campos y flujo a tu gusto.',

  // --- Artículo: Renombrar y eliminar proyectos -----------------------------
  'help.a.gestionar-proyectos.title': 'Renombrar y eliminar proyectos',
  'help.a.gestionar-proyectos.summary':
    'Cambiá el nombre y la descripción de un proyecto, o eliminalo cuando ya no lo necesites.',
  'help.a.gestionar-proyectos.s1.t': 'Renombrar desde Proyectos',
  'help.a.gestionar-proyectos.s1.b':
    'En la lista de Proyectos, cada tarjeta tiene un menú de tres puntos (⋯). Abrilo y elegí “Renombrar” para cambiar el nombre y la descripción del proyecto. Los cambios se aplican al instante y no afectan las tareas ni las columnas.',
  'help.a.gestionar-proyectos.s2.t': 'Renombrar desde el tablero',
  'help.a.gestionar-proyectos.s2.b':
    'Si ya estás dentro de un proyecto, tenés el botón “Renombrar” en la cabecera del tablero. Es el mismo formulario: ajustás nombre y descripción sin salir de donde estás trabajando.',
  'help.a.gestionar-proyectos.s3.t': 'Eliminar un proyecto',
  'help.a.gestionar-proyectos.s3.b':
    'La opción “Eliminar” está en el mismo menú de tres puntos (⋯), pero solo la ven los administradores de la empresa. Al elegirla te pedimos una confirmación, y recién ahí se borra el proyecto junto con todas sus tareas, columnas y comentarios.',
  'help.a.gestionar-proyectos.c1':
    'Eliminar un proyecto es permanente: no se puede deshacer y se borran también sus tareas. Si tenés dudas, renombralo o archivá su contenido antes de borrar.',

  // --- Artículo: Subtareas dentro de una tarea ------------------------------
  'help.a.subtareas.title': 'Subtareas dentro de una tarea',
  'help.a.subtareas.summary':
    'Dividí una tarea en pasos, asigná responsables y marcá los que son obligatorios.',
  'help.a.subtareas.s1.t': 'Crear y completar subtareas',
  'help.a.subtareas.s1.b':
    'Abrí una tarea y sumá subtareas para dividir el trabajo en pasos más chicos. Cada subtarea se puede marcar como hecha con un clic, así ves de un vistazo cuánto falta para terminar la tarea completa.',
  'help.a.subtareas.s2.t': 'Responsable y subtareas obligatorias',
  'help.a.subtareas.s2.b':
    '• Responsable: asignás cada subtarea a una persona del equipo, para que quede claro quién hace qué.\n• Obligatoria: marcás una subtarea como obligatoria cuando es un paso que no se puede saltear antes de avanzar la tarea.',
  'help.a.subtareas.s3.t': 'Cómo funciona el bloqueo',
  'help.a.subtareas.s3.b':
    'Una subtarea obligatoria bloquea el avance de la tarea hasta que la completes (u omitas). Una vez que la tarea avanzó, una obligatoria ya completada no se puede desmarcar; si necesitás corregirla, mové la tarea hacia atrás y ahí la editás.',
  'help.a.subtareas.s4.t': 'Para qué paso es obligatoria',
  'help.a.subtareas.s4.b':
    'Al marcar una subtarea como obligatoria, elegís “para pasar a…” una columna (por defecto, la última — “Hecho”). Bloquea entrar a ESA columna y las siguientes, pero NO las anteriores. Así, una obligatoria “para terminar” no te impide empezar la tarea, solo cerrarla. Ejemplo: “Aprobación del cliente” obligatoria para pasar a “Hecho”.',
  'help.a.subtareas.s5.t': 'Si no se puede completar: omitir',
  'help.a.subtareas.s5.b':
    'Si una obligatoria quedó trabada y no se puede hacer, un administrador o el responsable de la tarea puede tocar “Omitir” y escribir un motivo. La tarea se desbloquea y queda registrado quién la omitió y por qué. Se puede reactivar después.',
  'help.a.subtareas.c1':
    'Usá las obligatorias para tus pasos de control de calidad: “revisado”, “aprobado”, “publicado”. Así nada avanza sin cumplir lo importante.',

  // --- Artículo: El panel de inicio -----------------------------------------
  'help.a.panel-inicio.title': 'El panel de inicio',
  'help.a.panel-inicio.summary':
    'Tu pantalla de arranque: acciones rápidas, novedades del equipo y contactos a mano.',
  'help.a.panel-inicio.s1.t': 'Acciones rápidas',
  'help.a.panel-inicio.s1.b':
    'Al entrar caés en el panel de inicio. Desde ahí hacés lo más común de un toque: crear un proyecto nuevo o invitar a alguien a tu equipo, sin tener que buscar el menú.',
  'help.a.panel-inicio.s2.t': 'Novedades: comentarios y cambios de estado',
  'help.a.panel-inicio.s2.b':
    '• Últimos comentarios: los mensajes más recientes de tus tareas, para retomar la conversación al instante.\n• Últimos cambios de estado: qué tareas se movieron de columna y quién las movió, así seguís el avance del trabajo sin abrir cada proyecto.',
  'help.a.panel-inicio.s3.t': 'Tu equipo a la vista',
  'help.a.panel-inicio.s3.b':
    'La tarjeta de equipo muestra a tus compañeros con su foto y sus datos de contacto. Es la forma más rápida de saber quién está en tu empresa y cómo ubicarlo.',
  'help.a.panel-inicio.c1':
    'Volvé al inicio cuando arranques el día: en un vistazo ves lo último que pasó y por dónde seguir.',

  // --- Artículo: Cambiar el idioma ------------------------------------------
  'help.a.cambiar-idioma.title': 'Cambiar el idioma',
  'help.a.cambiar-idioma.summary':
    'Elegí entre español y portugués desde la barra superior. Tu preferencia se guarda.',
  'help.a.cambiar-idioma.s1.t': 'Español o portugués',
  'help.a.cambiar-idioma.s1.b':
    'En la barra superior tenés el selector de idioma. Elegí español (ES) o português (PT) y toda la aplicación cambia al instante. Tu elección queda guardada en tu perfil, así la próxima vez que entres ya está en el idioma que preferís.',
  'help.a.cambiar-idioma.s2.t': 'La moneda va aparte',
  'help.a.cambiar-idioma.s2.b':
    'El idioma no cambia la moneda de los precios. La moneda (₲ guaraníes, R$ reales o USD) se elige en la sección de Planes, cuando mirás o activás tu suscripción.',
  'help.a.cambiar-idioma.c1':
    'Cada persona del equipo elige su propio idioma: el cambio afecta solo a tu cuenta, no a la de tus compañeros.',

  // --- Artículo: Descuentos de planes (admin) -------------------------------
  'help.a.admin-descuentos.title': 'Descuentos de planes',
  'help.a.admin-descuentos.summary':
    'Configurá el descuento anual y una promo de lanzamiento desde Super Admin → Planes.',
  'help.a.admin-descuentos.s1.t': 'Descuento anual',
  'help.a.admin-descuentos.s1.b':
    'En Super Admin → Planes definís un descuento anual global, expresado en porcentaje. Se aplica cuando el cliente elige pagar por año en lugar de mes a mes, y es el clásico incentivo para que se comprometa por más tiempo.',
  'help.a.admin-descuentos.s2.t': 'Promo de lanzamiento',
  'help.a.admin-descuentos.s2.b':
    'Además podés activar una promo de lanzamiento por tiempo limitado. Configurás:\n• Si está activa o no.\n• El porcentaje de descuento.\n• La fecha de fin de la promo.\n• Una etiqueta (por ejemplo “Lanzamiento” u “Oferta”) que se muestra junto al precio.',
  'help.a.admin-descuentos.s3.t': 'Cómo se combinan y dónde se ven',
  'help.a.admin-descuentos.s3.b':
    'El descuento anual y la promo de lanzamiento se acumulan: si el cliente paga por año durante la promo, recibe los dos. Los precios ya rebajados aparecen automáticamente en la landing y en la pantalla de registro, sin que tengas que tocar nada más.',
  'help.a.admin-descuentos.c1':
    'Poné siempre una fecha de fin a la promo de lanzamiento: la urgencia ayuda a convertir, y evitás dejar una oferta encendida más de lo previsto.',

  // --- Artículo: SEO y campañas (admin) -------------------------------------
  'help.a.admin-seo.title': 'SEO y campañas (SEM)',
  'help.a.admin-seo.summary':
    'Metadatos por idioma, verificación de buscadores e IDs de analytics, todo desde el menú SEO.',
  'help.a.admin-seo.s1.t': 'Metadatos del sitio',
  'help.a.admin-seo.s1.b':
    'En el menú SEO cargás los metadatos con los que tu sitio aparece en buscadores y al compartir enlaces:\n• Título y descripción, por idioma (español y portugués).\n• Imagen y textos para redes sociales (Open Graph).\n• URL canónica, para indicar la dirección oficial de cada página.',
  'help.a.admin-seo.s2.t': 'Verificación de buscadores',
  'help.a.admin-seo.s2.b':
    'Podés pegar los códigos de verificación de Google y Bing para demostrar que sos el dueño del sitio. Eso te habilita a usar sus paneles de webmaster (Search Console y Bing Webmaster Tools) y ver cómo te encuentran.',
  'help.a.admin-seo.s3.t': 'Analytics y campañas',
  'help.a.admin-seo.s3.b':
    'Para medir visitas y campañas cargás los identificadores de tus herramientas: Google Analytics 4 (GA4), Google Tag Manager (GTM), Meta Pixel (Facebook/Instagram) y Google Ads. Una vez cargados, el sitio los inyecta solo, sin tocar código.',
  'help.a.admin-seo.c1':
    'Si vas a verificar Search Console, te conviene hacerlo a través de GA4 o GTM: si ya cargaste esos IDs, la verificación es directa y no tenés que pegar otro código aparte.',

  // --- Artículo: Anuncios del login (admin) ---------------------------------
  'help.a.admin-anuncios.title': 'Anuncios del login',
  'help.a.admin-anuncios.summary':
    'Mostrá avisos en la pantalla de ingreso, con versión en español y portugués.',
  'help.a.admin-anuncios.s1.t': 'Qué son los anuncios del login',
  'help.a.admin-anuncios.s1.b':
    'Los anuncios son avisos que aparecen en la pantalla de ingreso, en el centro de anuncios junto al formulario de login. Sirven para comunicar novedades, mantenimientos o promociones a todos los que entran, antes incluso de iniciar sesión.',
  'help.a.admin-anuncios.s2.t': 'Crear y editar avisos bilingües',
  'help.a.admin-anuncios.s2.b':
    'Desde Super Admin creás o editás cada anuncio con su versión en español y, opcionalmente, su versión en portugués. Si no cargás la versión en portugués, se muestra la de español como respaldo.',
  'help.a.admin-anuncios.s3.t': 'Qué versión ve cada persona',
  'help.a.admin-anuncios.s3.b':
    'Cada visitante ve el anuncio en su idioma: quienes navegan en portugués ven la versión PT, y el resto ve la versión en español. Así el mismo aviso llega bien redactado a ambos mercados.',
  'help.a.admin-anuncios.c1':
    'Escribí siempre las dos versiones para tus campañas importantes: un aviso en el idioma correcto se lee mucho más que uno traducido a medias.',

  // --- Artículo: Cambiar la contraseña del super admin (admin) --------------
  'help.a.admin-seguridad.title': 'Cambiar la contraseña del super admin con PIN',
  'help.a.admin-seguridad.summary':
    'Un cambio de clave protegido con un PIN que te llega al WhatsApp de control.',
  'help.a.admin-seguridad.s1.t': 'Iniciar el cambio',
  'help.a.admin-seguridad.s1.b':
    'Como super admin, entrás a la configuración de seguridad e ingresás tu contraseña actual y la nueva que querés usar. Por ser una cuenta sensible, no alcanza con eso: activamos una verificación extra antes de aplicar el cambio.',
  'help.a.admin-seguridad.s2.t': 'El PIN por WhatsApp',
  'help.a.admin-seguridad.s2.b':
    'Al confirmar, enviamos un PIN al número de WhatsApp de control configurado para la plataforma. Ese PIN es la prueba de que sos vos: nadie que no tenga acceso a ese WhatsApp puede completar el cambio.',
  'help.a.admin-seguridad.s3.t': 'Confirmar con el PIN',
  'help.a.admin-seguridad.s3.b':
    'Ingresás el PIN que recibiste y recién ahí guardamos la nueva contraseña. Desde ese momento, tu próximo ingreso es con la clave nueva.',
  'help.a.admin-seguridad.c1':
    'El PIN vence a los pocos minutos y tenés una cantidad limitada de intentos. Si se te venció o lo erraste varias veces, empezá el cambio de nuevo para que te enviemos uno nuevo.',

  'help.a.varias-empresas.title': 'Trabajar en varias empresas',
  'help.a.varias-empresas.summary':
    'Pertenecé a la empresa de tu cliente además de la tuya, elegí en cuál trabajar al entrar y cambiá cuando quieras.',
  'help.a.varias-empresas.s1.t': '¿Cómo funciona?',
  'help.a.varias-empresas.s1.b':
    'Una misma persona puede pertenecer a varias empresas: la tuya y la de un cliente, por ejemplo. Tu cuenta es una sola, pero cada empresa es independiente y sus datos están aislados: nunca se mezclan. Tu rol puede ser distinto en cada una.',
  'help.a.varias-empresas.s2.t': 'Elegir empresa al ingresar',
  'help.a.varias-empresas.s2.b':
    'Si pertenecés a más de una empresa, al iniciar sesión te preguntamos “¿En qué empresa vas a trabajar?” y elegís una para entrar. Si estás en una sola, entrás directo como siempre.',
  'help.a.varias-empresas.s3.t': 'Cambiar de empresa',
  'help.a.varias-empresas.s3.b':
    'Arriba a la izquierda, al lado del logo, dice “Trabajando en: [Empresa] ▾”. Tocalo para cambiar a otra empresa sin cerrar sesión: al cambiar vas a ver los proyectos, tareas y equipo de esa empresa.',
  'help.a.varias-empresas.s4.t': 'Invitar a alguien que ya usa Tembiapo',
  'help.a.varias-empresas.s4.b':
    'Podés invitar por email a alguien que ya tiene cuenta en Tembiapo. En vez de fallar, se suma a tu empresa: cuando esa persona inicia sesión con su cuenta, tu empresa le aparece para elegir y cambiar. No necesita registrarse de nuevo.',
  'help.a.varias-empresas.s5.t': 'Quitar a un miembro',
  'help.a.varias-empresas.s5.b':
    'Desde Equipo, un administrador puede tocar “Quitar” junto a un miembro para sacarlo de esta empresa. No se borra su cuenta ni sus otras empresas: solo pierde acceso a esta. No podés quitarte a vos mismo ni al último administrador.',
  'help.a.varias-empresas.c1':
    'Tu rol es por empresa: podés ser administrador en la tuya y miembro en la de tu cliente. Cada empresa ve solo sus propios datos.',

  'help.a.equipo-permisos.title': 'Equipo y permisos del proyecto',
  'help.a.equipo-permisos.summary':
    'Quién puede crear proyectos, quién edita y quién solo mira. Sumá personas al equipo de cada proyecto.',
  'help.a.equipo-permisos.s1.t': 'El equipo del proyecto',
  'help.a.equipo-permisos.s1.b':
    'Cada proyecto tiene su propio equipo: las personas que pueden trabajar en él (crear y editar tareas), sin que eso implique ser responsable de nada. Desde el botón “Equipo” en el tablero, un administrador o cualquier integrante puede agregar o quitar personas de tu empresa.',
  'help.a.equipo-permisos.s2.t': 'Quién puede crear proyectos',
  'help.a.equipo-permisos.s2.b':
    'Solo los administradores de la empresa crean proyectos. Un miembro del equipo no ve el botón de “Nuevo proyecto”. Si sos miembro y todavía no tenés proyectos, vas a ver el aviso de esperar a que un administrador te asigne trabajo.',
  'help.a.equipo-permisos.s3.t': 'Quién edita y quién solo mira',
  'help.a.equipo-permisos.s3.b':
    '• Administrador: todo, en todos los proyectos.\n• Integrante del equipo del proyecto: crea, edita y mueve tareas, asigna y comenta.\n• No integra el equipo, pero el proyecto es público: puede ver las tareas y comentar, pero NO crear ni editar (verás un cartel de “solo lectura”).',
  'help.a.equipo-permisos.s4.t': 'Público y privado',
  'help.a.equipo-permisos.s4.b':
    'Un proyecto PÚBLICO lo ven (y comentan) todos los miembros de la empresa; solo el equipo edita. Un proyecto PRIVADO lo ven únicamente el equipo del proyecto y los administradores: el resto ni siquiera lo encuentra. Cambiar entre público y privado lo hace un administrador o el creador del proyecto.',
  'help.a.equipo-permisos.s5.t': 'Asignar suma al equipo',
  'help.a.equipo-permisos.s5.b':
    'Podés asignar una tarea a cualquier miembro de tu empresa. Al asignársela, esa persona se suma automáticamente al equipo del proyecto para que pueda trabajar. Quitar a alguien del equipo no borra sus tareas: solo pierde el acceso de edición (y, en un privado, deja de verlo).',
  'help.a.equipo-permisos.c1':
    'Pensalo así: el equipo del proyecto es “quién trabaja acá”; público/privado es “quién puede mirar desde afuera”. Simple y sin permisos enredados.',

  // Agenda legal — audiencias, plazos y datos del caso (feature calendar_agenda)
  'help.a.agenda-legal.title': 'Agenda con horarios y recordatorios',
  'help.a.agenda-legal.summary':
    'Cargá eventos con fecha, hora y lugar (audiencias, reuniones, citas, turnos), sumá datos propios del caso o del cliente y velo todo en el calendario. Con recordatorio 24 h antes y sincronización con tu calendario personal.',
  'help.a.agenda-legal.s1.t': 'Dónde se activa',
  'help.a.agenda-legal.s1.b':
    'La agenda viene incluida en el plan Abogados (Legal Solo y Legal Pro), pero se puede activar para empresas de CUALQUIER rubro (reuniones, citas, turnos): escribinos por soporte y la habilitamos para tu cuenta.\n\nLa reconocés porque, al abrir cualquier tarea, aparecen dos secciones nuevas: “Agenda” y “Datos del caso”. Si no las ves, tu empresa todavía no la tiene activada — pedila y listo.',
  'help.a.agenda-legal.s2.t': 'Cargar una audiencia o evento',
  'help.a.agenda-legal.s2.b':
    'Abrí la tarea del expediente y buscá la sección “Agenda”. Completá:\n\n• Fecha del evento y Hora (por ejemplo, la audiencia).\n• Hasta (opcional): la hora de fin.\n• Lugar: juzgado, sala o dirección.\n• Link de sala virtual: si la audiencia es online.\n\nCon la fecha y hora cargadas, la tarea pasa a ser un evento en el calendario. Ojo con la diferencia: el “plazo” (fecha límite) es de todo el día y marca un vencimiento; la “audiencia” tiene hora y lugar. Podés usar los dos en la misma tarea.',
  'help.a.agenda-legal.s3.t': 'Datos del caso (juzgado, expediente, etc.)',
  'help.a.agenda-legal.s3.b':
    'Debajo de “Agenda” está “Datos del caso”: los datos propios del expediente que trae la plantilla del rubro (cliente, contraparte, fuero, juzgado, N° de expediente, abogado responsable…). Completá los que uses.\n\nCada dato tiene una casilla “Mostrar en el calendario”. Marcá los que quieras ver de un vistazo en la agenda (por ejemplo Juzgado y N° de expediente). Esa marca se aplica a todo el proyecto, así no la repetís tarea por tarea.',
  'help.a.agenda-legal.fig1.alt': 'Vista de calendario mostrando audiencias con hora y lugar',
  'help.a.agenda-legal.fig1.cap':
    'En la vista Calendario cada audiencia aparece en su día, con la hora, el lugar y los datos del caso que marcaste.',
  'help.a.agenda-legal.s4.t': 'Ver la agenda en el calendario',
  'help.a.agenda-legal.s4.b':
    'Dentro del proyecto, cambiá a la vista “Calendario”. Cada audiencia aparece en su día con la hora, el lugar y los datos marcados. Podés arrastrar un evento a otro día para reprogramarlo (mantiene la hora).\n\nEn “Mis tareas” tenés tu agenda personal: todas tus audiencias y plazos de todos los proyectos, juntos.',
  'help.a.agenda-legal.s5.t': 'Llevá tu agenda a Google, Apple u Outlook',
  'help.a.agenda-legal.s5.b':
    'En “Mis tareas” tocá “Suscribir agenda”. Copiás la dirección que te da y la pegás en Google Calendar, Apple o Outlook como “suscribirse a un calendario”. Tus audiencias y plazos aparecen ahí y se actualizan solos cada vez que cambian en Tembiapo. Es de sólo lectura: la agenda se administra desde Tembiapo.',
  'help.a.agenda-legal.c1':
    'Tembiapo te avisa 24 horas antes de cada audiencia (notificación en la app, y push si la activaste), para que no se te pase ningún señalamiento.',
  'help.a.agenda-legal.c2':
    'La agenda viene incluida en el plan Abogados (Legal Solo y Legal Pro) y se puede activar para empresas de cualquier rubro. Si te interesa, escribinos y la habilitamos.',

  // Importar notas de reuniones (Plaud) — Fase 1: pegar la nota como tarea
  'help.a.importar-plaud.title': 'Importar notas de reuniones (Plaud)',
  'help.a.importar-plaud.summary':
    'Grabás una reunión con Plaud (o cualquier grabadora que te dé un resumen/transcripción) y la traés a Tembiapo como una tarea, con todo el texto adentro, en el proyecto o caso que quieras.',
  'help.a.importar-plaud.s1.t': 'Para qué sirve',
  'help.a.importar-plaud.s1.b':
    'Plaud es una grabadora con IA que transcribe y resume tus reuniones. Con esta función, esa nota deja de quedar suelta en otra app: la pegás en Tembiapo y queda como una tarea dentro del proyecto o caso, buscable y a mano para todo el equipo.',
  'help.a.importar-plaud.s2.t': 'Cómo importar una nota',
  'help.a.importar-plaud.s2.b':
    'Dentro de un proyecto, tocá el botón “Importar de Plaud”. Se abre un diálogo donde:\n\n• Ponés un título (viene precargado “Reunión — fecha”).\n• Elegís la columna donde cae la tarea.\n• Pegás la nota de Plaud (resumen y/o transcripción, todo junto).\n\nTocás “Crear tarea” y listo: se crea con todo el texto en la descripción.',
  'help.a.importar-plaud.s3.t': 'Qué queda en Tembiapo',
  'help.a.importar-plaud.s3.b':
    'Una tarea normal, con la nota completa en su descripción. Podés asignarla, ponerle fecha, comentar, sumar subtareas o moverla de columna como cualquier otra. El texto queda dentro del caso, no en una app aparte.',
  'help.a.importar-plaud.c1':
    'Por ahora la importación es pegando la nota (no hace falta configurar nada en Plaud). Más adelante sumaremos que la nota llegue sola y que la IA proponga las tareas y audiencias que menciona la reunión.',

  // Legales — comunes
  'legal.disclaimer':
    'Este es un documento modelo, provisto a título informativo. Hacelo revisar por tu asesor legal antes de usarlo en producción.',
  'legal.lastUpdated': 'Última actualización: julio 2026',
  'legal.eyebrow': 'Legal',

  // Política de Privacidad (/privacidad)
  'legal.privacy.title': 'Política de Privacidad',
  'legal.privacy.intro':
    'Tembiapo es una plataforma de gestión de proyectos y tareas de GRUPO EVOLUADO EAS (marca Evoluado), Paraguay. Se compone de una aplicación web (tembiapoapp.com) y una app móvil compañera para Android e iOS. Esta política explica, en lenguaje claro, qué datos personales tratamos, para qué, con quién los compartimos y qué derechos tenés. Responsable del tratamiento: GRUPO EVOLUADO EAS. Contacto: hola@evoluado.com · WhatsApp +595 992 954 499 · https://tembiapoapp.com/soporte.',
  'legal.privacy.s1.title': 'Tembiapo y la app compañera',
  'legal.privacy.s1.body':
    'Tembiapo se usa principalmente desde la web (tembiapoapp.com), donde creás, organizás y administrás tus proyectos. La app móvil es una compañera: te muestra tus tareas asignadas y tus proyectos y tableros en modo lectura, te avisa por notificaciones y te deja hacer acciones rápidas, como cambiar el estado de una tarea, marcar una subtarea o dejar un comentario. La gestión completa (crear proyectos, organizar equipos, administrar la cuenta) se hace en la web.',
  'legal.privacy.s2.title': 'Qué datos recogemos',
  'legal.privacy.s2.body':
    '• Datos de cuenta: email, nombre y apellido, nombre de tu empresa (tenant), rol, idioma y zona horaria. La contraseña se guarda con hash Argon2id (no la podemos leer). Podés subir un avatar opcional.\n• Contenido de trabajo: proyectos, tareas, subtareas, comentarios (con @menciones), archivos adjuntos y plantillas. Todo queda aislado por empresa (multi-tenant).\n• Token de notificaciones push (FCM): un identificador de tu dispositivo, guardado para poder enviarte avisos. Se da de baja cuando cerrás sesión.\n• Datos operativos y de seguridad: tokens de sesión (guardados con hash), límites de intentos de inicio de sesión (por IP y email) y registros de auditoría (quién hizo qué). Si está configurado, usamos Sentry para monitorear errores, depurando los datos personales antes de enviarlos.',
  'legal.privacy.s3.title': 'Tu biometría nunca sale de tu teléfono',
  'legal.privacy.s3.body':
    'Si activás el desbloqueo con huella o Face ID en la app móvil, esos datos biométricos NO se envían a nuestros servidores ni los almacenamos. La biometría se procesa únicamente en tu dispositivo, a través del llavero o el almacenamiento seguro del sistema, y solo sirve para desbloquear localmente una sesión que ya iniciaste. Nosotros nunca recibimos tu huella ni tu rostro.',
  'legal.privacy.s4.title': 'Para qué usamos tus datos',
  'legal.privacy.s4.body':
    'Usamos tus datos para prestarte el servicio: crear y operar tu cuenta, mostrarte tus proyectos y tareas, enviarte las notificaciones que elegiste recibir, mantener la seguridad y prevenir abusos, y darte soporte. No hacemos publicidad, no usamos analítica publicitaria de terceros y no vendemos tus datos. Dentro de una empresa, el contenido de trabajo es visible para los miembros de esa empresa según sus roles.',
  'legal.privacy.s5.title': 'Base legal del tratamiento',
  'legal.privacy.s5.body':
    'Tratamos tus datos sobre tres bases: la ejecución del contrato (para prestarte el servicio que contrataste), nuestro interés legítimo (mantener la seguridad de la plataforma y prevenir el abuso) y tu consentimiento (por ejemplo, para el permiso de notificaciones push, o para iniciar sesión con Google si elegís esa opción).',
  'legal.privacy.s6.title': 'Con quién compartimos tus datos',
  'legal.privacy.s6.body':
    'No vendemos tus datos. Solo los compartimos con proveedores que actúan como encargados de tratamiento, para que el servicio funcione:\n\n• Google Firebase Cloud Messaging (FCM): entrega de notificaciones push; recibe el token de tu dispositivo.\n• Google OAuth / Google Sign-In: si elegís entrar con Google, Google confirma tu identidad y nosotros recibimos tu email y nombre.\n• Hostinger (SMTP): envío de emails transaccionales (invitaciones, avisos de cuenta).\n• Proveedor de hosting/VPS: donde se alojan los datos de la plataforma.\n• Sentry: monitoreo de errores, solo si está habilitado, con datos técnicos y con los datos personales depurados.',
  'legal.privacy.s7.title': 'Transferencias internacionales',
  'legal.privacy.s7.body':
    'Algunos de nuestros encargados (por ejemplo, Google) pueden procesar datos fuera de Paraguay y Brasil, bajo sus propios términos y salvaguardas de protección. Al usar el servicio, entendés que ciertos datos técnicos pueden tratarse en el exterior con ese resguardo.',
  'legal.privacy.s8.title': 'Cuánto tiempo conservamos los datos',
  'legal.privacy.s8.body':
    'Conservamos tus datos mientras tu cuenta y tu empresa estén activas. Cuando eliminás la cuenta, borramos los datos asociados (ver la sección siguiente). Los backups rotan y se sobrescriben con el tiempo, y los registros de seguridad se guardan por un período limitado para poder detectar y responder a incidentes.',
  'legal.privacy.s9.title': 'Tus derechos como titular',
  'legal.privacy.s9.body':
    'Podés ejercer tus derechos de acceso, rectificación, eliminación, portabilidad y oposición sobre tus datos personales. La forma más rápida es editar tu perfil directamente en la app o en la web. También podés escribirnos a hola@evoluado.com y te ayudamos; podemos pedirte que verifiques tu identidad antes de responder.',
  'legal.privacy.s10.title': 'Cómo eliminar tu cuenta y todos tus datos',
  'legal.privacy.s10.body':
    'Desde la web podés eliminar tu cuenta vos mismo: entrá a Preferencias → "Zona de peligro" → Eliminar cuenta, confirmá, y se elimina tu empresa/cuenta junto con todos sus datos asociados. También podés pedir la eliminación escribiendo a hola@evoluado.com. El token de notificaciones se elimina automáticamente cuando cerrás sesión.',
  'legal.privacy.s11.title': 'Seguridad',
  'legal.privacy.s11.body':
    'Protegemos tus datos con cifrado en tránsito (HTTPS/TLS), contraseñas con hash Argon2id, tokens de sesión guardados con hash, credenciales sensibles cifradas (AES-GCM) y aislamiento estricto por empresa, de modo que una empresa nunca vea los datos de otra. Ningún sistema es 100% infalible, pero trabajamos para reducir los riesgos al mínimo.',
  'legal.privacy.s12.title': 'Menores de edad',
  'legal.privacy.s12.body':
    'Tembiapo es una herramienta de trabajo para empresas y equipos (B2B). No está dirigida a menores de edad ni recogemos datos de ellos a sabiendas.',
  'legal.privacy.s13.title': 'Cambios y contacto',
  'legal.privacy.s13.body':
    'Podemos actualizar esta política; cuando lo hagamos, publicaremos la versión vigente en esta página con su fecha y, si el cambio es importante, te avisaremos por los canales del producto. Última actualización: julio de 2026. Ante cualquier duda sobre tus datos, escribinos a hola@evoluado.com o entrá a https://tembiapoapp.com/soporte.',

  // Términos y Condiciones (/terminos)
  'legal.terms.title': 'Términos y Condiciones',
  'legal.terms.intro':
    'Estos términos regulan el uso de Tembiapo, un servicio de gestión de proyectos y tareas de GRUPO EVOLUADO EAS (marca Evoluado), Paraguay, formado por la web tembiapoapp.com y una app móvil compañera para Android e iOS. Al crear una cuenta o usar el servicio, aceptás estos términos.',
  'legal.terms.s1.title': 'El servicio',
  'legal.terms.s1.body':
    'Tembiapo te permite organizar proyectos, tareas y equipos. Se usa principalmente desde la web; la app móvil es una compañera que muestra tus tareas y tableros en modo lectura, te envía avisos y permite acciones rápidas, como cambiar el estado de una tarea, marcar una subtarea o comentar. Podemos mejorar, cambiar o discontinuar funciones para mantener el servicio; si un cambio es importante, procuramos avisarte con anticipación.',
  'legal.terms.s2.title': 'Tu cuenta',
  'legal.terms.s2.body':
    'Sos responsable de la información que cargás y de mantener en secreto tu contraseña. Cada persona debe usar sus propias credenciales. Avisanos de inmediato a hola@evoluado.com si detectás un uso no autorizado de tu cuenta.',
  'legal.terms.s3.title': 'Uso aceptable',
  'legal.terms.s3.body':
    'Te comprometés a usar Tembiapo conforme a la ley y a no cargar contenido ilícito, no vulnerar la seguridad, no interferir con el servicio ni intentar acceder a datos de otras empresas. Podemos suspender las cuentas que incumplan estas reglas.',
  'legal.terms.s4.title': 'Prueba gratis, planes y pagos',
  'legal.terms.s4.body':
    'Ofrecemos una prueba gratuita de 30 días, sin tarjeta. Al vencer, si no activás un plan pago, tu cuenta pasa a solo lectura hasta que registres un pago. Hoy los pagos se hacen de forma manual (transferencia o depósito): registrás el comprobante en la sección de Facturación y nuestro equipo lo valida para activar o renovar tu plan. Los precios pueden actualizarse; te avisaremos antes de que un cambio te afecte.',
  'legal.terms.s5.title': 'Propiedad intelectual',
  'legal.terms.s5.body':
    'La plataforma, su software, su marca (Tembiapo / Tembiapp) y su diseño son propiedad de GRUPO EVOLUADO EAS. No adquirís derechos sobre ellos más allá del uso del servicio según estos términos.',
  'legal.terms.s6.title': 'Tu contenido',
  'legal.terms.s6.body':
    'El contenido de trabajo que cargás (proyectos, tareas, comentarios, archivos) es y sigue siendo tuyo. Para poder prestarte el servicio, nos otorgás una licencia limitada para alojar, procesar y mostrar ese contenido a los miembros de tu empresa según sus roles. No usamos tu contenido para otros fines.',
  'legal.terms.s7.title': 'Garantías y limitación de responsabilidad',
  'legal.terms.s7.body':
    'Nos esforzamos por brindar un servicio disponible y confiable, pero se ofrece "tal cual", sin garantías implícitas. En la medida que permita la ley, no somos responsables por daños indirectos, por lucro cesante ni por pérdida de datos causada por factores fuera de nuestro control razonable. Te recomendamos mantener tus propias copias de la información crítica.',
  'legal.terms.s8.title': 'Suspensión y terminación',
  'legal.terms.s8.body':
    'Podés dejar de usar el servicio y eliminar tu cuenta cuando quieras desde Preferencias → "Zona de peligro", o escribiéndonos. También podemos suspender o cerrar cuentas ante incumplimientos graves de estos términos.',
  'legal.terms.s9.title': 'Ley aplicable',
  'legal.terms.s9.body':
    'Estos términos se rigen por las leyes de la República del Paraguay, en particular la Ley N° 6534/2020 de Protección de Datos Personales. Para los usuarios ubicados en Brasil, aplicamos además prácticas alineadas con la Lei Geral de Proteção de Dados (LGPD). Ante controversias, sin perjuicio de los derechos que la ley reconozca a los consumidores en su lugar de residencia, las partes se someten a los tribunales competentes de Paraguay.',
  'legal.terms.s10.title': 'Cambios',
  'legal.terms.s10.body':
    'Podemos actualizar estos términos. Publicaremos la versión vigente en esta página con su fecha y, si el cambio es importante, te avisaremos por los canales del producto. Última actualización: julio de 2026.',
  'legal.terms.s11.title': 'Contacto',
  'legal.terms.s11.body':
    'Para consultas sobre estos términos, escribinos a hola@evoluado.com, por WhatsApp al +595 992 954 499 o entrá a https://tembiapoapp.com/soporte.',

  // Política de Cookies (/cookies)
  'legal.cookies.title': 'Política de Cookies',
  'legal.cookies.intro':
    'Tembiapo usa la mínima cantidad de cookies y almacenamiento local necesarios para funcionar. No usamos cookies de publicidad ni de seguimiento de terceros por defecto. Responsable: GRUPO EVOLUADO EAS (Evoluado), hola@evoluado.com.',
  'legal.cookies.s1.title': '¿Qué es una cookie?',
  'legal.cookies.s1.body':
    'Una cookie es un pequeño archivo que un sitio guarda en tu navegador. También usamos "almacenamiento local" (localStorage), una tecnología similar, para recordar preferencias. En conjunto nos permiten mantener tu sesión y tu configuración.',
  'legal.cookies.s2.title': 'Cookies esenciales',
  'legal.cookies.s2.body':
    'Son imprescindibles para que la app funcione y no se pueden desactivar. La principal es la cookie de sesión de refresco (tb_refresh), que mantiene tu sesión iniciada de forma segura bajo la ruta /tembiapo. Sin ella no podrías permanecer logueado.',
  'legal.cookies.s3.title': 'Preferencias',
  'legal.cookies.s3.body':
    'Guardamos algunas preferencias para mejorar tu experiencia, como el tema claro/oscuro y el idioma elegido. Esta información se guarda en tu navegador (localStorage) y no se usa para rastrearte.',
  'legal.cookies.s4.title': 'Lo que NO usamos',
  'legal.cookies.s4.body':
    'Por defecto no cargamos cookies de publicidad, ni píxeles de redes sociales, ni rastreadores de terceros para perfilar tu navegación. Si en el futuro incorporáramos herramientas de analítica, lo informaríamos y pediríamos tu consentimiento cuando corresponda.',
  'legal.cookies.s5.title': 'Cómo gestionarlas',
  'legal.cookies.s5.body':
    'Podés borrar o bloquear cookies desde la configuración de tu navegador. Tené en cuenta que si bloqueás las cookies esenciales, es probable que no puedas iniciar sesión ni usar Tembiapo con normalidad. El tema y el idioma se restablecerán si borrás el almacenamiento local.',

  // Admin · Configuración de plataforma
  'adminSettings.title': 'Configuración',
  'adminSettings.subtitle':
    'Credenciales de la plataforma. Lo que cargues acá se guarda en la base y sobrescribe las variables de entorno.',
  'adminSettings.loadError': 'No pudimos cargar la configuración.',
  'adminSettings.save': 'Guardar',
  'adminSettings.saved': 'Cambios guardados.',
  'adminSettings.saveError': 'No pudimos guardar la configuración.',
  'adminSettings.testEmail.title': 'Probar el envío',
  'adminSettings.testEmail.label': 'Enviar un email de prueba a',
  'adminSettings.testEmail.send': 'Enviar prueba',
  'adminSettings.testEmail.sent': '✓ Email enviado por SMTP. Revisá la bandeja.',
  'adminSettings.testEmail.logMode': 'Sin SMTP configurado: el email quedó en el log (no se envió).',
  'adminSettings.testEmail.failed': 'No se pudo enviar',
  'adminSettings.secretPlaceholder': '•••• (dejá vacío para no cambiar)',
  'adminSettings.clear': 'Borrar',
  'adminSettings.undo': 'Deshacer',
  'adminSettings.willDelete': 'Se borrará al guardar (vuelve a env)',
  'adminSettings.status.set': 'Configurado',
  'adminSettings.status.unset': 'Sin definir',
  'adminSettings.source.db': 'DB',
  'adminSettings.source.env': 'env',
  'adminSettings.source.unset': 'Sin definir',
  'adminSettings.note.email':
    'SMTP/DSN para enviar correos. Sin DSN, las invitaciones se registran en el log.',
  'adminSettings.note.push': 'Claves VAPID para las notificaciones push (Web Push).',
  'adminSettings.note.payments': 'Credenciales de la pasarela de pago. Para la Fase 4.',
  'adminSettings.note.errors': 'DSN de Sentry para el reporte de errores.',
  'adminSettings.note.auth':
    'Ingreso con Google (OAuth) y protección anti-bot reCAPTCHA v3. Todo opcional: si dejás los campos vacíos, el botón de Google no aparece y el captcha no se exige.',

  // Login — panel de marca
  'login.brand.title': 'Todo tu trabajo, en un solo lugar.',
  'login.brand.tagline': 'Gestión de proyectos y tareas para tu equipo, sin complicaciones.',
  'login.brand.backHome': 'Volver al inicio',

  // Centro de anuncios (público, en el login)
  'announcements.title': 'Novedades',
  'announcements.empty': 'Sin novedades por ahora.',

  // WhatsApp (soporte)
  'whatsapp.support': 'Soporte por WhatsApp',
  'whatsapp.contact': 'Contactar por WhatsApp',

  // Panel de anuncios (Super Admin)
  'adminAnnouncements.title': 'Anuncios',
  'adminAnnouncements.subtitle': 'Avisos que ven todos en la pantalla de acceso.',
  'adminAnnouncements.new': 'Nuevo anuncio',
  'adminAnnouncements.newTitle': 'Nuevo anuncio',
  'adminAnnouncements.editTitle': 'Editar anuncio',
  'adminAnnouncements.empty': 'Todavía no hay anuncios.',
  'adminAnnouncements.loadError': 'No pudimos cargar los anuncios.',
  'adminAnnouncements.saveError': 'No pudimos guardar el anuncio.',
  'adminAnnouncements.deleteError': 'No pudimos eliminar el anuncio.',
  'adminAnnouncements.confirmDelete': '¿Eliminar el anuncio "{title}"? Esta acción no se puede deshacer.',
  'adminAnnouncements.loginNote': 'Los anuncios activos se muestran en la pantalla de login (públicos, sin sesión).',
  'adminAnnouncements.col.title': 'Anuncio',
  'adminAnnouncements.col.created': 'Publicado',
  'adminAnnouncements.col.active': 'Activo',
  'adminAnnouncements.col.actions': 'Acciones',
  'adminAnnouncements.status.active': 'Visible',
  'adminAnnouncements.status.inactive': 'Oculto',
  'adminAnnouncements.field.title': 'Título',
  'adminAnnouncements.field.body': 'Mensaje',
  'adminAnnouncements.field.ptSection': 'Português (Brasil) — opcional',
  'adminAnnouncements.field.titlePt': 'Título (PT)',
  'adminAnnouncements.field.bodyPt': 'Mensaje (PT)',
  'adminAnnouncements.field.active': 'Activo',
  'adminAnnouncements.field.activeHint': 'Si está activo, se muestra en la pantalla de login.',

  // Gestionar empresa (Super Admin) — negociación caso por caso
  'manageCompany.action': 'Gestionar',
  'manageCompany.custom': 'Personalizado',
  'manageCompany.title': 'Gestionar empresa',
  'manageCompany.loadError': 'No pudimos cargar la empresa.',
  'manageCompany.summary.title': 'Resumen',
  'manageCompany.summary.plan': 'Plan base',
  'manageCompany.summary.status': 'Estado',
  'manageCompany.summary.projects': 'Proyectos',
  'manageCompany.summary.users': 'Usuarios',
  'manageCompany.summary.usage': '{used} / {limit}',
  'manageCompany.summary.price': 'Precio efectivo',
  'manageCompany.summary.perMonth': '/mes',
  'manageCompany.summary.free': 'Gratis / demo',
  'manageCompany.summary.addons': 'Add-ons',
  'manageCompany.summary.created': 'Alta',
  'manageCompany.overrides.title': 'Plan personalizado',
  'manageCompany.overrides.hint':
    'Dejá un campo vacío para usar el valor del plan base. Los cambios aplican solo a esta empresa.',
  'manageCompany.features.title': 'Funciones adicionales',
  'manageCompany.features.hint':
    'Activá funciones extra para esta empresa, sin importar su plan ni su rubro (ej. la Agenda con horarios para reuniones, citas o audiencias).',
  'manageCompany.field.projects': 'Proyectos (límite)',
  'manageCompany.field.users': 'Usuarios (límite)',
  'manageCompany.field.price': 'Precio mensual',
  'manageCompany.field.priceHint': '0 = gratis / modo demo. Vacío = usa el precio del plan.',
  'manageCompany.field.planPlaceholder': 'Plan: {value}',
  'manageCompany.field.plan': 'Plan',
  'manageCompany.field.planHint':
    'Cambia el plan de la suscripción (sin prorrateo; el precio se ajusta con el override de arriba).',
  'manageCompany.field.status': 'Estado',
  'manageCompany.field.trialEnds': 'Fin de trial / demo',
  'manageCompany.field.trialEndsHint':
    'Puede superar los 30 días para extender la demo. Vacío = sin fecha de fin.',
  'manageCompany.status.TRIAL': 'Trial',
  'manageCompany.status.ACTIVE': 'Activa',
  'manageCompany.status.PAST_DUE': 'Pago pendiente',
  'manageCompany.status.SUSPENDED': 'Suspendida',
  'manageCompany.status.CANCELED': 'Cancelada',
  'manageCompany.save': 'Guardar cambios',
  'manageCompany.saved': 'Cambios guardados.',
  'manageCompany.saveError': 'No pudimos guardar los cambios.',
  'manageCompany.noChanges': 'No hay cambios para guardar.',
  'manageCompany.restore': 'Restaurar al plan',
  'manageCompany.restoreConfirm':
    '¿Restaurar los límites y el precio al plan base? Se quitarán los valores personalizados.',
  'manageCompany.restoreError': 'No pudimos restaurar el plan.',

  // Historial de cambios (auditoría) del modal "Gestionar empresa"
  'audit.title': 'Historial de cambios',
  'audit.empty': 'Sin cambios registrados.',
  'audit.loadError': 'No pudimos cargar el historial.',
  'audit.superAdmin': 'Super admin',
  'audit.field.projects': 'Proyectos',
  'audit.field.users': 'Usuarios',
  'audit.field.price': 'Precio mensual',
  'audit.field.trialEnds': 'Fin de trial',
  'audit.field.status': 'Estado',
  'audit.field.plan': 'Plan',
  'audit.value.plan': 'según plan',
  'audit.value.free': 'gratis',
  'audit.value.none': 'sin definir',

  // Acciones de soporte / peligro del modal "Gestionar empresa"
  'companyAdmin.title': 'Acciones',
  'companyAdmin.deactivate.suspendTitle': 'Desactivar empresa',
  'companyAdmin.deactivate.suspendHint':
    'Suspende el acceso: la empresa queda en solo lectura hasta reactivarla.',
  'companyAdmin.deactivate.suspendAction': 'Desactivar',
  'companyAdmin.deactivate.suspendConfirm':
    '¿Desactivar a {company}? Su equipo perderá el acceso de escritura hasta que la reactives.',
  'companyAdmin.deactivate.reactivateTitle': 'Reactivar empresa',
  'companyAdmin.deactivate.reactivateHint':
    'La empresa está suspendida. Reactivala para devolverle el acceso.',
  'companyAdmin.deactivate.reactivateAction': 'Reactivar',
  'companyAdmin.deactivate.reactivateConfirm': '¿Reactivar a {company} y devolverle el acceso?',
  'companyAdmin.deactivate.error': 'No pudimos cambiar el estado de la empresa.',
  'companyAdmin.impersonate.title': 'Ver como esta empresa',
  'companyAdmin.impersonate.hint':
    'Entrá a la app con la vista de esta empresa para dar soporte. No modifica su sesión.',
  'companyAdmin.impersonate.action': 'Ver como esta empresa (soporte)',
  'companyAdmin.impersonate.error': 'No pudimos iniciar la vista de soporte. Probá de nuevo.',
  'companyAdmin.impersonate.banner': 'Estás viendo como {company} (soporte)',
  'companyAdmin.impersonate.back': 'Volver al panel admin',
  'companyAdmin.delete.zone': 'Zona de peligro',
  'companyAdmin.delete.hint':
    'Eliminar la empresa borra de forma permanente sus proyectos, tareas y usuarios. No se puede deshacer.',
  'companyAdmin.delete.open': 'Eliminar empresa',
  'companyAdmin.delete.confirmTitle': 'Eliminar empresa',
  'companyAdmin.delete.warning':
    'Esto borrará definitivamente a {company} con TODOS sus proyectos, tareas, usuarios y datos. Esta acción es irreversible.',
  'companyAdmin.delete.typeToConfirm': 'Escribí "{company}" para confirmar',
  'companyAdmin.delete.confirmAction': 'Eliminar definitivamente',
  'companyAdmin.delete.error': 'No pudimos eliminar la empresa. Probá de nuevo.',

  // Tour de onboarding (primera vez en /inicio)
  'tour.skip': 'Saltar',
  'tour.back': 'Anterior',
  'tour.next': 'Siguiente',
  'tour.cta': 'Crear mi primer proyecto',
  'tour.close': 'Cerrar tour',
  'tour.replay': 'Ver el tour de nuevo',
  'tour.welcome.title': 'Bienvenido a Tembiapo',
  'tour.welcome.body': 'Te mostramos lo básico en 30 segundos. Podés saltarlo cuando quieras.',
  'tour.projects.title': 'Tus proyectos',
  'tour.projects.body': 'Creá tu primer proyecto desde una plantilla de tu rubro o empezá en blanco.',
  'tour.views.title': 'Vistas del tablero',
  'tour.views.body':
    'Mirá tu trabajo como Kanban, Lista, Calendario o Timeline. Elegí la vista que mejor te sirva.',
  'tour.team.title': 'Sumá a tu equipo',
  'tour.team.body': 'Invitá a tu equipo por email desde este menú y trabajen juntos.',
  'tour.notifications.title': 'Notificaciones',
  'tour.notifications.body':
    'Enterate de menciones, asignaciones y vencimientos desde la campanita.',
  'tour.help.title': 'Ayuda y soporte',
  'tour.help.body': 'Si te trabás, tenés el centro de ayuda y WhatsApp para escribirnos.',

  // Selector de moneda de visualización (₲ / R$ / US$)
  'currency.label': 'Moneda',
  'currency.name.PYG': 'Guaraníes (₲)',
  'currency.name.BRL': 'Reales (R$)',
  'currency.name.USD': 'Dólares (US$)',
  'currency.billedInPyg': 'El cobro es en guaraníes (₲); tu moneda es solo referencia',

  // Panel Super Admin — Tasas de cambio
  'adminRates.nav': 'Tasas de cambio',
  'adminRates.title': 'Tasas de cambio',
  'adminRates.subtitle':
    'Los precios se cobran en ₲. Estas tasas solo definen cómo se muestran en R$ y US$.',
  'adminRates.loadError': 'No pudimos cargar las tasas de cambio.',
  'adminRates.current.title': 'Tasas actuales',
  'adminRates.current.hint': 'Cuántos guaraníes vale 1 unidad de cada moneda.',
  'adminRates.updatedAt': 'Última actualización: {date}',
  'adminRates.updatedNever': 'Sin actualizar todavía',
  'adminRates.perUsd': '₲ por 1 US$',
  'adminRates.perBrl': '₲ por 1 R$',
  'adminRates.refresh.title': 'Actualización automática',
  'adminRates.refresh.hint': 'Traé las tasas más recientes desde la fuente configurada.',
  'adminRates.refresh.button': 'Actualizar automáticamente',
  'adminRates.refresh.success': 'Tasas actualizadas desde la fuente.',
  'adminRates.refresh.unavailable':
    'La fuente de tasas no está disponible ahora. Probá de nuevo en un rato o cargalas manualmente.',
  'adminRates.refresh.error': 'No pudimos actualizar las tasas. Probá de nuevo.',
  'adminRates.manual.title': 'Carga manual',
  'adminRates.manual.hint': 'Ingresá cuántos guaraníes vale 1 unidad de cada moneda.',
  'adminRates.manual.usd': '₲ por 1 US$',
  'adminRates.manual.brl': '₲ por 1 R$',
  'adminRates.manual.save': 'Guardar tasas',
  'adminRates.manual.success': 'Tasas guardadas.',
  'adminRates.manual.error': 'No pudimos guardar las tasas. Revisá los valores.',
  'adminRates.manual.invalid': 'Ingresá un valor mayor a cero.',

  // Panel Super Admin — Seguridad (cambio de contraseña con PIN)
  'adminSecurity.nav': 'Seguridad',
  'adminSecurity.title': 'Seguridad',
  'adminSecurity.subtitle':
    'Cambiá tu contraseña de super admin con verificación por PIN al WhatsApp de control.',
  'adminSecurity.changed': 'Contraseña cambiada correctamente.',
  'adminSecurity.card.title': 'Cambiar contraseña',
  'adminSecurity.card.intro':
    'Ingresá tu contraseña actual y la nueva. Te enviaremos un PIN de 6 dígitos al WhatsApp de control para confirmar.',
  'adminSecurity.field.current': 'Contraseña actual',
  'adminSecurity.field.new': 'Nueva contraseña',
  'adminSecurity.field.newHint': 'Mínimo 8 caracteres.',
  'adminSecurity.field.repeat': 'Repetir nueva contraseña',
  'adminSecurity.field.pin': 'PIN de verificación',
  'adminSecurity.pin.sentTo': 'Te enviamos un PIN al WhatsApp de control {phone}.',
  'adminSecurity.action.sendPin': 'Enviar PIN',
  'adminSecurity.action.confirm': 'Confirmar cambio',
  'adminSecurity.action.back': 'Volver',
  'adminSecurity.action.resend': 'Reenviar PIN',
  'adminSecurity.action.resendIn': 'Reenviar en {s}s',
  'adminSecurity.validation.tooShort': 'La contraseña debe tener al menos 8 caracteres.',
  'adminSecurity.validation.mismatch': 'Las contraseñas no coinciden.',
  'adminSecurity.validation.pinFormat': 'El PIN debe tener 6 dígitos.',
  'adminSecurity.error.invalidCurrent': 'La contraseña actual es incorrecta.',
  'adminSecurity.error.passwordSame': 'La nueva contraseña debe ser distinta a la actual.',
  'adminSecurity.error.pinExpired': 'El PIN expiró o no existe. Pedí uno nuevo.',
  'adminSecurity.error.invalidPin': 'El PIN es incorrecto.',
  'adminSecurity.error.invalidPinLeft': 'El PIN es incorrecto. Te quedan {left} intentos.',
  'adminSecurity.error.pinTooMany': 'Demasiados intentos. Pedí un PIN nuevo.',
  'adminSecurity.error.resendTooSoon': 'Esperá un momento antes de pedir otro PIN.',
  'adminSecurity.error.generic': 'No pudimos completar la operación. Probá de nuevo.',

  // --- SEO por página (títulos/descripciones de las pantallas públicas) ---
  'seo.login.title': 'Iniciar sesión',
  'seo.login.desc': 'Ingresá a Tembiapo para gestionar tus proyectos, tareas y equipo.',
  'seo.register.title': 'Creá tu empresa gratis',
  'seo.register.desc': 'Probá Tembiapo 30 días gratis. Elegí tu rubro, tu plan y empezá en minutos.',
  'seo.about.title': 'Sobre nosotros',
  'seo.about.desc': 'Conocé Tembiapo: gestión de proyectos y tareas sin complicaciones para equipos de Paraguay y Brasil.',
  'seo.contact.title': 'Contacto',
  'seo.contact.desc': 'Escribinos por WhatsApp o email. Estamos para ayudarte con Tembiapo.',
  'seo.support.title': 'Soporte',
  'seo.support.desc': 'Centro de ayuda, guías y canales de soporte de Tembiapo.',
  'seo.help.title': 'Centro de ayuda',
  'seo.help.desc': 'Guías paso a paso para sacarle el máximo provecho a Tembiapo.',

  // --- Panel SEO + SEM (Super Admin) ---
  'adminSeo.title': 'SEO y campañas',
  'adminSeo.subtitle': 'Metadatos del sitio, verificación de buscadores y etiquetas de analytics/campañas.',
  'adminSeo.loadError': 'No pudimos cargar la configuración de SEO.',
  'adminSeo.saveError': 'No pudimos guardar los cambios. Probá de nuevo.',
  'adminSeo.saved': 'Cambios guardados.',
  'adminSeo.save': 'Guardar',
  'adminSeo.section.meta': 'Metadatos del sitio',
  'adminSeo.section.metaNote': 'Título, descripción e imagen para compartir. Dejá un campo vacío para volver al valor por defecto.',
  'adminSeo.section.verify': 'Verificación de buscadores',
  'adminSeo.section.verifyNote': 'Lo ideal es verificar por Google Analytics o Tag Manager: el robot de verificación no ejecuta JavaScript, así que estas metas son sólo un respaldo.',
  'adminSeo.section.sem': 'Analytics y campañas (SEM)',
  'adminSeo.section.semNote': 'Al cargar un ID se inyecta automáticamente su script en el sitio. Dejá vacío para desactivarlo.',
  'adminSeo.lang.es': 'Español',
  'adminSeo.lang.pt': 'Português (BR)',
  'adminSeo.field.siteName': 'Nombre del sitio',
  'adminSeo.hint.siteName': 'Se usa en el título y en las tarjetas para compartir (og:site_name).',
  'adminSeo.field.canonical': 'URL canónica base',
  'adminSeo.hint.canonical': 'Dominio público de producción, ej. https://tembiapoapp.com. Base para las URLs canónicas.',
  'adminSeo.field.ogImage': 'Imagen para compartir (Open Graph)',
  'adminSeo.hint.ogImage': 'URL absoluta de la imagen que se muestra al compartir en redes (1200×630 px recomendado).',
  'adminSeo.field.twitter': 'Usuario de X/Twitter',
  'adminSeo.hint.twitter': 'Con arroba, ej. @tuempresa. Se usa en las tarjetas de Twitter.',
  'adminSeo.field.robots': 'Permitir indexación',
  'adminSeo.hint.robots': 'Si lo desactivás, se le pide a los buscadores no indexar el sitio (noindex).',
  'adminSeo.field.titleEs': 'Título (ES)',
  'adminSeo.field.descEs': 'Descripción (ES)',
  'adminSeo.field.keywordsEs': 'Palabras clave (ES)',
  'adminSeo.field.titlePt': 'Título (PT)',
  'adminSeo.field.descPt': 'Descripción (PT)',
  'adminSeo.field.keywordsPt': 'Palabras clave (PT)',
  'adminSeo.hint.title': 'Título por defecto de la home. Hasta ~60 caracteres.',
  'adminSeo.hint.desc': 'Descripción por defecto para buscadores y redes. Hasta ~160 caracteres.',
  'adminSeo.hint.keywords': 'Opcional, separadas por comas. Poco relevante para Google hoy.',
  'adminSeo.field.verifyGoogle': 'Google Search Console',
  'adminSeo.hint.verifyGoogle': 'Contenido de la meta google-site-verification (sin las comillas).',
  'adminSeo.field.verifyBing': 'Bing Webmaster',
  'adminSeo.hint.verifyBing': 'Contenido de la meta msvalidate.01 de Bing.',
  'adminSeo.field.ga4': 'Google Analytics 4',
  'adminSeo.hint.ga4': 'ID de medición, ej. G-XXXXXXX. Se obtiene en Administrar → Flujos de datos.',
  'adminSeo.field.gtm': 'Google Tag Manager',
  'adminSeo.hint.gtm': 'ID del contenedor, ej. GTM-XXXXXX.',
  'adminSeo.field.pixel': 'Meta Pixel (Facebook)',
  'adminSeo.hint.pixel': 'ID del pixel, se obtiene en el Administrador de eventos de Meta.',
  'adminSeo.field.adsId': 'Google Ads',
  'adminSeo.hint.adsId': 'ID de conversión, ej. AW-XXXXXXXXX.',
  'adminSeo.field.adsLabel': 'Etiqueta de conversión de Google Ads',
  'adminSeo.hint.adsLabel': 'Etiqueta que acompaña al ID de Google Ads para medir conversiones.',

  // Descuentos — badge/precio (landing + registro)
  'discount.off': '-{pct}%',
  'discount.endsAt': 'termina el {date}',
  'discount.wasPrice': 'Precio sin descuento',

  // Descuentos — panel Super Admin (dentro de Planes)
  'adminDiscount.title': 'Descuentos',
  'adminDiscount.subtitle':
    'Descuento anual global y promo de lanzamiento. No se acumulan: gana el mayor.',
  'adminDiscount.annualPercent': 'Descuento anual (%)',
  'adminDiscount.annualHint':
    'El precio anual se calcula: precio mensual × 12 con este descuento aplicado.',
  'adminDiscount.promoEnabled': 'Promo de lanzamiento activa',
  'adminDiscount.promoEnabledHint':
    'Aplica al precio mensual y anual (gana el mayor entre promo y descuento anual).',
  'adminDiscount.promoPercent': 'Descuento de promo (%)',
  'adminDiscount.promoEndsAt': 'Fecha de fin',
  'adminDiscount.promoEndsAtHint': 'Opcional. Vacío = sin vencimiento.',
  'adminDiscount.promoLabel': 'Etiqueta',
  'adminDiscount.promoLabelHint': 'Texto corto, ej. "Lanzamiento".',
  'adminDiscount.saved': 'Descuentos guardados.',
  'adminDiscount.saveError': 'No pudimos guardar los descuentos.',
  'adminDiscount.loadError': 'No pudimos cargar los descuentos.',

  // Zona de peligro: borrado self-service de la propia empresa (sólo COMPANY_ADMIN)
  'account.delete.zone': 'Zona de peligro',
  'account.delete.warning':
    'Eliminar la cuenta borra de forma permanente tu empresa y TODOS sus datos: proyectos, tareas, archivos y miembros. Esta acción es irreversible.',
  'account.delete.open': 'Eliminar cuenta',
  'account.delete.confirmTitle': 'Eliminar cuenta',
  'account.delete.confirmWarning':
    'Esto borrará definitivamente a {company} con TODOS sus proyectos, tareas, archivos y miembros. No se puede deshacer.',
  'account.delete.passwordLabel': 'Tu contraseña',
  'account.delete.passwordPlaceholder': 'Ingresá tu contraseña',
  'account.delete.typeToConfirm': 'Escribí "{company}" para confirmar',
  'account.delete.confirmAction': 'Eliminar definitivamente',
  'account.delete.invalidPassword': 'La contraseña es incorrecta.',
  'account.delete.error': 'No pudimos eliminar la cuenta. Probá de nuevo.',

  // Membresía multi-empresa: selección al ingresar + switcher
  'company.select.title': '¿En qué empresa vas a trabajar?',
  'company.select.subtitle': 'Pertenecés a varias empresas. Elegí con cuál seguir.',
  'company.select.back': 'Usar otra cuenta',
  'company.select.expired': 'La selección expiró. Ingresá de nuevo.',
  'company.switch.title': 'Cambiar de empresa',
  'company.switch.workingIn': 'Trabajando en',
  'company.switch.done': 'Cambiaste a {name}.',

  // Carpetas (agrupar proyectos)
  'folders.new': 'Nueva carpeta',
  'folders.create': 'Crear carpeta',
  'folders.rename': 'Renombrar carpeta',
  'folders.delete': 'Eliminar carpeta',
  'folders.delete.confirm':
    'Se eliminará la carpeta “{name}”. Los proyectos NO se borran: quedan en “Sin carpeta”.',
  'folders.deleteError': 'No pudimos eliminar la carpeta. Probá de nuevo.',
  'folders.name': 'Nombre de la carpeta',
  'folders.namePlaceholder': 'Ej: Clientes activos',
  'folders.name.required': 'El nombre de la carpeta es obligatorio.',
  'folders.saveError': 'No pudimos guardar la carpeta. Probá de nuevo.',
  'folders.color': 'Color',
  'folders.color.none': 'Sin color',
  'folders.uncategorized': 'Sin carpeta',
  'folders.moveTo': 'Mover a carpeta',
  'folders.actions': 'Acciones de la carpeta',
  'folders.projectCount': '{n} proyectos',
  'folders.collapse': 'Contraer',
  'folders.expand': 'Expandir',
  'folders.field.label': 'Carpeta',
  'folders.sectionTitle': 'Carpetas',
  'folders.open': 'Abrir carpeta',
  'folders.changeColor': 'Cambiar color',
  'folders.reorder': 'Reordenar carpeta',
  'folders.notFound': 'No encontramos esta carpeta.',
  'folderView.empty.title': 'Esta carpeta está vacía',
  'folderView.empty.body':
    'Todavía no hay proyectos en esta carpeta. Creá uno nuevo o mové proyectos existentes acá desde su menú.',
  'convert.action': 'Convertir a proyecto',
  'convert.nameLabel': 'Nombre del proyecto',
  'convert.deleteOriginal': 'Eliminar la tarea original',
  'convert.hint':
    'La tarea se mueve al nuevo proyecto con sus comentarios, adjuntos, descripción y responsable; sus subtareas se vuelven tareas. Deja de aparecer en este tablero.',
  'convert.submit': 'Convertir',
  'convert.error': 'No se pudo convertir la tarea. Probá de nuevo.',
  'view.asCards': 'Ver como tarjetas',
  'view.asList': 'Ver como lista',

  // Feedback de lanzamiento (banner + modal en el shell autenticado)
  'feedback.banner.title': 'Estamos en lanzamiento — contanos qué mejorar',
  'feedback.banner.cta': 'Enviar sugerencia',
  'feedback.modal.title': '¿Qué querés contarnos?',
  'feedback.type.suggestion': 'Sugerencia',
  'feedback.type.bug': 'Reportar un bug',
  'feedback.type.vertical': 'Nueva vertical/rubro',
  'feedback.type.template': 'Nueva plantilla',
  'feedback.type.other': 'Otro',
  'feedback.message.label': 'Mensaje',
  'feedback.message.placeholder': 'Contanos con detalle qué mejorarías o qué encontraste…',
  'feedback.submit': 'Enviar',
  'feedback.disclaimer':
    'Las sugerencias se analizan en equipo; no garantizamos su desarrollo. Los bugs sí reciben respuesta.',
  'feedback.done.title': '¡Recibido!',
  'feedback.done.bug':
    'Recibimos tu reporte. Vamos a revisarlo y responderte si quedó solucionado.',
  'feedback.done.idea':
    '¡Gracias! Tu idea va a ser analizada por el equipo. Puede que la desarrollemos o no, pero todas las propuestas nos ayudan a mejorar.',
  'feedback.error.tooMany':
    'Enviaste feedback hace poco. Esperá un momento antes de mandar otro.',

  // Feedback — panel Super Admin
  'admin.feedback.title': 'Feedback',
  'admin.feedback.subtitle': 'Sugerencias, bugs y pedidos de las empresas.',
  'admin.feedback.filter.new': 'Nuevos',
  'admin.feedback.filter.reviewed': 'Revisados',
  'admin.feedback.filter.all': 'Todos',
  'admin.feedback.filter.allTypes': 'Todos los tipos',
  'admin.feedback.col.type': 'Tipo',
  'admin.feedback.col.message': 'Mensaje',
  'admin.feedback.col.company': 'Empresa / Autor',
  'admin.feedback.col.source': 'Origen',
  'admin.feedback.col.date': 'Fecha',
  'admin.feedback.col.status': 'Estado',
  'admin.feedback.col.actions': 'Acciones',
  'admin.feedback.status.new': 'Nuevo',
  'admin.feedback.status.reviewed': 'Revisado',
  'admin.feedback.markReviewed': 'Marcar revisado',
  'admin.feedback.markNew': 'Marcar nuevo',
  'admin.feedback.empty': 'No hay feedback para mostrar.',
  'admin.feedback.more': 'Ver más',
  'admin.feedback.less': 'Ver menos',
} as const;

export type MessageKey = keyof typeof es;

// --- Português (Brasil) — parcial; lo que falte cae a `es` -------------------
const pt: Partial<Record<MessageKey, string>> = {
  'common.appName': 'Tembiapo',
  'common.tagline': 'Gestão de projetos e tarefas, sem complicações.',
  'common.support': 'Suporte',
  'common.poweredBy': 'desenvolvido por Evoluado.com',
  'common.email': 'E-mail',
  'common.password': 'Senha',
  'login.title': 'Entre no seu tembiapo',
  'login.submit': 'Entrar',
  'register.freeBadge': '30 dias grátis · sem cartão',
  'register.quickSubtitle': 'Crie sua conta em segundos. Você escolhe o segmento e o plano depois.',
  'register.quickReassure': '30 dias grátis · sem cartão · cancele quando quiser',
  'register.terms.pre': 'Aceito os',
  'register.terms.tos': 'Termos e Condições',
  'register.terms.and': 'e a',
  'register.terms.privacy': 'Política de Privacidade',
  'register.terms.post': ', e dou meu consentimento para o tratamento dos meus dados.',
  'register.terms.error': 'Você precisa aceitar os Termos e a Política de Privacidade para continuar.',
  'onboarding.vertical.title': 'Qual é a área da sua empresa?',
  'onboarding.vertical.subtitle': 'Escolha seu segmento e preparamos os quadros e modelos para você.',
  'onboarding.vertical.skip': 'Escolher depois',
  'home.userMenu.logout': 'Sair',
  'nav.projects': 'Projetos',
  'nav.reports': 'Relatórios',
  'nav.myTasks': 'Minhas tarefas',
  'nav.agenda': 'Agenda',

  // Calendário combinado (feature calendar_agenda)
  'agendaPage.title': 'Agenda',
  'agendaPage.subtitle': 'Todos os eventos com horário: os meus e os da equipe.',
  'agendaPage.mine': 'Meus',
  'agendaPage.team': 'Da equipe',
  'agendaPage.noFeature': 'A agenda com horários não está ativada na sua conta.',
  'agendaPage.noEventsDay': 'Sem eventos neste dia.',

  // Buscador global
  'search.open': 'Buscar',
  'search.title': 'Buscar',
  'search.placeholder': 'Buscar projetos e tarefas…',
  // Criação rápida de tarefa (FAB móvel)
  'quickTask.button': 'Nova tarefa',
  'quickTask.title': 'Nova tarefa rápida',
  'quickTask.project': 'Projeto',
  'quickTask.column': 'Coluna',
  'quickTask.titleLabel': 'Título',
  'quickTask.assignMe': 'Atribuir a mim',
  'quickTask.create': 'Criar tarefa',
  'quickTask.titleRequired': 'Escreva um título.',
  'quickTask.columnRequired': 'Escolha uma coluna.',
  'quickTask.error': 'Não foi possível criar a tarefa. Tente novamente.',
  'search.hint': 'Digite para buscar projetos e tarefas.',
  'search.empty': 'Sem resultados.',
  'search.projects': 'Projetos',
  'search.tasks': 'Tarefas',
  'search.close': 'Fechar',

  // Agenda / evento (feature calendar_agenda)
  'agenda.title': 'Agenda',
  'agenda.date': 'Data do evento',
  'agenda.time': 'Hora',
  'agenda.until': 'Até (opcional)',
  'agenda.location': 'Local',
  'agenda.locationPh': 'Vara, sala, endereço…',
  'agenda.meetingUrl': 'Link da sala virtual',
  'agenda.subscribe': 'Assinar agenda',
  'agenda.subscribeTitle': 'Assine sua agenda',
  'agenda.subscribeHint':
    'Cole este endereço no Google Calendar, Apple ou Outlook para ver suas audiências e eventos no seu calendário, sempre atualizados.',
  'agenda.subscribeSteps': 'No Google Calendar: “Outras agendas” → “Assinar agenda” → cole a URL.',
  'agenda.copy': 'Copiar',
  'agenda.copied': 'Copiado',
  // Conflitos/proximidade de horário
  'agenda.conflictTitle': 'Horário comprometido',
  'agenda.nearTitle': 'Muito perto de outro evento',
  'agenda.you': 'você',
  'agenda.someone': 'alguém da equipe',
  'agenda.nearGap': '{n} min de diferença',

  // Dados do caso (campos personalizados no calendário, feature calendar_agenda)
  'caseData.title': 'Dados do caso',
  'caseData.hint':
    'Preencha os dados deste projeto (vara, processo, etc.). Marque os que quiser ver no calendário.',
  'caseData.showInCalendar': 'Mostrar no calendário',

  // Importar nota do Plaud (gravador IA) — Fase 1: colar a nota como tarefa
  'plaud.button': 'Importar do Plaud',
  'plaud.title': 'Importar nota do Plaud',
  'plaud.hint':
    'Cole a nota da sua gravação do Plaud (resumo e/ou transcrição). Cria-se uma tarefa com todo o texto na descrição, para ficar no caso.',
  'plaud.titleLabel': 'Título da tarefa',
  'plaud.columnLabel': 'Coluna',
  'plaud.noteLabel': 'Nota / transcrição',
  'plaud.notePlaceholder': 'Cole aqui o resumo e a transcrição do Plaud…',
  'plaud.create': 'Criar tarefa',
  'plaud.defaultTitlePrefix': 'Reunião —',
  'plaud.noteRequired': 'Cole a nota ou transcrição.',
  'plaud.columnRequired': 'Escolha uma coluna.',
  'plaud.saveError': 'Não foi possível criar a tarefa. Tente novamente.',

  // Asistente IA (bot de ajuda flutuante)
  'assistant.open': 'Abrir assistente',
  'assistant.close': 'Fechar assistente',
  'assistant.title': 'Assistente Tembiapo',
  'assistant.subtitle': 'Ajudo com suas tarefas',
  'assistant.greeting': 'Olá! Posso consultar suas tarefas e projetos, e criar ou mover tarefas para você. Em que posso ajudar?',
  'assistant.placeholder': 'Escreva sua mensagem…',
  'assistant.send': 'Enviar',
  'assistant.thinking': 'Pensando…',
  'assistant.error': 'Não consegui processar isso agora. Tente de novo.',
  'assistant.proposed': 'Preparei esta ação para você:',
  'assistant.done': 'Pronto ✅',
  'assistant.cancelled': 'Cancelado, não fiz nenhuma alteração.',
  'assistant.confirmTitle': 'Confirme esta ação',
  'assistant.confirm': 'Confirmar',
  'assistant.cancel': 'Cancelar',
  'assistant.disclaimer': 'Assistente com IA em fase piloto. Revise os dados antes de confirmar.',
  'assistant.sug.pending': 'O que tenho pendente?',
  'assistant.sug.progress': 'Como vão meus projetos?',
  'assistant.sug.create': 'Criar uma tarefa',
  'assistant.counter': 'Grátis hoje: {n} · Créditos: {c}',
  'assistant.buyTitle': 'Suas consultas de hoje acabaram.',
  'assistant.buyCta': 'Comprar créditos',
  'assistant.buyAskAdmin': 'As consultas acabaram. Peça a um administrador que compre créditos de IA.',

  // Créditos de IA (faturamento)
  'credits.title': 'Créditos de IA',
  'credits.subtitle': 'Consultas do assistente. Cada usuário tem {n} grátis por dia; esgotadas, usam-se créditos da empresa.',
  'credits.balance': 'Créditos disponíveis',
  'credits.freeTodayLabel': 'Seu uso grátis de hoje',
  'credits.freeToday': '{used} de {total}',
  'credits.pack': '{n} créditos',
  'credits.buy': 'Comprar',
  'credits.buyError': 'Não foi possível iniciar a compra. Tente de novo.',
  'credits.none': 'Não há pacotes disponíveis por enquanto.',
  'credits.cardDisabled': 'A compra com cartão não está habilitada por enquanto.',
  'credits.return.confirmed': 'Créditos adicionados! Já pode usá-los com o assistente.',
  'credits.return.rejected': 'O pagamento não foi concluído. Nenhum crédito foi adicionado.',
  'credits.return.verifying': 'Verificando seu pagamento…',
  'credits.freeSub': 'consultas grátis hoje',
  'credits.personal': 'Seus créditos',
  'credits.company': 'Créditos da empresa',
  'credits.scope.title': 'Para quem está comprando?',
  'credits.scope.personal': 'Só para mim',
  'credits.scope.company': 'Para toda a empresa',
  'credits.scope.personalHint': 'Só você os usa.',
  'credits.scope.companyHint': 'Qualquer membro da equipe pode usá-los.',
  'credits.scope.memberNote': 'Os créditos que você comprar são só seus. Um administrador pode comprar para toda a equipe.',
  'credits.scope.adminOnly': 'Só um administrador pode comprar créditos para toda a empresa.',
  'credits.perCredit': '{price} por crédito',
  'credits.buyForMe': 'Comprar para mim',
  'credits.buyForCompany': 'Comprar para a empresa',
  'credits.cardNotice': 'A compra é com cartão. O pagamento é processado pela plataforma segura da Evoluado.',
  'credits.linkFromBilling': 'Compre créditos para você ou para toda a equipe, e veja seu saldo.',
  'mytasks.title': 'Minhas tarefas',
  'mytasks.subtitle': 'Tudo o que foi atribuído a você, de todos os projetos.',
  'mytasks.empty': 'Você não tem tarefas atribuídas',
  'mytasks.emptyHint': 'Quando atribuírem uma tarefa ou subtarefa a você, vai aparecer aqui.',
  'mytasks.showDone': 'Ver concluídas ({n})',
  'mytasks.viaSubtask': 'via subtarefa',
  'mytasks.today': 'Hoje',
  'mytasks.thisWeek': 'Esta semana',
  'mytasks.weekOf': 'Semana de {date}',
  'mytasks.bucket.overdue': 'Atrasadas',
  'mytasks.bucket.today': 'Hoje',
  'mytasks.bucket.week': 'Próximos 7 dias',
  'mytasks.bucket.later': 'Mais adiante',
  'mytasks.bucket.none': 'Sem data',
  // Menu móvel (sanduíche)
  'menu.open': 'Abrir menu',
  'menu.close': 'Fechar menu',
  'menu.sectionNav': 'Navegar',
  'menu.sectionAccount': 'Conta',
  'menu.language': 'Idioma',
  'menu.theme': 'Tema',
  // Login com Google (OAuth) + reCAPTCHA v3
  'auth.google.separator': 'ou',
  'auth.google.error': 'Não conseguimos entrar com o Google. Tente novamente.',
  'auth.google.inactive': 'Sua conta não está ativa. Fale com o suporte.',
  'auth.captcha.failed': 'Não conseguimos verificar a segurança. Recarregue a página e tente de novo.',
  // Verificação de e-mail
  'verifyEmail.verifying.title': 'Verificando seu e-mail…',
  'verifyEmail.verifying.body': 'Só um instante enquanto confirmo seu endereço.',
  'verifyEmail.success.title': 'E-mail verificado!',
  'verifyEmail.success.body': 'Seu endereço foi confirmado. Já pode usar o Tembiapo normalmente.',
  'verifyEmail.success.goApp': 'Ir para o meu tembiapo',
  'verifyEmail.success.goLogin': 'Entrar',
  'verifyEmail.error.title': 'Não conseguimos verificar seu e-mail',
  'verifyEmail.error.body': 'Este link não é válido ou já expirou. Peça um novo pelo app e tente de novo.',
  'verifyEmail.error.goLogin': 'Entrar',
  'verifyEmail.banner.text': 'Enviamos um e-mail para verificar seu endereço. Confira sua caixa (e o spam).',
  'verifyEmail.banner.resend': 'Reenviar',
  'verifyEmail.banner.resending': 'Enviando…',
  'verifyEmail.banner.sent': 'E-mail reenviado! Confira sua caixa.',
  'verifyEmail.banner.tooMany': 'Acabamos de enviar. Aguarde um momento antes de tentar de novo.',
  'verifyEmail.banner.error': 'Não conseguimos reenviar o e-mail. Tente novamente em instantes.',
  'verifyEmail.banner.dismiss': 'Ocultar',
  'verifyGate.body':
    'Verifique seu e-mail para criar projetos e convidar sua equipe. Confira sua caixa de entrada (e o spam).',
  'verifyGate.resend': 'Reenviar e-mail',
  'verifyGate.sent': 'Reenviamos o e-mail ✓',
  'verifyGate.error': 'Não foi possível reenviar. Tente de novo.',
  // Gate BLOQUEANTE (tela cheia) — verificação de e-mail obrigatória
  'verifyGate.title': 'Verifique seu e-mail para continuar',
  'verifyGate.wallBody':
    'Para proteger sua conta, verificar seu e-mail é obrigatório antes de usar o TEMBIAPO. Enviamos um link de verificação para:',
  'verifyGate.wallHint':
    'Abra esse e-mail e clique no link para ativar sua conta. Verifique também a pasta de spam ou promoções.',
  'verifyGate.checked': 'Já verifiquei',
  'verifyGate.stillPending':
    'Ainda não confirmamos seu e-mail. Se você já clicou no link, aguarde alguns segundos e tente de novo.',
  'verifyGate.logout': 'Sair',
  'reports.title': 'Relatórios',
  'reports.subtitle': 'O pulso da sua empresa: projetos, tarefas e equipe num relance.',
  'reports.loadError': 'Não foi possível carregar os relatórios.',
  'reports.metric.projects': 'Projetos',
  'reports.metric.projectsDetail': '{active} ativos',
  'reports.metric.tasks': 'Tarefas',
  'reports.metric.overdue': 'Atrasadas',
  'reports.metric.completed': 'Concluídas',
  'reports.metric.completedDetail': '{pct}% do total',
  'reports.metric.unassigned': 'Sem responsável',
  'reports.viewList': 'Ver lista',
  'reports.list.empty': 'Não há tarefas para mostrar.',
  'reports.list.unassigned': 'Sem responsável',
  'reports.priority.title': 'Distribuição por prioridade',
  'reports.priority.totalLabel': 'tarefas',
  'reports.byAssignee.title': 'Por responsável',
  'reports.byAssignee.empty': 'Ainda não há tarefas atribuídas.',
  'reports.byProject.title': 'Por projeto',
  'reports.byProject.empty': 'Ainda não há projetos com tarefas.',
  'reports.byProject.name': 'Projeto',
  'reports.byProject.tasks': 'Tarefas',
  'reports.byProject.progress': 'Concluídas',
  'reports.byProject.overdue': 'Atrasadas',
  'reports.empty.title': 'Sem dados para exibir',
  'reports.empty.body':
    'Quando você criar projetos e tarefas, verá aqui o resumo da sua empresa.',
  'reports.empty.cta': 'Criar projeto',
  'projects.title': 'Projetos',
  'projects.new': 'Novo projeto',
  'board.addTaskSubmit': 'Adicionar',
  'board.addTask': 'Adicionar tarefa',
  'board.emptyColumn': 'Ainda sem tarefas',
  'board.readOnly.title': 'Quadro somente leitura',
  'board.readOnly.body': 'Seu teste grátis terminou. Ative seu plano para voltar a mover e editar tarefas.',
  'board.spectator.title': 'Somente leitura',
  'board.spectator.body': 'Você não faz parte da equipe deste projeto. Pode ver e comentar.',

  // Equipe do projeto (quem pode editar)
  'projectTeam.button': 'Equipe',
  // Compartilhar por link de somente leitura (migr. 041)
  'share.button': 'Compartilhar',
  'share.title': 'Compartilhar em somente leitura',
  'share.subtitle': 'Gere um link para alguém de fora acompanhar o andamento, sem conta e sem poder editar.',
  'share.generate': 'Gerar link',
  'share.copy': 'Copiar',
  'share.copied': 'Copiado!',
  'share.share': 'Compartilhar no WhatsApp…',
  'share.revoke': 'Revogar link',
  'share.hint': 'Qualquer pessoa com o link pode VER (não editar nem comentar). Você pode revogar quando quiser.',
  // Métricas dos cards (projeto/pasta)
  'cardm.progressHint': '{done} de {total} na etapa final',
  'cardm.members': '{n} pessoas trabalhando',
  'cardm.overdue': '{n} atrasadas',
  'cardm.comments': '{n} mensagens',
  'projectTeam.title': 'Equipe do projeto',
  'projectTeam.subtitle': 'Quem pode editar este projeto. Os demais só podem ver e comentar.',
  'projectTeam.members': 'Integrantes',
  'projectTeam.empty': 'Você ainda não adicionou ninguém à equipe.',
  'projectTeam.loadError': 'Não foi possível carregar a equipe.',
  'projectTeam.addLabel': 'Adicionar à equipe',
  'projectTeam.addPlaceholder': 'Escolha um membro…',
  'projectTeam.addSubmit': 'Adicionar',
  'projectTeam.allAdded': 'Todos os membros da empresa já estão na equipe.',
  'projectTeam.remove': 'Remover da equipe',
  'projectTeam.removeConfirm': 'Remover {name} da equipe? As tarefas atribuídas não são alteradas.',
  'projectTeam.addError': 'Não foi possível adicionar essa pessoa. Tente de novo.',
  'projectTeam.removeError': 'Não foi possível remover essa pessoa. Tente de novo.',
  'projectTeam.error.forbidden': 'Você não tem permissão para gerenciar a equipe deste projeto.',
  'projectTeam.error.notCompany': 'Essa pessoa não pertence à sua empresa.',
  'common.cancel': 'Cancelar',
  'common.save': 'Salvar',
  'ui.password.show': 'Mostrar senha',
  'ui.password.hide': 'Ocultar senha',
  'common.delete': 'Excluir',
  'task.overdue': 'Atrasada',
  'task.dueToday': 'Vence hoje',
  'task.priority.LOW': 'Baixa',
  'task.priority.MEDIUM': 'Média',
  'task.priority.HIGH': 'Alta',
  'task.priority.URGENT': 'Urgente',
  'projects.create.freeBadge': '30 dias grátis · sem cartão',
  'view.label': 'Visão',
  'view.kanban': 'Kanban',
  'view.list': 'Lista',
  'view.calendar': 'Calendário',
  'view.timeline': 'Timeline',

  // Visão Calendário
  'calendar.today': 'Hoje',
  'calendar.prevMonth': 'Mês anterior',
  'calendar.nextMonth': 'Próximo mês',
  'calendar.moreCount': '+{n} mais',
  'calendar.less': 'Ver menos',
  'calendar.noColumns': 'Este quadro ainda não tem colunas.',
  'calendar.noDate.title': 'Sem data',
  'calendar.noDate.empty': 'Todas as tarefas têm data.',
  'calendar.dragHint': 'Arraste uma tarefa para um dia para definir a data.',
  'calendar.upcoming': 'Próximos eventos',
  'calendar.showUpcoming': 'Ver próximos',
  'calendar.noEvents': 'Sem eventos para mostrar.',
  'calendar.conflict': 'Conflito',

  // Visão Timeline / Gantt simples
  'timeline.groupBy': 'Agrupar por',
  'timeline.group.column': 'Status',
  'timeline.group.assignee': 'Responsável',
  'timeline.taskCol': 'Tarefa',
  'timeline.today': 'Hoje',
  'timeline.range': 'Próximas {n} semanas',
  'timeline.hidden': '{n} tarefas sem data ou fora do intervalo ocultas',
  'timeline.hiddenOne': '1 tarefa sem data ou fora do intervalo oculta',
  'timeline.noColumns': 'Este quadro ainda não tem colunas.',
  'timeline.empty': 'Não há tarefas com data limite neste intervalo.',
  'list.search': 'Buscar',
  'list.searchPlaceholder': 'Buscar por título…',
  'list.filter.column': 'Status',
  'list.filter.priority': 'Prioridade',
  'list.filter.assignee': 'Responsável',
  'list.filter.all': 'Todos',
  'list.group.label': 'Agrupar por',
  'list.group.column': 'Status',
  'list.group.assignee': 'Responsável',
  'list.group.priority': 'Prioridade',
  'list.group.none': 'Sem agrupar',
  'list.col.title': 'Título',
  'list.col.status': 'Status',
  'list.col.priority': 'Prioridade',
  'list.col.assignee': 'Responsável',
  'list.col.dueDate': 'Data limite',
  'list.col.subtasks': 'Subtarefas',
  'list.results': '{n} resultados',
  'list.resultsOne': '1 resultado',
  'list.resultsZero': 'Sem resultados',
  'list.empty.title': 'Ainda sem tarefas',
  'list.noResults.title': 'Nada corresponde à sua busca',
  'list.clearFilters': 'Limpar filtros',
  'list.quickAdd.placeholder': 'Nova tarefa…',
  'list.quickAdd.submit': 'Adicionar',
  'comments.title': 'Comentários',
  'comments.empty': 'Ainda sem comentários. Escreva o primeiro.',
  'comments.placeholder': 'Escreva um comentário… use @ para mencionar',
  'comments.submit': 'Comentar',
  'attachments.title': 'Anexos',
  'attachments.empty': 'Sem arquivos anexados.',
  'attachments.pick': 'Escolher arquivos',
  'attachments.download': 'Baixar',
  'notifications.title': 'Notificações',
  'notifications.markAll': 'Marcar todas como lidas',
  'notifications.empty': 'Sem notificações.',
  'notifications.seeAll': 'Ver todas',
  'notifications.open': 'Abrir',
  'profile.avatar.title': 'Foto de perfil',
  'profile.avatar.change': 'Alterar foto',
  'profile.appearance.title': 'Aparência e preferências',
  'profile.appearance.subtitle': 'Tema, idioma e avisos do app.',
  'profile.appearance.theme': 'Tema (claro / escuro)',
  'profile.appearance.language': 'Idioma',
  'profile.appearance.notifications': 'Preferências de notificações',
  'profile.appearance.shareApp': 'Compartilhar Tembiapo (convide a instalar)',
  'profile.appearance.shareAppText': 'Use o Tembiapo para organizar o trabalho da sua equipe, sem complicação.',

  // Colunas — edição estrutural
  'column.add': 'Adicionar coluna',
  'column.addTitle': 'Nova coluna',
  'column.addSubmit': 'Adicionar',
  'column.settings.title': 'Editar coluna',
  'column.menu': 'Opções da coluna',
  'column.moveLeft': 'Mover para a esquerda',
  'column.moveRight': 'Mover para a direita',
  'column.name': 'Nome',
  'column.namePlaceholder': 'Nome da coluna',
  'column.color': 'Cor',
  'column.wip': 'Limite de trabalho em curso',
  'column.wipPlaceholder': 'Sem limite',
  'column.wipHint': 'Máximo de tarefas nesta coluna. Deixe vazio para não limitar.',
  'column.final': 'Coluna final (encerra a tarefa)',
  'column.finalHint':
    'As tarefas nesta coluna são consideradas concluídas: não contam como vencidas, não enviam lembretes e liberam as que dependiam delas. Você pode marcar várias (Concluído, Arquivado, etc.).',
  'column.delete': 'Excluir coluna',
  'column.deleteConfirm': 'Excluir esta coluna?',
  'column.deleteConfirmCta': 'Excluir',
  'column.error.lastColumn': 'Você não pode excluir a última coluna do quadro.',
  'column.saveError': 'Não foi possível salvar as alterações.',

  // Modelos de tarefa
  'taskTemplate.saveAs': 'Salvar como modelo',
  'taskTemplate.saveAsTitle': 'Reutilizável em qualquer projeto da sua conta.',
  'taskTemplate.thisProjectOnly': 'Somente este projeto',
  'taskTemplate.nameLabel': 'Nome do modelo',
  'taskTemplate.namePlaceholder': 'Ex: Relatório de bug',
  'taskTemplate.save': 'Salvar modelo',
  'taskTemplate.saved': 'Modelo salvo.',
  'taskTemplate.saveError': 'Não foi possível salvar o modelo.',
  'taskTemplate.fromTemplate': 'A partir de modelo',
  'taskTemplate.pickTitle': 'Criar tarefa a partir de modelo',
  'taskTemplate.empty': 'Você ainda não tem modelos. Salve uma tarefa como modelo para reutilizá-la.',
  'taskTemplate.loadError': 'Não foi possível carregar os modelos.',
  'taskTemplate.createError': 'Não foi possível criar a tarefa a partir do modelo.',
  'taskTemplate.use': 'Usar',
  'taskTemplate.delete': 'Excluir modelo',
  'taskTemplate.deleteConfirm': 'Excluir este modelo?',
  'taskTemplate.preview.priority': 'Prioridade: {priority}',
  'taskTemplate.preview.checklist': '{n} itens de checklist',
  'taskTemplate.preview.checklistOne': '1 item de checklist',
  'taskTemplate.preview.noChecklist': 'Sem checklist',

  // Automações when-then
  'automation.button': 'Automações',
  'automation.title': 'Automações',
  'automation.subtitle': 'Regras "quando isto acontecer, faça isto" para o seu quadro.',
  'automation.loadError': 'Não foi possível carregar as automações.',
  'automation.empty': 'Você ainda não tem automações. Crie sua primeira regra abaixo.',
  'automation.rulesTitle': 'Regras',
  'automation.count': '{n} regras',
  'automation.countOne': '1 regra',
  'automation.new': 'Nova regra',
  'automation.newTitle': 'Criar regra',
  'automation.editTitle': 'Editar regra',
  'automation.name': 'Nome da regra',
  'automation.namePlaceholder': 'Ex: Atribuir ao mover para Concluído',
  'automation.whenLabel': 'Quando…',
  'automation.thenLabel': 'Fazer…',
  'automation.event.label': 'Gatilho',
  'automation.event.task_moved_to': 'Uma tarefa é movida para uma coluna',
  'automation.event.task_created': 'Uma tarefa é criada',
  'automation.column.label': 'Coluna destino',
  'automation.action.label': 'Ação',
  'automation.action.assign': 'Atribuir a um membro',
  'automation.action.set_priority': 'Alterar a prioridade',
  'automation.action.notify': 'Notificar um membro',
  'automation.user.label': 'Membro',
  'automation.priority.label': 'Prioridade',
  'automation.save': 'Salvar regra',
  'automation.edit': 'Editar regra',
  'automation.delete': 'Excluir regra',
  'automation.deleteConfirm': 'Excluir esta regra? Não pode ser desfeito.',
  'automation.saveError': 'Não foi possível salvar a regra.',
  'automation.active': 'Regra ativa',
  'automation.inactive': 'Inativa',
  'automation.activate': 'Ativar',
  'automation.deactivate': 'Pausar',
  'automation.readOnly': 'Seu teste grátis terminou. Você pode ver suas regras, mas não criá-las ou editá-las.',
  'automation.when.movedTo': 'Quando uma tarefa é movida para',
  'automation.when.created': 'Quando uma tarefa é criada',
  'automation.do.assign': 'atribuir a',
  'automation.do.setPriority': 'alterar a prioridade para',
  'automation.do.notify': 'notificar',
  'automation.entity.deleted': '(excluída)',
  'automation.entity.someone': 'alguém',

  // Navegação / menu de usuário
  'nav.preferences': 'Notificações',

  // Preferências de notificação (Fase 2/3)
  'prefs.title': 'Preferências',
  'prefs.subtitle': 'Ajuste seu Tembiapo: página inicial, guias e notificações.',
  // Preferências do app (migr. 040)
  'prefs.app.title': 'Preferências do app',
  'prefs.app.subtitle': 'Ajuste como o seu Tembiapo inicia e aparece.',
  'prefs.app.homePage': 'Página inicial',
  'prefs.app.homePageHint': 'Escolha qual tela ver primeiro ao entrar.',
  'prefs.app.home.inicio': 'Início',
  'prefs.app.home.proyectos': 'Projetos',
  'prefs.app.home.reportes': 'Relatórios',
  'prefs.app.guide': 'Guia de projetos',
  'prefs.app.guideHint': 'Mostrar o acordeão que explica como se organizam pastas, projetos e tarefas.',
  // Guia de hierarquia em /proyectos
  'guide.title': 'Como o trabalho se organiza?',
  'guide.subtitle': 'Uma olhada rápida na ordem das coisas no Tembiapo.',
  'guide.dismiss': 'Ocultar este guia',
  'guide.footer': 'Você pode mostrá-lo de novo quando quiser em Preferências.',
  'guide.ex.folder': 'Marketing',
  'guide.ex.project': 'Campanha de lançamento',
  'guide.ex.task': 'Desenhar o flyer',
  'guide.ex.subtask': 'Escolher a paleta de cores',
  'guide.folder.title': '1. Pastas — para agrupar',
  'guide.folder.body':
    'São o primeiro nível, opcional. Agrupam seus projetos por área, cliente ou tema (ex. “Marketing”, “Clientes”, “Interno”). Uma pasta contém vários projetos.',
  'guide.project.title': '2. Projetos — seu espaço de trabalho',
  'guide.project.body':
    'Cada projeto é um quadro com colunas (Kanban); também pode ser visto como Lista, Calendário ou Timeline. Fica dentro de uma pasta ou solto na raiz.',
  'guide.task.title': '3. Tarefas — o que precisa ser feito',
  'guide.task.body':
    'Dentro de um projeto você cria tarefas: com responsável, prioridade, data de vencimento, comentários e arquivos. Movem-se entre colunas conforme avançam.',
  'guide.subtask.title': '4. Subtarefas — o checklist de uma tarefa',
  'guide.subtask.body':
    'Cada tarefa pode ter subtarefas: passos menores, atribuíveis e com sua própria data. Servem para dividir uma tarefa grande em partes.',
  'prefs.loadError': 'Não foi possível carregar suas preferências.',
  'push.device.title': 'Notificações push neste dispositivo',
  'push.device.hint':
    'Receba avisos mesmo sem o Tembiapo aberto, neste navegador ou dispositivo.',
  'push.unsupported': 'Este navegador não suporta notificações push.',
  'push.denied':
    'Você bloqueou as notificações para este site. Habilite-as nas configurações do navegador para ativá-las.',
  'push.unavailable': 'As notificações push não estão disponíveis no momento.',
  'push.error': 'Não foi possível alterar a configuração de push. Tente de novo.',
  'prefs.matrix.title': 'O que você quer receber',
  'prefs.matrix.hint': 'Ative ou desative cada tipo de aviso por canal.',
  'prefs.eventColumn': 'Evento',
  'prefs.soon': 'Em breve',
  'prefs.email.note': 'O envio de e-mails por evento estará disponível em breve.',
  'prefs.turnAllOn': 'Ligar tudo',
  'prefs.turnAllOff': 'Desligar tudo',
  'prefs.matrix.note':
    'Os e-mails chegam ao seu correio conforme estas preferências. Use os interruptores de cada coluna para ligar ou desligar um canal inteiro de uma vez.',
  'prefs.channel.IN_APP': 'No app',
  'prefs.channel.EMAIL': 'E-mail',
  'prefs.channel.PUSH': 'Push',
  'prefs.event.task_assigned': 'Uma tarefa é atribuída a você',
  'prefs.event.subtask_assigned': 'Uma subtarefa é atribuída a você',
  'prefs.event.task_due_soon': 'Tarefa a vencer',
  'prefs.event.task_overdue': 'Tarefa vencida',
  'prefs.event.event_upcoming': 'Audiência/evento próximo',
  'prefs.event.daily_agenda': 'Resumo diário (agenda do dia)',
  'prefs.event.weekly_digest': 'Resumo semanal',
  'prefs.event.mention': 'Você é mencionado',
  'prefs.event.comment': 'Comentários nas suas tarefas',
  'prefs.event.plan_expiring': 'Seu plano está a vencer',
  'prefs.event.trial_ending': 'Seu período de teste está a terminar',
  'prefs.event.plan_expired': 'Seu plano venceu',
  'prefs.event.account_blocked': 'Conta bloqueada',
  'prefs.event.payment_confirmed': 'Pagamento confirmado',
  'prefs.event.payment_rejected': 'Pagamento recusado',
  'prefs.event.automation': 'Automações',
  'prefs.event.invitation_accepted': 'Seu convite é aceito',

  // Dependências entre tarefas (Fase 3)
  'dependency.title': 'Dependências',
  'dependency.loadError': 'Não foi possível carregar as dependências.',
  'dependency.blockedBy': 'Bloqueada por',
  'dependency.blockedBy.empty': 'Não depende de nenhuma outra tarefa.',
  'dependency.blocks': 'Bloqueia',
  'dependency.blocks.empty': 'Não bloqueia nenhuma outra tarefa.',
  'dependency.addBlockedBy': 'Adicionar tarefa que a bloqueia…',
  'dependency.addBlocks': 'Adicionar tarefa que esta bloqueia…',
  'dependency.noCandidates': 'Não há outras tarefas para vincular.',
  'dependency.remove': 'Remover dependência',
  'dependency.done': 'Bloqueador concluído',
  'dependency.blocked': 'Bloqueada por outra tarefa',
  'dependency.blockedByCount': 'Bloqueada por {n} tarefa(s)',
  'dependency.error.cycle': 'Não é possível: criaria um ciclo de dependências.',
  'dependency.error.invalid': 'Não foi possível adicionar essa dependência.',
  'dependency.error.generic': 'Não foi possível atualizar as dependências. Tente de novo.',

  // Subtarefas (checklist com responsável + obrigatórias)
  'subtask.title': 'Subtarefas',
  'subtask.add': 'Adicionar subtarefa',
  'subtask.placeholder': 'Nova subtarefa…',
  'subtask.required': 'Obrigatória',
  'subtask.markRequired': 'Marcar como obrigatória',
  'subtask.markOptional': 'Remover obrigatória',
  'subtask.assignee': 'Responsável pela subtarefa',
  'subtask.unassigned': 'Sem responsável',
  'subtask.dueDate': 'Data de vencimento (não pode ultrapassar a da tarefa)',
  'subtask.dueClear': 'remover',
  'subtask.delete': 'Excluir subtarefa',
  'subtask.toggleDone': 'Marcar como concluída',
  'subtask.empty': 'Ainda não há subtarefas.',
  'subtask.loadError': 'Não foi possível carregar as subtarefas.',
  'subtask.saveError': 'Não foi possível salvar. Tente de novo.',
  'subtask.requiredHint': 'As subtarefas obrigatórias não concluídas impedem mover a tarefa para outra coluna.',
  'subtask.blockedMove': 'Não é possível avançar: há subtarefas obrigatórias não concluídas.',
  'subtask.blockedMoveNamed': 'Não é possível passar para {column}: falta concluir «{task}».',
  'subtask.blockedMoveNamedMore':
    'Não é possível passar para {column}: falta concluir «{task}» e mais {n}.',
  'subtask.requiredForChip': 'Obrigatória · para {column}',
  'subtask.requiredForLabel': 'para passar para',
  'subtask.columnSelect': 'Coluna que exige a obrigatória',
  'subtask.waive': 'Ignorar',
  'subtask.waived': 'Ignorada',
  'subtask.reactivate': 'Reativar',
  'subtask.reason': 'Motivo',
  'subtask.reasonPlaceholder': 'Motivo para ignorar…',
  'subtask.reasonRequired': 'Informe um motivo.',
  'subtask.waivedReason': 'Motivo: {reason}',
  'subtask.lockedAdvanced':
    'Não é possível desmarcar uma subtarefa obrigatória com a tarefa avançada. Mova-a para a primeira coluna para editá-la.',
  'card.requiredOpen': '{n} obrigatória(s) pendente(s)',
  'card.owner': 'Responsável principal',
  'card.inSubtask': 'Responsável de uma subtarefa',
  'card.comments': '{n} comentário(s)',
  'card.commentsUnread': '{n} comentário(s) · {u} não lido(s)',
  'card.attachments': '{n} arquivo(s) anexo(s)',

  // Filtros salvos (Fase 3)
  'savedFilter.label': 'Filtros salvos',
  'savedFilter.apply': 'Aplicar filtro salvo',
  'savedFilter.applyPlaceholder': 'Escolha um filtro…',
  'savedFilter.empty': 'Sem filtros salvos',
  'savedFilter.save': 'Salvar filtro',
  'savedFilter.nameLabel': 'Nome do filtro',
  'savedFilter.namePlaceholder': 'Ex: Urgentes sem responsável',
  'savedFilter.delete': 'Excluir filtro',
  'savedFilter.deleteConfirm': 'Excluir o filtro "{name}"?',
  'savedFilter.saveError': 'Não foi possível salvar o filtro.',
  'savedFilter.deleteError': 'Não foi possível excluir o filtro.',

  // Comuns / marca
  'common.loading': 'Carregando…',
  'common.retry': 'Tentar de novo',
  'common.genericError': 'Algo deu errado. Tente de novo em instantes.',
  'common.required': 'Este campo é obrigatório.',
  'common.emailInvalid': 'Digite um e-mail válido.',
  'common.saving': 'Salvando…',
  'common.deleting': 'Excluindo…',
  'common.back': 'Voltar',
  'common.close': 'Fechar',
  'common.none': 'Sem responsável',
  'common.optional': 'Opcional',
  'common.copy': 'Copiar link',
  'common.copied': 'Copiado!',
  'common.optionalLabel': '{label} (opcional)',
  'common.savedOk': 'Alterações salvas.',

  // Login
  'login.subtitle': 'Gerencie seus projetos e sua equipe.',
  'login.emailPlaceholder': 'voce@empresa.com',
  'login.passwordPlaceholder': 'Sua senha',
  'login.submitting': 'Entrando…',
  'login.noAccount': 'Ainda não tem conta?',
  'login.createAccount': 'Crie sua empresa grátis',
  'login.error.invalid': 'E-mail ou senha incorretos.',
  'login.error.tooMany': 'Muitas tentativas. Tente de novo em alguns minutos.',
  'login.forgot': 'Esqueceu sua senha?',

  // Recuperar senha (esqueci minha senha)
  'forgot.title': 'Recupere sua senha',
  'forgot.subtitle': 'Digite seu e-mail e enviaremos um link para redefini-la.',
  'forgot.emailLabel': 'E-mail',
  'forgot.submit': 'Enviar link',
  'forgot.sent':
    'Se houver uma conta com esse e-mail, enviamos um link para redefinir a senha. Verifique seu e-mail.',
  'forgot.backToLogin': 'Voltar a entrar',

  // Redefinir senha (a partir do link do e-mail)
  'reset.title': 'Escolha uma nova senha',
  'reset.subtitle': 'Crie uma nova senha para sua conta.',
  'reset.newPassword': 'Nova senha',
  'reset.confirmPassword': 'Confirme a senha',
  'reset.mismatch': 'As senhas não coincidem.',
  'reset.submit': 'Salvar senha',
  'reset.success': 'Sua senha foi atualizada.',
  'reset.expired': 'O link expirou ou já foi usado. Solicite um novo.',
  'reset.invalid': 'O link não é válido. Solicite um novo.',
  'reset.toLogin': 'Entrar',

  // Login com biometria (somente app nativo)
  'auth.biometric.cta': 'Entrar com Face ID / digital',
  'auth.biometric.verifying': 'Verificando…',
  'auth.biometric.or': 'ou',
  'auth.biometric.title': 'Entre com biometria',
  'auth.biometric.reason': 'Confirme sua identidade para entrar no Tembiapo.',

  // Cadastro
  'register.title': 'Crie sua empresa no Tembiapo',
  'register.subtitle': 'Escolha seu segmento e seu plano. Você começa em segundos.',
  'register.step.vertical': 'Escolha seu segmento',
  'register.step.verticalHint': 'Pré-carregamos projetos e modelos de acordo com sua atividade.',
  'register.step.plan': 'Escolha seu plano',
  'register.step.planHint': 'Você começa com 30 dias grátis: hoje não paga nada, a cobrança começa só em 30 dias. Pode trocar de plano quando quiser.',
  'register.step.planPending': 'Escolha primeiro seu setor para ver os planos.',
  'register.step.account': 'Dados da sua empresa e da sua conta',
  'register.billing.monthly': 'Mensal',
  'register.billing.yearly': 'Anual',
  'register.billing.yearlyHint': '2 meses de brinde',
  'register.plan.projects': '{n} projetos',
  'register.plan.users': '{n} usuários',
  'register.plan.perMonth': '/mês',
  'register.plan.perYear': '/ano',
  'register.plan.trialNote': 'Hoje você paga {zero} · cobrança em 30 dias',
  'register.plan.choose': 'Escolher',
  'register.plan.chosen': 'Escolhido',
  'register.company.name': 'Nome da empresa',
  'register.company.namePlaceholder': 'Minha Empresa Ltda.',
  'register.admin.firstName': 'Nome',
  'register.admin.lastName': 'Sobrenome',
  'register.admin.passwordPlaceholder': 'Mínimo 8 caracteres',
  'register.admin.passwordConfirm': 'Repetir senha',
  'register.password.weak': 'Fraca',
  'register.password.fair': 'Aceitável',
  'register.password.strong': 'Forte',
  'register.password.tooShort': 'Use pelo menos 8 caracteres.',
  'register.password.needMix': 'Combine letras e números.',
  'register.password.mismatch': 'As senhas não coincidem.',
  'register.submit': 'Criar minha empresa',
  'register.submitting': 'Criando sua empresa…',
  'register.selectPlanFirst': 'Escolha um plano para continuar.',
  'register.error.emailTaken': 'Esse e-mail já está cadastrado. Quer entrar?',
  'register.haveAccount': 'Já tem conta?',
  'register.goLogin': 'Entrar',
  'register.loadError': 'Não foi possível carregar os segmentos e planos.',

  // Home / painel
  'home.greeting': 'Olá, {name}',
  'home.subtitle': 'Este é o painel de {company}.',
  'home.userMenu.account': 'Minha conta',
  'home.trial.active': 'Você está no seu teste grátis: faltam {days} dias.',
  'home.trial.lastDays': 'Seu teste grátis termina em {days} dias!',
  'home.trial.lastDay': 'Seu teste grátis termina hoje!',
  'home.trial.expired': 'Seu teste grátis terminou. Ative seu plano para continuar trabalhando.',
  'home.trial.cta': 'Ativar meu plano',
  'home.empty.title': 'Crie seu primeiro projeto',
  'home.empty.body': 'Um projeto é o seu quadro de trabalho. Comece a partir de um modelo do seu segmento ou do zero.',
  'home.empty.cta': 'Criar projeto',
  'home.empty.soon': 'Disponível no próximo milestone',
  'home.empty.memberTitle': 'Ainda não há projetos',
  'home.empty.memberBody': 'Aguarde um administrador atribuir tarefas a você.',
  'home.quick.title': 'Ações rápidas',
  'home.quick.newProjectDesc': 'Monte um quadro a partir de um modelo do seu segmento ou do zero.',
  'home.quick.invite': 'Convidar membros',
  'home.quick.inviteDesc': 'Adicione sua equipe para trabalharem juntos.',
  'home.quick.pair': 'Conectar app móvil',
  'home.quick.pairDesc': 'Gere o QR para vincular o app no seu celular.',
  'home.comments.title': 'Últimos comentários',
  'home.comments.empty': 'Ainda sem comentários.',
  'home.activity.title': 'Últimas mudanças de estado',
  'home.activity.empty': 'Sem atividade recente.',
  'home.team.title': 'Equipe',
  'home.team.empty': 'Ainda não há membros ativos.',
  'home.team.invite': 'Convidar',
  'home.team.viewAll': 'Ver todos',
  'home.team.emailAria': 'Enviar e-mail para {name}',
  'home.team.phoneAria': 'Ligar para {name}',
  'home.readonly.locked': 'Ative seu plano para voltar a editar.',

  // Sessão
  'session.expired': 'Sua sessão expirou. Entre de novo.',

  // Navegação
  'nav.home': 'Início',
  'nav.team': 'Equipe',
  'nav.profile': 'Perfil',
  'nav.billing': 'Faturamento',
  'nav.connectors': 'Conectores',

  // Página "Conectores" (baixar o app + conector de IA)
  'connectors.title': 'Conectores',
  'connectors.subtitle':
    'Baixe o app e conecte seu assistente de IA para usar o Tembiapo de onde estiver.',

  // Projetos (lista)
  'projects.subtitle': 'Seus quadros de trabalho.',
  'projects.loadError': 'Não foi possível carregar seus projetos.',
  'projects.taskCount': '{n} tarefas',
  'projects.taskCountOne': '1 tarefa',
  'projects.taskCountZero': 'Sem tarefas',
  'projects.empty.title': 'Crie seu primeiro projeto',
  'projects.empty.body':
    'Um projeto é o seu quadro de trabalho. Comece a partir de um modelo do seu segmento ou do zero.',
  'projects.empty.cta': 'Criar projeto',
  'projects.empty.memberTitle': 'Ainda não há projetos',
  'projects.empty.memberBody': 'Aguarde um administrador atribuir tarefas a você.',
  'projects.open': 'Abrir quadro',

  // Ações sobre um projeto (renomear / excluir)
  'project.actions': 'Ações do projeto',
  'project.rename': 'Renomear',
  'project.rename.title': 'Renomear projeto',
  'project.name': 'Nome',
  'project.description': 'Descrição',
  'project.namePlaceholder': 'Redesign do site',
  'project.descriptionPlaceholder': 'Sobre o que é este projeto?',
  'project.name.required': 'O nome do projeto é obrigatório.',
  'project.rename.error': 'Não foi possível salvar as alterações. Tente de novo.',
  'project.delete': 'Excluir',
  'project.delete.title': 'Excluir projeto',
  'project.delete.confirm':
    'O projeto “{name}” e todas as suas tarefas, colunas e comentários serão excluídos. Esta ação não pode ser desfeita.',
  'project.delete.submit': 'Excluir projeto',
  'project.delete.error': 'Não foi possível excluir o projeto. Tente de novo.',
  'project.delete.forbidden': 'Apenas um administrador da empresa pode excluir projetos.',
  'project.notFound': 'Este projeto não existe mais.',
  'project.favorite': 'Marcar como favorito',
  'project.unfavorite': 'Remover dos favoritos',
  'project.reorder': 'Arraste para reordenar',

  // Visibilidade do projeto (controle de acesso; aplicado no backend)
  'project.visibility': 'Visibilidade',
  'project.visibility.public': 'Público',
  'project.visibility.private': 'Privado',
  'project.visibility.publicHint': 'Toda a equipe vê todas as tarefas do projeto.',
  'project.visibility.privateHint': 'Cada pessoa vê apenas as tarefas em que está atribuída. O administrador e quem criou o projeto veem todas.',
  'project.visibility.badge': 'Privado',

  // Criar projeto
  'projects.create.title': 'Novo projeto',
  'projects.create.subtitle': 'Escolha um modelo ou comece em branco.',
  'projects.create.templateStep': 'Escolha um modelo',
  'projects.create.detailsStep': 'Nome do projeto',
  'projects.create.blank': 'Em branco',
  'projects.create.blankHint': 'Um quadro vazio para montar do seu jeito.',
  'projects.create.columnsPreview': 'Colunas',
  'projects.create.fields': '{n} campos',
  'projects.create.fieldsOne': '1 campo',
  'projects.create.name': 'Nome',
  'projects.create.namePlaceholder': 'Redesign do site',
  'projects.create.description': 'Descrição',
  'projects.create.descriptionPlaceholder': 'Sobre o que é este projeto?',
  'projects.create.submit': 'Criar projeto',
  'projects.create.submitting': 'Criando…',
  'projects.create.loadError': 'Não foi possível carregar os modelos.',
  'projects.create.limitReached':
    'Você atingiu o limite de projetos do seu plano. Ative ou melhore seu plano para criar mais.',
  'projects.create.limitCta': 'Ver meu plano',
  'projects.create.blankBadge': 'Do zero',
  'projects.create.pickTemplate': 'Escolha um ponto de partida',
  'projects.create.columnsCount': '{n} colunas',

  // Quadro (Kanban)
  'board.loadError': 'Não foi possível carregar o quadro.',
  'board.moveError': 'Não foi possível mover a tarefa. Tente de novo.',
  'board.notFound': 'Este projeto não existe ou você não tem acesso.',
  'board.backToProjects': 'Projetos',
  'board.empty': 'Este quadro ainda não tem colunas.',
  'board.taskCount': '{n} tarefas',
  'board.taskCountOne': '1 tarefa',
  'board.column.count': '{n}',
  'board.column.tasks': 'Tarefas na coluna',
  'board.column.wip': 'Limite de trabalho em curso: {n}',
  'board.column.wipReached': 'Você atingiu o limite de {n}.',
  'board.column.wipOver': 'Você passou do limite ({n}).',
  'board.addTaskPlaceholder': 'Título da tarefa…',
  'board.addTaskHint': 'Enter para adicionar · Esc para fechar',
  'board.emptyColumnHint': 'Arraste tarefas para cá ou adicione uma nova.',
  'board.readOnly.cta': 'Ativar meu plano',

  // Tarefa (cartão e edição)
  'task.edit.title': 'Editar tarefa',
  'task.expand': 'Ver em tela cheia',
  'task.collapse': 'Voltar ao painel lateral',
  'task.field.title': 'Título',
  'task.field.description': 'Descrição',
  'task.field.priority': 'Prioridade',
  'task.field.assignee': 'Responsável',
  'task.field.dueDate': 'Data limite',
  'task.dueSoon': 'Vence em breve',
  'task.saveError': 'Não foi possível salvar as alterações.',
  'task.deleteConfirm': 'Excluir esta tarefa? Não pode ser desfeito.',
  'task.readOnly': 'Somente leitura.',

  // Visão Lista/Tabela
  'list.col.actions': 'Ações',
  'list.sortBy': 'Ordenar por {col}',
  'list.subtasks': '{n} subtarefas',
  'list.inSubtasks': 'em subtarefas',
  'list.subtaskAssignees': 'Responsáveis das subtarefas',
  'list.empty.body': 'Crie sua primeira tarefa com a criação rápida acima.',
  'list.noResults.body': 'Tente mudar os filtros ou o texto de busca.',
  'list.quickAdd.column': 'Coluna destino',
  'list.open': 'Abrir tarefa',
  'list.noColumns': 'Este quadro ainda não tem colunas.',

  // Funções / status
  'role.SUPER_ADMIN': 'Super admin',
  'role.COMPANY_ADMIN': 'Administrador',
  'role.MEMBER': 'Membro',
  'status.ACTIVE': 'Ativo',
  'status.SUSPENDED': 'Suspenso',
  'subBanner.blockedTitle': 'Sua conta está bloqueada',
  'subBanner.blockedDesc': 'Foi bloqueada por falta de pagamento. Regularize para recuperar o acesso.',
  'subBanner.trialTitle': 'Seu teste grátis terminou',
  'subBanner.trialDesc': 'Escolha um plano e pague para continuar usando o Tembiapo.',
  'subBanner.pay': 'Pagar para desbloquear',
  'subBanner.askAdmin': 'Peça a um administrador para regularizar o pagamento.',
  'subBanner.update': 'Atualizar pagamento',
  'subBanner.grace.trialTitle': 'Seu teste grátis terminou',
  'subBanner.grace.trialDesc':
    'Você tem {days} dias de carência para continuar usando o Tembiapo. Atualize seu pagamento para não perder o acesso.',
  'subBanner.grace.dueTitle': 'Seu pagamento está vencido',
  'subBanner.grace.dueDesc':
    'Você tem {days} dias de carência. Atualize seu pagamento para manter sua conta ativa.',
  'subBanner.grace.lastDayTitle': 'Último dia de carência',
  'subBanner.grace.lastDayDesc': 'Atualize seu pagamento hoje para não perder o acesso.',
  'status.INVITED': 'Convidado',
  'status.PENDING': 'Pendente',

  // Equipe
  'team.title': 'Equipe',
  'team.subtitle': 'Gerencie quem trabalha em {company}.',
  'team.invite': 'Convidar',
  'team.loadError': 'Não foi possível carregar sua equipe.',
  'team.restricted.title': 'Acesso restrito',
  'team.restricted.body': 'Apenas administradores podem gerenciar a equipe.',
  'team.members.title': 'Membros',
  'team.members.count': '{n} membros',
  'team.col.name': 'Nome',
  'team.col.email': 'E-mail',
  'team.col.jobTitle': 'Cargo',
  'team.col.role': 'Função',
  'team.col.status': 'Status',
  'team.member.remove': 'Remover',
  'team.member.removeConfirm':
    'Remover {name} desta empresa? Ela perderá acesso a esta empresa. A conta e as outras empresas não são afetadas.',
  'team.member.lastAdmin': 'Você não pode remover o último administrador da empresa.',
  'team.member.removeError': 'Não foi possível remover o membro. Tente de novo.',
  'team.role.change': 'Alterar função',
  'team.role.changeError': 'Não foi possível alterar a função. Tente de novo.',
  'team.col.actions': 'Ações',
  'team.noJobTitle': '—',
  'team.pending.title': 'Convites pendentes',
  'team.pending.count': '{n} pendentes',
  'team.pending.empty': 'Não há convites pendentes.',
  'team.pending.expires': 'Expira em {date}',
  'team.pending.revoke': 'Revogar',
  'team.pending.revokeConfirm': 'Revogar o convite de {email}?',
  'team.invite.shareText': 'Convido você a entrar na nossa equipe no Tembiapo. Acesse com este link:',
  'team.pending.copyUnavailable': 'O link não está disponível. Reenvie o convite para obter um novo.',

  // Modal de convite
  'team.inviteModal.title': 'Convide sua equipe',
  'team.inviteModal.subtitle': 'Enviaremos um link para completar a conta.',
  'team.inviteModal.email': 'E-mail',
  'team.inviteModal.emailPlaceholder': 'pessoa@empresa.com',
  'team.inviteModal.firstName': 'Nome',
  'team.inviteModal.lastName': 'Sobrenome',
  'team.inviteModal.jobTitle': 'Cargo',
  'team.inviteModal.jobTitlePlaceholder': 'Designer, Dev, PM…',
  'team.inviteModal.role': 'Função',
  'team.inviteModal.submit': 'Enviar convite',
  'team.inviteModal.submitting': 'Enviando…',
  'team.inviteModal.another': 'Convidar outra pessoa',
  'team.inviteModal.done': 'Pronto',
  'team.inviteModal.successTitle': 'Convite criado!',
  'team.inviteModal.successBody': 'Compartilhe este link com {email} para que complete a conta:',
  'team.error.emailTaken': 'Esse e-mail já pertence a um membro da sua equipe.',
  'team.error.alreadyInvited': 'Já existe um convite pendente para esse e-mail.',
  'team.error.limit': 'Você atingiu o limite de usuários do seu plano. Ative ou melhore seu plano para convidar mais pessoas.',
  'team.error.limitCta': 'Ver meu plano',

  // Aceitar convite (público)
  'invite.title': 'Junte-se a {company}',
  'invite.subtitle': 'Complete seus dados e defina sua senha para começar.',
  'invite.emailLabel': 'Você vai entrar com',
  'invite.roleLabel': 'Função atribuída',
  'invite.firstName': 'Nome',
  'invite.lastName': 'Sobrenome',
  'invite.jobTitle': 'Cargo',
  'invite.phone': 'Telefone',
  'invite.password': 'Senha',
  'invite.passwordConfirm': 'Repetir senha',
  'invite.submit': 'Criar minha conta',
  'invite.submitting': 'Criando sua conta…',
  'invite.loading': 'Carregando convite…',
  'invite.invalid.title': 'Convite inválido',
  'invite.invalid.body': 'Este link não é válido. Peça à sua equipe para reenviar o convite.',
  'invite.expired.title': 'Convite expirado',
  'invite.expired.body': 'Este link já expirou. Peça à sua equipe um novo convite.',
  'invite.accepted.title': 'Convite já utilizado',
  'invite.accepted.body': 'Este convite já foi aceito. Entre com seu e-mail e senha.',
  'invite.goLogin': 'Ir para entrar',
  'invite.join.subtitle': 'Aceite o convite para se juntar a {company} com sua conta atual.',
  'invite.join.cta': 'Aceitar convite',
  'invite.join.joining': 'Entrando…',
  'invite.join.wrongUser':
    'Este convite é para {invited}, mas você está usando {current}. Entre com a conta convidada para aceitá-lo.',
  'invite.join.loginAs': 'Entrar como {email}',
  'invite.joined.title': 'Você entrou em {company}',
  'invite.joined.body': 'Você já faz parte desta empresa. Quer mudar para ela agora?',
  'invite.joined.switch': 'Mudar para {company}',
  'invite.joined.stay': 'Continuar na minha empresa atual',
  'login.invite.notice': 'Entre com sua conta para aceitar o convite.',

  // Perfil
  'profile.title': 'Meu perfil',
  'profile.subtitle': 'Atualize seus dados pessoais.',
  'profile.firstName': 'Nome',
  'profile.lastName': 'Sobrenome',
  'profile.jobTitle': 'Cargo',
  'profile.department': 'Departamento / Área',
  'profile.nationalId': 'Documento',
  'profile.phone': 'Telefone',
  'profile.locale': 'Idioma',
  'profile.locale.es': 'Espanhol',
  'profile.locale.pt': 'Português (Brasil)',
  'profile.timezone': 'Fuso horário',
  'profile.emailLabel': 'E-mail',
  'profile.emailHint': 'Seu e-mail de acesso não pode ser alterado aqui.',
  'profile.submit': 'Salvar alterações',
  'profile.saveError': 'Não foi possível salvar suas alterações.',
  'ai.title': 'Conectar com IA',
  'ai.subtitle': 'Gere um token para operar o Tembiapo pelo Claude, ChatGPT ou Gemini: criar e atribuir tarefas, movê-las, comentar e mais, por linguagem natural.',
  'ai.generate': 'Gerar token para IA',
  'ai.tokenTitle': 'Seu token (copie agora)',
  'ai.tokenWarning': 'Guarde como uma senha. Não será mostrado de novo; se perder, gere um novo.',
  'ai.copy': 'Copiar',
  'ai.copied': 'Copiado!',
  'ai.commandTitle': 'Comando para o Claude Code',
  'ai.commandHint': 'Cole no seu terminal. Depois peça à sua IA: “liste meus projetos” ou “crie uma tarefa…”.',
  'ai.listTitle': 'Tokens ativos',
  'ai.created': 'Criado {date}',
  'ai.lastUsed': 'último uso {date}',
  'ai.never': 'nunca',
  'ai.revoke': 'Revogar',
  'ai.error': 'Não foi possível gerar o token. Tente novamente.',
  'ai.otherAIs': 'Usa ChatGPT ou Gemini?',
  'ai.docsLink': 'Ver o guia de conexão',
  'getApp.title': 'Baixe o app',
  'getApp.subtitle': 'Leve o Tembiapo no seu telefone — disponível para iOS e Android.',
  'getApp.downloadOn': 'Baixar na',
  'getApp.availableOn': 'Disponível no',
  'pairing.title': 'Conectar app móvil',
  'pairing.subtitle': 'Vincule o app do Tembiapo escaneando este QR pelo seu celular. Não precisa de senha no telefone.',
  'pairing.generate': 'Gerar código de vinculação',
  'pairing.regenerate': 'Gerar um novo',
  'pairing.step1': 'Abra o app do Tembiapo no seu celular e toque em “Vincular dispositivo”.',
  'pairing.step2': 'Escaneie este QR (ou digite o código abaixo).',
  'pairing.codeLabel': 'Código',
  'pairing.expiresIn': 'Expira em {time}',
  'pairing.expired': 'O código expirou. Gere um novo.',
  'pairing.error': 'Não foi possível gerar o código. Tente novamente.',
  'password.title': 'Alterar senha',
  'password.subtitle': 'Você precisa da sua senha atual para alterá-la.',
  'password.current': 'Senha atual',
  'password.new': 'Nova senha',
  'password.repeat': 'Repetir nova senha',
  'password.submit': 'Alterar senha',
  'password.saved': 'Senha atualizada.',
  'password.tooShort': 'A senha deve ter pelo menos 8 caracteres.',
  'password.mismatch': 'As senhas não coincidem.',
  'password.invalidCurrent': 'A senha atual está incorreta.',
  'password.same': 'A nova senha deve ser diferente da atual.',
  'password.error': 'Não foi possível alterar a senha. Tente de novo.',
  'profile.avatar.hint': 'JPG ou PNG, até 5 MB.',
  'profile.avatar.uploading': 'Enviando…',
  'profile.avatar.invalidType': 'O arquivo deve ser uma imagem.',
  'profile.avatar.tooBig': 'A imagem ultrapassa 5 MB.',
  'profile.avatar.error': 'Não foi possível enviar sua foto. Tente de novo.',

  // Comentários
  'comments.loadError': 'Não foi possível carregar os comentários.',
  'comments.hint': '@ para mencionar · ⌘/Ctrl + Enter para enviar',
  'comments.saveError': 'Não foi possível publicar seu comentário.',

  // Anexos
  'attachments.loadError': 'Não foi possível carregar os anexos.',
  'attachments.dropHint': 'Arraste arquivos para cá ou',
  'attachments.uploading': 'Enviando…',
  'attachments.error.tooBig': 'O arquivo é muito grande (máx. 25 MB).',
  'attachments.error.invalidType': 'Tipo de arquivo não permitido.',
  'attachments.error.generic': 'Não foi possível enviar o arquivo. Tente de novo.',
  'attachments.error.storageFull': 'Você atingiu o limite de armazenamento do seu plano. Libere espaço ou faça upgrade.',
  'attachment.link.add': 'Adicionar link',
  'attachment.link.url': 'Link (URL)',
  'attachment.link.urlPlaceholder': 'https://drive.google.com/…',
  'attachment.link.name': 'Nome (opcional)',
  'attachment.link.namePlaceholder': 'Documento de requisitos',
  'attachment.link.save': 'Adicionar',
  'attachment.link.invalid': 'Cole uma URL válida que comece com http:// ou https://',
  'attachment.link.badge': 'Link externo',
  'attachment.link.open': 'Abrir link',
  'storage.title': 'Armazenamento',
  'storage.usedOf': 'Você usou {used} de {limit}',
  'storage.full': 'Você atingiu o limite de armazenamento do seu plano.',
  'register.plan.storage': '{size} de arquivos',
  'landing.plans.storage': '{size} de armazenamento',

  // Faturamento (empresa)
  'billing.title': 'Faturamento',
  'billing.subtitle': 'Seu plano, sua assinatura e seus pagamentos.',
  'billing.loadError': 'Não foi possível carregar seu faturamento.',
  'billing.restricted.title': 'Acesso restrito',
  'billing.restricted.body': 'Apenas administradores podem gerenciar o faturamento.',
  'billing.plan.title': 'Seu plano',
  'billing.plan.none': 'Sem plano atribuído',
  'billing.plan.perMonth': '/mês',
  'billing.plan.perYear': '/ano',
  'billing.change.title': 'Mudar de plano',
  'billing.change.subtitle': 'Faça upgrade ou downgrade quando quiser. A mudança é imediata.',
  'billing.change.current': 'Plano atual',
  'billing.change.limits': '{projects} projetos · {users} usuários',
  'billing.change.switchTo': 'Mudar para este plano',
  'billing.change.customNote':
    'Você tem um plano personalizado. Para mudar de plano, fale com a gente e cuidamos disso com você.',
  'billing.change.confirmTo': 'Você vai mudar para o plano {plan}.',
  'billing.change.prorationNote':
    'Será gerado um pagamento pendente de {amount} pela diferença dos dias restantes do período. Você paga abaixo, como qualquer pagamento.',
  'billing.change.downgradeNote':
    'O downgrade é imediato. Não há reembolso nem crédito pelo período já pago.',
  'billing.change.freeNote': 'Sem custo adicional: a mudança é aplicada na hora.',
  'billing.change.confirm': 'Confirmar mudança',
  'billing.change.cancel': 'Cancelar',
  'billing.change.success': 'Pronto, você mudou para o plano {plan}.',
  'billing.change.successProration':
    'Você mudou para o plano {plan}. Geramos um pagamento pendente de {amount} pela diferença — pague abaixo.',
  'billing.change.error': 'Não foi possível mudar o plano. Tente novamente.',
  'billing.change.overLimit':
    'Você não pode fazer downgrade para esse plano: ultrapassa os limites (projetos ou usuários em uso). Reduza o uso ou escolha outro plano.',
  'billing.status.label': 'Status',
  'billing.cycle.label': 'Ciclo de faturamento',
  'billing.cycle.monthly': 'Mensal',
  'billing.cycle.semiannual': 'Semestral',
  'billing.cycle.yearly': 'Anual',
  'billing.period.end': 'Próximo vencimento',
  // Preços por ciclo (modelo por multiplicador: 1/6/12 meses de serviço).
  'pricing.cycle.label': 'Ciclo de faturamento',
  'pricing.cycle.monthly': '1 mês',
  'pricing.cycle.semiannual': '6 meses',
  'pricing.cycle.yearly': '1 ano',
  'pricing.free.one': '1 mês grátis',
  'pricing.free.two': '2 meses grátis',
  'pricing.billed.semiannual': 'Cobrado {total} a cada 6 meses',
  'pricing.billed.yearly': 'Cobrado {total} por ano',
  'billing.trial.left': 'Faltam {days} dias de teste.',
  'billing.trial.lastDay': 'Seu teste termina hoje.',
  'billing.trial.expired': 'Seu teste grátis terminou. Registre um pagamento para reativar sua conta.',
  'billing.pay.title': 'Registrar pagamento',
  'billing.pay.subtitle': 'Envie seu comprovante de transferência ou depósito para validarmos.',
  'billing.pay.cardOnly': 'Pague sua assinatura com cartão de forma segura.',
  'billing.pay.none': 'No momento não há formas de pagamento habilitadas. Fale com o suporte.',
  'billing.pay.method': 'Forma de pagamento',
  'billing.pay.method.transfer': 'Transferência bancária',
  'billing.pay.method.deposit': 'Depósito',
  'billing.pay.method.qr': 'Pagamento com QR',
  'billing.pay.method.card': 'Cartão',
  'billing.pay.method.other': 'Outro',
  'billing.pay.amount': 'Valor',
  'billing.pay.reference': 'Referência / N.º da operação',
  'billing.pay.cycle': 'Ciclo',
  'billing.pay.proof': 'Comprovante',
  'billing.pay.proofHint': 'Imagem ou PDF, até 5 MB.',
  'billing.pay.submit': 'Enviar pagamento',
  'billing.pay.submitting': 'Enviando…',
  'billing.pay.success': 'Recebemos seu pagamento. Vamos validar em breve.',
  'billing.pay.error': 'Não foi possível registrar seu pagamento. Tente de novo.',
  // Pagamento com cartão (hub Bancard via pagos.evoluado.com)
  'billing.card.title': 'Pagar com cartão',
  'billing.card.subtitle': 'Pague na hora com cartão de crédito ou débito em um checkout seguro.',
  'billing.card.pay': 'Pagar com cartão',
  'billing.card.starting': 'Redirecionando…',
  'billing.card.evoluadoNotice': 'A cobrança aparecerá no seu extrato como EVOLUADO.',
  'billing.card.pygNotice': 'O pagamento é processado em guaranis (₲); o preço na sua moeda é apenas uma referência. Seu banco converte para a sua moeda e pode incluir tarifas de compra internacional (no Brasil, o IOF).',
  'billing.card.disabled': 'O pagamento com cartão não está disponível no momento.',
  'billing.card.error': 'Não foi possível iniciar o pagamento, tente de novo.',
  // Retorno do checkout (?pago=<id>)
  'billing.return.verifying': 'Estamos verificando seu pagamento…',
  'billing.return.confirmed': 'Pagamento confirmado! Sua assinatura está ativa.',
  'billing.return.rejected': 'O pagamento não foi concluído. Você pode tentar de novo.',
  'billing.return.dismiss': 'Fechar',
  'billing.history.title': 'Histórico de pagamentos',
  'billing.history.empty': 'Você ainda não registrou pagamentos.',
  'billing.history.date': 'Data',
  'billing.history.amount': 'Valor',
  'billing.history.method': 'Forma',
  'billing.history.status': 'Status',
  'billing.history.proof': 'Comprovante',
  'billing.payStatus.PENDING': 'Pendente',
  'billing.payStatus.CONFIRMED': 'Confirmado',
  'billing.payStatus.REJECTED': 'Recusado',

  // Como pagar (dados de transferência + QR configurados pelo admin)
  'billing.howToPay.title': 'Como pagar',
  'billing.howToPay.subtitle': 'Transfira ou deposite com estes dados e depois registre seu pagamento abaixo.',
  'billing.howToPay.empty': 'Ainda não há dados de pagamento configurados. Fale com o suporte para combinar o pagamento.',
  'billing.howToPay.bank': 'Banco',
  'billing.howToPay.account': 'Número da conta',
  'billing.howToPay.alias': 'Alias',
  'billing.howToPay.holder': 'Titular',
  'billing.howToPay.document': 'CNPJ / CPF',
  'billing.howToPay.instructions': 'Instruções',
  'billing.howToPay.copy': 'Copiar',
  'billing.howToPay.copied': 'Copiado',
  'billing.howToPay.qrLabel': 'Escaneie para pagar',
  'billing.howToPay.qrAlt': 'Código QR de pagamento',

  // Formas de pagamento — painel Super Admin
  'adminPay.title': 'Formas de pagamento',
  'adminPay.subtitle': 'Dados de transferência e QR que as empresas verão ao pagar.',
  'adminPay.loadError': 'Não foi possível carregar as formas de pagamento.',
  'adminPay.toggles.title': 'Métodos habilitados',
  'adminPay.toggles.note': 'Escolha quais formas de pagamento as empresas veem no Faturamento. As alterações são aplicadas na hora.',
  'adminPay.toggle.transfer': 'Transferência / depósito',
  'adminPay.toggle.transferNote': 'Pagamento manual: a empresa envia o comprovante e você confirma.',
  'adminPay.toggle.qr': 'Pagamento por QR',
  'adminPay.toggle.qrNote': 'Mostra a imagem do QR carregado abaixo como opção de pagamento.',
  'adminPay.toggle.card': 'Cartão (gateway)',
  'adminPay.toggle.cardNote': 'Cobrança automática com cartão via gateway. Requer as credenciais do hub em Configurações.',
  'adminPay.section.transfer': 'Dados de transferência',
  'adminPay.section.transferNote': 'São exibidos a todas as empresas na tela de Faturamento.',
  'adminPay.field.bank': 'Banco',
  'adminPay.hint.bank': 'Nome do banco ou financeira.',
  'adminPay.field.account': 'Número da conta',
  'adminPay.hint.account': 'Conta para transferir/depositar.',
  'adminPay.field.alias': 'Alias',
  'adminPay.hint.alias': 'Alias ou apelido da conta (se aplicável).',
  'adminPay.field.holder': 'Titular',
  'adminPay.hint.holder': 'Nome do titular da conta.',
  'adminPay.field.document': 'CNPJ / CPF',
  'adminPay.hint.document': 'Documento do titular (CNPJ ou CPF).',
  'adminPay.field.instructions': 'Instruções',
  'adminPay.hint.instructions': 'Texto livre com passos ou observações para o pagamento.',
  'adminPay.save': 'Salvar dados',
  'adminPay.saved': 'Dados salvos',
  'adminPay.saveError': 'Não foi possível salvar os dados.',
  'adminPay.qr.title': 'QR de pagamento',
  'adminPay.qr.note': 'Imagem do código QR para pagamentos (carteira, banco, etc.).',
  'adminPay.qr.hint': 'PNG, JPG, WEBP ou GIF, até 2 MB.',
  'adminPay.qr.upload': 'Enviar QR',
  'adminPay.qr.replace': 'Substituir QR',
  'adminPay.qr.saved': 'QR atualizado',
  'adminPay.qr.errorType': 'O arquivo deve ser uma imagem.',
  'adminPay.qr.errorSize': 'A imagem excede o máximo de 2 MB.',
  'adminPay.qr.errorUpload': 'Não foi possível enviar a imagem. Tente de novo.',

  // Landing pública (home do site) — PT-BR
  'landing.nav.product': 'Produto',
  'landing.nav.plans': 'Planos',
  'landing.nav.apps': 'Apps',
  'landing.action.login': 'Entrar',
  'landing.action.try': 'Testar grátis',
  'landing.action.start': 'Começar grátis',
  'landing.action.how': 'Ver como funciona',
  'landing.langLabel': 'Idioma',

  'landing.hero.eyebrow': 'Paraguai · Brasil · Tembiapo',
  'landing.hero.titleBefore': 'Sua equipe, seus projetos, em ',
  'landing.hero.titleAccent': 'um só lugar',
  'landing.hero.titleAfter': '.',
  'landing.hero.lede':
    'Gestão de projetos e tarefas, sem complicações. Quadros prontos para o seu setor, para começar hoje.',
  'landing.hero.trialBold': '30 dias grátis',
  'landing.hero.trialRest': ' — sem cartão de crédito',
  'landing.hero.stat.verticals': 'setores prontos',
  'landing.hero.stat.views': 'formas de ver',
  'landing.hero.stat.trial': 'dias grátis',

  'landing.board.todo': 'A fazer',
  'landing.board.doing': 'Em andamento',
  'landing.board.done': 'Concluído',
  'landing.board.tag.mkt': 'Marketing',
  'landing.board.tag.tech': 'Tech',
  'landing.board.card.landing': 'Desenhar landing da campanha',
  'landing.board.card.api': 'Definir esquema da API',
  'landing.board.card.billing': 'Endpoint de faturamento',
  'landing.board.card.content': 'Calendário de conteúdo',
  'landing.board.card.setup': 'Setup do projeto',
  'landing.board.card.brief': 'Brief aprovado',

  'landing.strip.claim': 'Simples de usar. Poderoso por dentro.',
  'landing.strip.kanban': 'Quadros Kanban',
  'landing.strip.plans': 'Planos por setor',
  'landing.strip.apps': 'Apps móveis',

  'landing.features.eyebrow': 'Tudo o que você precisa',
  'landing.features.title': 'Gerencie o trabalho como sua equipe pensa',
  'landing.features.subtitle':
    'Visões visuais, modelos por setor e automações — sem manuais nem curva de aprendizado.',
  'landing.features.views.title': 'Visões visuais',
  'landing.features.views.body': 'Kanban, lista, calendário e timeline. Arraste e solte suas tarefas.',
  'landing.features.agenda.title': 'Agenda com horários',
  'landing.features.agenda.body':
    'Eventos com data, hora e local (audiências, reuniões, consultas, agendamentos), com lembrete e sincronização com Google, Apple ou Outlook.',
  'landing.features.plans.title': 'Planos por setor',
  'landing.features.plans.body': 'Tecnologia, marketing e mais — com projetos já configurados para você.',
  'landing.features.workflows.title': 'Workflows e modelos',
  'landing.features.workflows.body':
    'Estados, transições e automações simples do tipo quando-então.',
  'landing.features.notifications.title': 'Notificações',
  'landing.features.notifications.body': 'No app, por e-mail e push — na web e no celular.',
  'landing.features.files.title': 'Arquivos e imagens',
  'landing.features.files.body': 'Anexe documentos e fotos em cada tarefa arrastando e soltando.',
  'landing.features.mobile.title': 'Apps Android e iPhone',
  'landing.features.mobile.body': 'Leve seus projetos no bolso, com notificações nativas.',

  'landing.plans.eyebrow': 'Planos por vertical',
  'landing.plans.title': 'Escolha o plano do seu setor',
  'landing.plans.subtitle': 'Cada plano vem com quadros e fluxos prontos. Você começa com 30 dias grátis.',
  'landing.plans.perMonth': '/mês',
  'landing.plans.badge': 'Mais escolhido',
  'landing.plans.tech.name': 'Tecnologia',
  'landing.plans.tech.f1': 'Sprints, backlog e bugs',
  'landing.plans.tech.f2': 'Até 10 projetos · 15 usuários',
  'landing.plans.tech.f3': 'Story points e prioridades',
  'landing.plans.mkt.name': 'Marketing',
  'landing.plans.mkt.f1': 'Quadros por campanha e cliente',
  'landing.plans.mkt.f2': 'Até 15 projetos · 25 usuários',
  'landing.plans.mkt.f3': 'Calendário de conteúdo',
  'landing.plans.pyme.name': 'PMEs',
  'landing.plans.pyme.f1': 'Funil de vendas simples',
  'landing.plans.pyme.f2': 'Operações e tarefas do dia a dia',
  'landing.plans.pyme.f3': 'Compras e fornecedores',
  'landing.plans.legal.name': 'Advogados',
  'landing.plans.legal.f1': 'Gestão de casos e processos',
  'landing.plans.legal.f2': 'Controle de prazos e audiências',
  'landing.plans.legal.f3': 'Revisão de contratos',
  'landing.plans.rrhh.name': 'Recursos Humanos',
  'landing.plans.rrhh.f1': 'Recrutamento e seleção',
  'landing.plans.rrhh.f2': 'Admissão e desligamento',
  'landing.plans.rrhh.f3': 'Treinamentos e desempenho',
  'landing.plans.custom.name': 'Personalizado',
  'landing.plans.custom.price': 'Sob medida',
  'landing.plans.custom.f1': 'Sua vertical e modelos próprios',
  'landing.plans.custom.f2': 'Projetos e usuários flexíveis',
  'landing.plans.custom.f3': 'Add-ons e suporte dedicado',
  'landing.plans.customCta': 'Falar com vendas',
  'landing.plans.footnote':
    'Preços de referência em ₲ (Paraguai). Também disponível em R$ (Brasil) e USD.',

  'landing.apps.eyebrow': 'Em todo lugar',
  'landing.ai.badge': 'Novo · Conecte sua IA',
  'landing.ai.title': 'Peça à sua IA que organize o seu trabalho',
  'landing.ai.body':
    'Conecte o Tembiapo ao Claude, ChatGPT ou Gemini e crie, atribua e organize tarefas por linguagem natural — aparecem na hora nos seus quadros. Com um só comando.',
  'landing.ai.cta': 'Como conectar',
  'landing.apps.title': 'Web, Android e iPhone',
  'landing.apps.soon': 'Em breve',
  'landing.apps.live': 'Já disponível no Android e iPhone',
  'landing.apps.androidCta': 'Baixar no Google Play',
  'landing.apps.iosCta': 'Baixar na App Store',
  'landing.apps.iosSoon': 'iPhone · em breve',
  'landing.apps.body':
    'Baixe o Tembiapo para Android e receba notificações onde estiver. Seus quadros, sempre sincronizados.',

  'landing.final.title': 'Comece grátis hoje',
  'landing.final.body': '30 dias sem custo nem cartão. Cadastre sua empresa em minutos.',
  'landing.final.note': 'Ao se cadastrar, você aceita nossos Termos e a Política de Privacidade.',

  'landing.footer.tagline': 'Gestão de projetos simples, para equipes do Paraguai e do Brasil.',
  'landing.footer.product': 'Produto',
  'landing.footer.link.features': 'Funcionalidades',
  'landing.footer.link.plans': 'Planos',
  'landing.footer.link.apps': 'Apps móveis',
  'landing.footer.company': 'Empresa',
  'landing.footer.link.about': 'Sobre nós',
  'landing.footer.link.contact': 'Contato',
  'landing.footer.link.support': 'Suporte',
  'landing.footer.legal': 'Legal',
  'landing.footer.link.privacy': 'Política de Privacidade',
  'landing.footer.link.terms': 'Termos de Uso',
  'landing.footer.link.cookies': 'Política de Cookies',
  'landing.footer.copyright': '© 2026 Tembiapo · des.evoluado.com/tembiapo',
  'landing.footer.madeIn': 'Feito no Paraguai 🇵🇾 — presente no Brasil 🇧🇷 · LGPD & Lei 6534',
  'landing.footer.bancard': 'Pague com cartão',

  // Admin · Configuração da plataforma
  'adminSettings.title': 'Configuração',
  'adminSettings.subtitle':
    'Credenciais da plataforma. O que você salvar aqui vai para o banco e sobrescreve as variáveis de ambiente.',
  'adminSettings.loadError': 'Não foi possível carregar a configuração.',
  'adminSettings.save': 'Salvar',
  'adminSettings.saved': 'Alterações salvas.',
  'adminSettings.saveError': 'Não foi possível salvar a configuração.',
  'adminSettings.testEmail.title': 'Testar o envio',
  'adminSettings.testEmail.label': 'Enviar um e-mail de teste para',
  'adminSettings.testEmail.send': 'Enviar teste',
  'adminSettings.testEmail.sent': '✓ E-mail enviado por SMTP. Confira a caixa de entrada.',
  'adminSettings.testEmail.logMode': 'Sem SMTP configurado: o e-mail ficou no log (não foi enviado).',
  'adminSettings.testEmail.failed': 'Não foi possível enviar',
  'adminSettings.secretPlaceholder': '•••• (deixe vazio para não alterar)',
  'adminSettings.clear': 'Apagar',
  'adminSettings.undo': 'Desfazer',
  'adminSettings.willDelete': 'Será apagado ao salvar (volta para env)',
  'adminSettings.status.set': 'Configurado',
  'adminSettings.status.unset': 'Não definido',
  'adminSettings.source.db': 'DB',
  'adminSettings.source.env': 'env',
  'adminSettings.source.unset': 'Não definido',
  'adminSettings.note.email':
    'SMTP/DSN para envio de e-mails. Sem DSN, os convites ficam registrados no log.',
  'adminSettings.note.push': 'Chaves VAPID para as notificações push (Web Push).',
  'adminSettings.note.payments': 'Credenciais do gateway de pagamento. Para a Fase 4.',
  'adminSettings.note.errors': 'DSN do Sentry para o reporte de erros.',
  'adminSettings.note.auth':
    'Login com Google (OAuth) e proteção anti-bot reCAPTCHA v3. Tudo opcional: se deixar os campos vazios, o botão do Google não aparece e o captcha não é exigido.',

  // Login — painel de marca
  'login.brand.title': 'Todo o seu trabalho, em um só lugar.',
  'login.brand.tagline': 'Gestão de projetos e tarefas para a sua equipe, sem complicações.',
  'login.brand.backHome': 'Voltar ao início',

  // Central de novidades (pública, no login)
  'announcements.title': 'Novidades',
  'announcements.empty': 'Sem novidades por enquanto.',

  // WhatsApp (suporte)
  'whatsapp.support': 'Suporte por WhatsApp',
  'whatsapp.contact': 'Falar pelo WhatsApp',

  // Painel de anúncios (Super Admin)
  'adminAnnouncements.title': 'Anúncios',
  'adminAnnouncements.subtitle': 'Avisos que todos veem na tela de acesso.',
  'adminAnnouncements.new': 'Novo anúncio',
  'adminAnnouncements.newTitle': 'Novo anúncio',
  'adminAnnouncements.editTitle': 'Editar anúncio',
  'adminAnnouncements.empty': 'Ainda não há anúncios.',
  'adminAnnouncements.loadError': 'Não foi possível carregar os anúncios.',
  'adminAnnouncements.saveError': 'Não foi possível salvar o anúncio.',
  'adminAnnouncements.deleteError': 'Não foi possível excluir o anúncio.',
  'adminAnnouncements.confirmDelete': 'Excluir o anúncio "{title}"? Esta ação não pode ser desfeita.',
  'adminAnnouncements.loginNote': 'Os anúncios ativos aparecem na tela de login (públicos, sem sessão).',
  'adminAnnouncements.col.title': 'Anúncio',
  'adminAnnouncements.col.created': 'Publicado',
  'adminAnnouncements.col.active': 'Ativo',
  'adminAnnouncements.col.actions': 'Ações',
  'adminAnnouncements.status.active': 'Visível',
  'adminAnnouncements.status.inactive': 'Oculto',
  'adminAnnouncements.field.title': 'Título',
  'adminAnnouncements.field.body': 'Mensagem',
  'adminAnnouncements.field.ptSection': 'Português (Brasil) — opcional',
  'adminAnnouncements.field.titlePt': 'Título (PT)',
  'adminAnnouncements.field.bodyPt': 'Mensagem (PT)',
  'adminAnnouncements.field.active': 'Ativo',
  'adminAnnouncements.field.activeHint': 'Se estiver ativo, aparece na tela de login.',

  // CTA reutilizável das páginas públicas
  'public.cta.title': 'Comece grátis com o Tembiapo',
  'public.cta.body': '30 dias de teste, sem cartão. Crie sua empresa e seus primeiros quadros em minutos.',
  'public.cta.button': 'Começar grátis',

  // Sobre nós (/sobre-nosotros)
  'about.eyebrow': 'Sobre nós',
  'about.title': 'Trabalho simples, para equipes que fazem as coisas acontecerem',
  'about.lead':
    'Tembiapo (tembiapo = "trabalho" em guarani) é uma ferramenta de gestão de projetos e tarefas pensada para equipes do Paraguai e do Brasil. Nós a construímos com uma ideia fixa: o melhor das ferramentas grandes, sem a complexidade.',
  'about.mission.title': 'Nossa missão',
  'about.mission.body':
    'Ajudar as equipes do Paraguai e do Brasil a gerenciar seus projetos e tarefas sem complicações. Acreditamos que uma ferramenta de trabalho tem que ser entendida em cinco minutos, não em um curso: por isso cuidamos de cada tela para que seja clara, rápida e no seu idioma.',
  'about.verticals.title': 'Modelos por setor',
  'about.verticals.body':
    'Você não começa do zero. O Tembiapo traz quadros e fluxos prontos de acordo com sua atividade — equipes de tecnologia (sprints, backlog e bugs), marketing (campanhas, clientes e calendário de conteúdo) e mais. Você escolhe seu setor no cadastro e já tem um ponto de partida; depois adapta ao seu jeito de trabalhar.',
  'about.values.title': 'No que acreditamos',
  'about.values.body':
    '• Simples primeiro: se algo pode ser mais fácil, nós tornamos mais fácil.\n• Perto de você: suporte de verdade, em espanhol e português, pelo WhatsApp.\n• Seus dados são seus: privacidade e segurança desde o design, com conformidade com a Lei 6534/2020 (Paraguai) e a LGPD (Brasil).\n• Feito na região: pensado para como as equipes do Paraguai e do Brasil trabalham de verdade.',
  'about.who.title': 'Quem somos',
  'about.who.body':
    'O Tembiapo é um produto da GRUPO EVOLUADO EAS (marca Evoluado), agência de tecnologia com sede em Lambaré, Paraguai. Se quiser falar com a gente, estamos em hola@evoluado.com e no WhatsApp +595 992 954 499.',

  // Contato (/contacto)
  'contact.eyebrow': 'Contato',
  'contact.title': 'Vamos conversar',
  'contact.lead':
    'Tem uma dúvida, quer uma demonstração ou precisa de uma mão? Fale com a gente pelo canal que for mais confortável. Respondemos em espanhol e português.',
  'contact.whatsapp.title': 'WhatsApp',
  'contact.whatsapp.body': 'A via mais rápida. Escreva e respondemos no horário de atendimento.',
  'contact.whatsapp.value': '+595 992 954 499',
  'contact.email.title': 'E-mail',
  'contact.email.body': 'Para dúvidas comerciais, suporte ou assuntos de faturamento.',
  'contact.email.value': 'hola@evoluado.com',
  'contact.address.title': 'Onde estamos',
  'contact.address.value':
    'GRUPO EVOLUADO EAS\nMayor Mazo 350, Lambaré\nDepartamento Central, Paraguai',
  'contact.hours.title': 'Horário e tempo de resposta',
  'contact.hours.body':
    'Atendemos de segunda a sexta, das 8h às 18h (horário do Paraguai). Costumamos responder as mensagens no mesmo dia útil.',
  'contact.social.title': 'Redes',
  'contact.social.body':
    'Siga como @evoluado no Instagram e no X, e nos encontre no Facebook e no LinkedIn como Evoluado.',

  // Suporte (/soporte)
  'support.eyebrow': 'Suporte',
  'support.title': 'Central de ajuda',
  'support.lead':
    'Respostas rápidas para as perguntas mais comuns. Se não encontrar o que procura, fale com a gente pelo WhatsApp que a gente ajuda.',
  'support.faq.title': 'Perguntas frequentes',
  'support.faq.q1': 'Como começo a usar o Tembiapo?',
  'support.faq.a1':
    'Crie sua empresa em "Testar grátis", escolha seu setor e seu plano, e preencha seus dados. Em segundos você tem seu espaço com quadros de exemplo prontos para trabalhar.',
  'support.faq.q2': 'O teste grátis tem custo ou pede cartão?',
  'support.faq.a2':
    'Não. O teste dura 30 dias, sem custo e sem cartão de crédito. Ao terminar, você ativa um plano para continuar; enquanto isso, nada é cobrado.',
  'support.faq.q3': 'Como convido minha equipe?',
  'support.faq.a3':
    'Na seção Equipe, toque em "Convidar" e informe o e-mail de cada pessoa. Ela recebe um link para completar a conta. Você pode convidar até o limite de usuários do seu plano.',
  'support.faq.q4': 'Como registro um pagamento?',
  'support.faq.a4':
    'Em Faturamento, escolha "Registrar pagamento", informe o valor e anexe seu comprovante de transferência ou depósito. Nossa equipe valida e ativa seu plano.',
  'support.cta.title': 'Ainda com dúvidas?',
  'support.cta.body':
    'Fale com a gente pelo WhatsApp ou veja todos os nossos meios de contato. Estamos aqui para ajudar.',
  'support.cta.contact': 'Ver meios de contato',

  // ==========================================================================
  // Central de ajuda (/ayuda) — índice + artigos (PT-BR).
  // ==========================================================================
  'help.nav.help': 'Central de ajuda',
  'help.index.eyebrow': 'Central de ajuda',
  'help.index.title': 'Aprenda a aproveitar todo o Tembiapo',
  'help.index.lead':
    'Guias curtos e práticos, passo a passo, para configurar seu espaço e trabalhar com sua equipe. Não encontrou o que procura? Fale com a gente pelo WhatsApp.',
  'help.search.placeholder': 'Buscar na ajuda…',
  'help.search.empty':
    'Não encontramos guias para “{q}”. Tente outras palavras ou fale com a gente pelo WhatsApp.',
  'help.search.results': '{n} resultado(s) para “{q}”',
  'help.backToIndex': 'Voltar à Central de ajuda',
  'help.article.related': 'Mais desta categoria',
  'help.article.allArticles': 'Ver todos os guias',
  'help.article.notFoundTitle': 'Guia não encontrado',
  'help.article.notFoundBody':
    'Talvez o link esteja desatualizado. Volte à Central de ajuda para ver todos os guias disponíveis.',
  'help.callout.tip': 'Dica',
  'help.callout.note': 'Nota',
  'help.callout.warn': 'Atenção',
  'help.cta.title': 'Não resolveu sua dúvida?',
  'help.cta.body':
    'Nossa equipe responde pelo WhatsApp ou pelos demais meios de contato. Você também pode ver as perguntas frequentes em Suporte.',
  'help.cta.support': 'Ir para Suporte',

  // Categorias
  'help.cat.empezar': 'Primeiros passos',
  'help.cat.empezar.desc': 'Crie sua empresa e deixe tudo pronto para trabalhar.',
  'help.cat.proyectos': 'Projetos e quadros',
  'help.cat.proyectos.desc': 'Modelos, Kanban e todas as visões do seu trabalho.',
  'help.cat.colaboracion': 'Equipe e colaboração',
  'help.cat.colaboracion.desc': 'Convide pessoas, comente, mencione e mantenha todos por dentro.',
  'help.cat.productividad': 'Produtividade',
  'help.cat.productividad.desc': 'Automações, dependências e relatórios.',
  'help.cat.cuenta': 'Conta e faturamento',
  'help.cat.cuenta.desc': 'Ative seu plano e gerencie sua assinatura.',
  'help.cat.administracion': 'Administração',
  'help.cat.administracion.desc':
    'Guias do painel de Super Admin: planos, descontos, SEO, anúncios e segurança.',

  // --- Artigo: Primeiros passos ---------------------------------------------
  'help.a.app-movil.title': 'Conectar o app móvil',
  'help.a.app-movil.summary':
    'Leve seus projetos no celular. O app se conecta vinculando o dispositivo com um QR (ou código) do seu painel web — sem senhas — e depois você entra com digital ou rosto.',
  'help.a.app-movil.s1.t': 'Para que serve?',
  'help.a.app-movil.s1.b':
    'O app do Tembiapo permite ver e mover suas tarefas, comentar e receber notificações nativas pelo celular. Para sua segurança, não usa mais usuário e senha no telefone: vincula-se uma vez com sua conta e depois desbloqueia com biometria (digital ou rosto).',
  'help.a.app-movil.s2.t': 'Como conectar (passo a passo)',
  'help.a.app-movil.s2.b':
    '• Na web, entre em Início e toque na ação rápida “Conectar app móvil” (ou vá ao seu Perfil → “Conectar app móvil”).\n\n• É gerado um QR e um código de 6 dígitos.\n\n• Abra o app no celular e, na tela inicial, escaneie o QR com a câmera. Se preferir, digite o código de 6 dígitos.\n\n• Pronto! Você já está no app com sua empresa.',
  'help.a.app-movil.s3.t': 'Entrar com biometria',
  'help.a.app-movil.s3.b':
    'Depois de vincular, o app fica protegido pela biometria do seu celular. Toda vez que você abre, pede sua digital ou rosto para desbloquear — rápido e seguro, sem digitar senhas.',
  'help.a.app-movil.c1':
    'O QR e o código servem uma única vez e expiram em 3 minutos. Se expirar, gere um novo na web com “Gerar um novo”.',
  'help.a.app-movil.c2':
    'Ainda não tem conta? O cadastro da empresa é feito na web (tembiapoapp.com); depois você vincula o app.',
  'help.a.primeros-pasos.title': 'Primeiros passos',
  'help.a.primeros-pasos.summary':
    'Crie sua empresa, escolha seu setor e faça seu primeiro login. Tudo pronto em minutos.',
  'help.a.primeros-pasos.s1.t': 'Crie sua empresa',
  'help.a.primeros-pasos.s1.b':
    'Acesse “Testar grátis” na landing e informe o nome da sua empresa, seu nome e um e-mail. Com isso criamos seu espaço de trabalho e seu usuário administrador. Não precisa de cartão de crédito.',
  'help.a.primeros-pasos.s2.t': 'Escolha seu setor',
  'help.a.primeros-pasos.s2.b':
    'No cadastro você escolhe sua atividade: Tecnologia, Marketing, PME ou Escritório de advocacia, entre outras. Conforme o setor, já deixamos projetos e modelos pensados para você, então não começa do zero.',
  'help.a.primeros-pasos.s3.t': 'O teste de 30 dias',
  'help.a.primeros-pasos.s3.b':
    'Sua conta começa em modo de teste por 30 dias, sem custo e sem cartão. Você pode usar todos os recursos. Quando faltarem poucos dias avisamos, e ao vencer seu espaço fica somente leitura até você ativar um plano.',
  'help.a.primeros-pasos.s4.t': 'Seu primeiro login',
  'help.a.primeros-pasos.s4.b':
    'Você entra com seu e-mail e senha em “Entrar”. Na primeira vez mostramos um tour de 30 segundos com o básico. Você pode repeti-lo quando quiser pelo menu do seu usuário.',
  'help.a.primeros-pasos.c1':
    'Convide sua equipe o quanto antes: trabalhar juntos desde o primeiro dia é a melhor forma de ver o valor do Tembiapo.',

  // --- Artigo: Criar um projeto a partir de um modelo -----------------------
  'help.a.crear-proyecto-plantilla.title': 'Criar um projeto a partir de um modelo',
  'help.a.crear-proyecto-plantilla.summary':
    'Escolha um modelo do seu setor ou comece do zero. Contamos o que cada um copia.',
  'help.a.crear-proyecto-plantilla.s1.t': 'Em branco ou a partir de um modelo',
  'help.a.crear-proyecto-plantilla.s1.b':
    'Ao criar um projeto você escolhe entre começar do zero —um quadro vazio com colunas básicas— ou usar um modelo. O modelo poupa toda a configuração inicial e já deixa você pronto para trabalhar.',
  'help.a.crear-proyecto-plantilla.s2.t': 'A galeria por setor',
  'help.a.crear-proyecto-plantilla.s2.b':
    'A galeria agrupa os modelos conforme seu setor. Cada card mostra para que serve o modelo e o que ele inclui, assim você escolhe o que mais se parece com seu jeito de trabalhar.',
  'help.a.crear-proyecto-plantilla.fig1.alt':
    'Tela de criação de projeto com a galeria de modelos agrupados por setor',
  'help.a.crear-proyecto-plantilla.fig1.cap':
    'Galeria de modelos: escolha por setor ou comece do zero.',
  'help.a.crear-proyecto-plantilla.s3.t': 'O que um modelo copia',
  'help.a.crear-proyecto-plantilla.s3.b':
    '• As colunas do quadro (os estados pelos quais o trabalho passa).\n• Os campos da tarefa: prioridade, responsável, datas e campos próprios do setor.\n• O fluxo de trabalho sugerido e, em alguns casos, tarefas de exemplo para orientar.',
  'help.a.crear-proyecto-plantilla.s4.t': 'Depois de criar',
  'help.a.crear-proyecto-plantilla.s4.b':
    'Você pode renomear o projeto, ajustar as colunas e somar sua equipe a qualquer momento. O modelo é um ponto de partida, não uma jaula.',
  'help.a.crear-proyecto-plantilla.c1':
    'Na dúvida, comece com o modelo mais parecido com seu processo e ajuste. É mais rápido do que montar tudo do zero.',

  // --- Artigo: O quadro Kanban ----------------------------------------------
  'help.a.tablero-kanban.title': 'O quadro Kanban',
  'help.a.tablero-kanban.summary':
    'A visão estrela: colunas, cards e arrastar e soltar para mover seu trabalho.',
  'help.a.tablero-kanban.s1.t': 'Colunas e cards',
  'help.a.tablero-kanban.s1.b':
    'O Kanban mostra seu trabalho em colunas (os estados) e cada tarefa é um card. Num olhar você vê o que há a fazer, o que está em andamento e o que já terminou.',
  'help.a.tablero-kanban.fig1.alt': 'Quadro Kanban com colunas e cards de tarefas',
  'help.a.tablero-kanban.fig1.cap':
    'Quadro Kanban: arraste os cards entre colunas para mudar o estado.',
  'help.a.tablero-kanban.s2.t': 'Adicionar e mover tarefas',
  'help.a.tablero-kanban.s2.b':
    'Toque no botão + de uma coluna para criar uma tarefa. Para mudar o estado, arraste e solte em outra coluna (drag & drop). Você também pode reordenar cards dentro da mesma coluna.',
  'help.a.tablero-kanban.s3.t': 'Prioridade, responsável e data',
  'help.a.tablero-kanban.s3.b':
    '• Prioridade: baixa, média, alta ou urgente, com cor para localizar rápido.\n• Responsável: você atribui a um membro da equipe e o avatar dele aparece no card.\n• Data de vencimento: os cards vencidos ou próximos do prazo são marcados para nada passar batido.',
  'help.a.tablero-kanban.s4.t': 'Editar colunas e limite WIP',
  'help.a.tablero-kanban.s4.b':
    'Você pode adicionar, renomear, reordenar ou mudar a cor das colunas. Além disso, cada coluna admite um limite WIP (trabalho em andamento): se ultrapassar, ela avisa para não sobrecarregar a equipe.',
  'help.a.tablero-kanban.s5.t': 'Indicador de dependência',
  'help.a.tablero-kanban.s5.b':
    'Se uma tarefa está bloqueada por outra, o card mostra um indicador de dependência. Assim você sabe o que não pode avançar até liberar aquilo que a bloqueia.',
  'help.a.tablero-kanban.c1':
    'O limite WIP é um guia, não um cadeado: ele avisa, mas você decide. Menos tarefas em andamento de cada vez costuma significar terminar mais rápido.',

  // --- Artigo: Outras visões ------------------------------------------------
  'help.a.otras-vistas.title': 'Outras visões: Lista, Calendário e Timeline',
  'help.a.otras-vistas.summary':
    'O mesmo trabalho, olhares diferentes. Escolha a visão que melhor serve em cada momento.',
  'help.a.otras-vistas.s1.t': 'Visão Lista / Tabela',
  'help.a.otras-vistas.s1.b':
    'A Lista mostra suas tarefas em linhas, ideal para revisar muita coisa de uma vez.\n\n• Filtre por responsável, prioridade, estado ou data.\n• Ordene por qualquer coluna.\n• Agrupe por estado, responsável ou prioridade.\n• Salve suas combinações como filtros salvos para reusar com um clique.',
  'help.a.otras-vistas.fig1.alt': 'Visão de Lista ou Tabela com filtros e agrupamento de tarefas',
  'help.a.otras-vistas.fig1.cap': 'Visão Lista: filtre, ordene, agrupe e salve seus filtros.',
  'help.a.otras-vistas.s2.t': 'Visão Calendário',
  'help.a.otras-vistas.s2.b':
    'O Calendário posiciona suas tarefas pela data de vencimento. Arraste uma tarefa para outro dia para mudar a data sem nem abri-la.',
  'help.a.otras-vistas.fig2.alt': 'Visão de Calendário com tarefas posicionadas por data',
  'help.a.otras-vistas.fig2.cap':
    'Calendário: arraste uma tarefa para outro dia para reprogramar.',
  'help.a.otras-vistas.s3.t': 'Visão Timeline / Gantt',
  'help.a.otras-vistas.s3.b':
    'O Timeline mostra a duração das tarefas no tempo e como elas se relacionam. Serve para planejar e ver num olhar o que se sobrepõe ou o que depende de quê.',
  'help.a.otras-vistas.fig3.alt': 'Visão Timeline ou Gantt com a duração das tarefas',
  'help.a.otras-vistas.fig3.cap': 'Timeline: planeje e visualize dependências no tempo.',
  'help.a.otras-vistas.c1':
    'Todas as visões trabalham sobre os mesmos dados: o que você muda em uma se reflete nas outras na hora.',

  // --- Artigo: Tarefas: comentários, menções e anexos -----------------------
  'help.a.tareas-colaboracion.title': 'Tarefas: comentários, menções e anexos',
  'help.a.tareas-colaboracion.summary':
    'Converse dentro de cada tarefa, chame quem for preciso e anexe arquivos.',
  'help.a.tareas-colaboracion.s1.t': 'Comentários',
  'help.a.tareas-colaboracion.s1.b':
    'Abra uma tarefa e escreva em Comentários para deixar contexto, decisões ou novidades. Tudo fica registrado em ordem cronológica, para a equipe ter sempre a história completa.',
  'help.a.tareas-colaboracion.s2.t': 'Menções com @',
  'help.a.tareas-colaboracion.s2.b':
    'Escreva @ seguido do e-mail de um colega para mencioná-lo. Ele recebe uma notificação com o link direto para a tarefa, mesmo que não seja o responsável.',
  'help.a.tareas-colaboracion.s3.t': 'Anexos',
  'help.a.tareas-colaboracion.s3.b':
    'Suba arquivos —documentos, imagens, comprovantes— pela tarefa. Eles ficam associados a ela para a equipe ter sempre à mão.',
  'help.a.tareas-colaboracion.c1':
    'Use as menções em vez de chats soltos: o contexto fica colado à tarefa e qualquer um encontra depois.',

  // --- Artigo: Equipe e convites --------------------------------------------
  'help.a.equipo-invitaciones.title': 'Equipe e convites',
  'help.a.equipo-invitaciones.summary':
    'Convide sua equipe por e-mail, defina papéis e controle o limite do seu plano.',
  'help.a.equipo-invitaciones.s1.t': 'Convidar por e-mail',
  'help.a.equipo-invitaciones.s1.b':
    'Em Equipe, toque em “Convidar” e informe o e-mail de cada pessoa. Ela recebe um link para criar a senha e entrar na sua empresa.',
  'help.a.equipo-invitaciones.fig1.alt': 'Página de Equipe com a lista de membros e convites',
  'help.a.equipo-invitaciones.fig1.cap':
    'Página de Equipe: convide, reenvie convites e gerencie papéis.',
  'help.a.equipo-invitaciones.s2.t': 'Papéis: administrador e membro',
  'help.a.equipo-invitaciones.s2.b':
    '• Administrador: gerencia a equipe, o faturamento e a configuração, além de trabalhar nos projetos.\n• Membro: trabalha nos projetos —cria e move tarefas, comenta— sem acesso ao faturamento nem à gestão da equipe.',
  'help.a.equipo-invitaciones.s3.t': 'Aceitar um convite',
  'help.a.equipo-invitaciones.s3.b':
    'A pessoa convidada abre o link do e-mail, define a senha e já entra no seu espaço. Se o link venceu, reenvie o convite em Equipe.',
  'help.a.equipo-invitaciones.s4.t': 'Limite do plano',
  'help.a.equipo-invitaciones.s4.b':
    'Você pode convidar até o máximo de usuários do seu plano, contando os convites pendentes. Se precisar de mais, adicione um add-on ou mude de plano em Faturamento.',
  'help.a.equipo-invitaciones.c1':
    'Os convites pendentes contam para o limite. Se ficou sem vagas, cancele os que não foram usados ou amplie seu plano.',

  // --- Artigo: Automações ---------------------------------------------------
  'help.a.conectar-ia.title': 'Conectar o Tembiapo com sua IA (Claude, ChatGPT, Gemini)',
  'help.a.conectar-ia.summary':
    'Opere o Tembiapo por linguagem natural pela sua IA: criar e atribuir tarefas, movê-las, comentar e usar modelos — tudo dentro da sua empresa.',
  'help.a.conectar-ia.s1.t': 'O que é e para que serve?',
  'help.a.conectar-ia.s1.b':
    'O Tembiapo se conecta a assistentes de IA pelo padrão MCP. Depois de conectar, você pode pedir à sua IA coisas como “crie uma tarefa ‘Ligar para o fornecedor’ com prioridade alta no projeto Vendas”, “o que vence esta semana?” ou “mova a tarefa X para Concluído”, e é executado no seu Tembiapo. Tudo fica restrito à sua empresa.',
  'help.a.conectar-ia.s2.t': 'Passo 1: gere seu token',
  'help.a.conectar-ia.s2.b':
    'Vá ao seu Perfil → seção “Conectar com IA” → toque em “Gerar token para IA”. É exibido um token (começa com tbmcp_) uma única vez: copie e guarde como uma senha. Você pode revogá-lo quando quiser na mesma seção.',
  'help.a.conectar-ia.s3.t': 'Passo 2: conecte sua IA',
  'help.a.conectar-ia.s3.b':
    '• Claude Code (terminal): cole o comando que o app mostra —\n  claude mcp add tembiapo --scope user --transport http https://www.tembiapoapp.com/api/mcp --header "Authorization: Bearer SEU_TOKEN"\n\n• Gemini CLI: gemini mcp add tembiapo --transport http https://www.tembiapoapp.com/api/mcp --header "Authorization: Bearer SEU_TOKEN"\n\n• ChatGPT: em Configurações → Conectores, adicione um conector com a URL https://www.tembiapoapp.com/api/mcp e o header Authorization: Bearer SEU_TOKEN.',
  'help.a.conectar-ia.c1':
    'Exemplos para pedir à sua IA: “liste meus projetos”, “crie 5 tarefas de onboarding no projeto Lançamento”, “atribua a tarefa X para joao@empresa.com”, “comente ‘revisar amanhã’ na tarefa Y”.',
  'help.a.conectar-ia.c2':
    'Por segurança, a IA NÃO apaga tarefas nem mexe em faturamento/planos/membros: se pedir para apagar algo, ela devolve o link para fazer isso na web. O token é pessoal e revogável.',
  'help.a.automatizaciones.title': 'Automações (quando → então)',
  'help.a.automatizaciones.summary':
    'Deixe o Tembiapo fazer as tarefas repetitivas por você com regras simples.',
  'help.a.automatizaciones.s1.t': 'Como funcionam',
  'help.a.automatizaciones.s1.b':
    'Uma automação é uma regra com duas partes: um gatilho (quando algo acontece) e uma ação (então faça isto). Elas rodam sozinhas, sem você precisar lembrar.',
  'help.a.automatizaciones.s2.t': 'Exemplos úteis',
  'help.a.automatizaciones.s2.b':
    '• Quando uma tarefa vai para “Concluído” → notificar o responsável do projeto.\n• Quando uma tarefa entra em “Em revisão” → atribuí-la a quem revisa.\n• Quando é marcada como “Urgente” → subir a prioridade e avisar por notificação.',
  'help.a.automatizaciones.s3.t': 'Criar e ativar',
  'help.a.automatizaciones.s3.b':
    'Você define o gatilho, escolhe a ação e ativa. Pode ter várias regras por projeto e desativá-las quando não precisar.',
  'help.a.automatizaciones.c1':
    'Comece com uma ou duas regras simples: automatize aquilo que hoje você faz na mão e sempre esquece.',

  // --- Artigo: Modelos de tarefa e dependências -----------------------------
  'help.a.plantillas-dependencias.title': 'Modelos de tarefa e dependências',
  'help.a.plantillas-dependencias.summary':
    'Reutilize tarefas que se repetem e organize o que vem antes do quê.',
  'help.a.plantillas-dependencias.s1.t': 'Salvar uma tarefa como modelo',
  'help.a.plantillas-dependencias.s1.b':
    'Se uma tarefa, com seus campos e sua checklist, se repete com frequência, salve-a como modelo. Depois você a cria de novo em um clique, já configurada.',
  'help.a.plantillas-dependencias.s2.t': 'Dependências: “bloqueada por”',
  'help.a.plantillas-dependencias.s2.b':
    'Marque que uma tarefa está “bloqueada por” outra quando ela não pode avançar até a primeira terminar. No Kanban você verá o indicador de dependência no card.',
  'help.a.plantillas-dependencias.s3.t': 'Por que importa',
  'help.a.plantillas-dependencias.s3.b':
    'As dependências evitam que alguém comece algo que ainda não pode ser feito e, no Timeline, mostram o caminho crítico do projeto.',
  'help.a.plantillas-dependencias.c1':
    'Evite dependências circulares (A bloqueia B e B bloqueia A): ninguém conseguiria avançar. O Tembiapo avisa se você tentar criar uma.',

  // --- Artigo: Notificações -------------------------------------------------
  'help.a.notificaciones.title': 'Notificações',
  'help.a.notificaciones.summary':
    'Fique por dentro do importante pelo sininho, e-mail e push, do seu jeito.',
  'help.a.notificaciones.s1.t': 'O sininho',
  'help.a.notificaciones.s1.b':
    'O ícone de sino na barra superior mostra menções, atribuições e vencimentos. O número indica quantas notificações você não leu.',
  'help.a.notificaciones.s2.t': 'E-mail e push (PWA)',
  'help.a.notificaciones.s2.b':
    'Além do sininho, você pode receber avisos por e-mail e notificações push. Se instalar o Tembiapo como app (PWA) no celular, as push chegam mesmo com ele fechado.',
  'help.a.notificaciones.s3.t': 'Preferências',
  'help.a.notificaciones.s3.b':
    'Em Preferências você escolhe quais eventos notificam e por qual canal. Assim recebe só o que importa, sem ruído.',
  'help.a.notificaciones.c1':
    'Instale o Tembiapo como app no celular para receber push: é a forma mais rápida de não perder uma menção ou um vencimento.',

  // --- Artigo: Relatórios ---------------------------------------------------
  'help.a.reportes.title': 'Relatórios',
  'help.a.reportes.summary':
    'Veja como o trabalho avança: por prioridade, responsável, projeto e vencidas.',
  'help.a.reportes.s1.t': 'O que você vai ver',
  'help.a.reportes.s1.b':
    'O painel de Relatórios resume o estado do seu trabalho com métricas e gráficos, sem você precisar montar planilhas na mão.',
  'help.a.reportes.fig1.alt': 'Painel de Relatórios com métricas e gráficos',
  'help.a.reportes.fig1.cap':
    'Dashboard de Relatórios: métricas por prioridade, responsável e projeto.',
  'help.a.reportes.s2.t': 'Métricas principais',
  'help.a.reportes.s2.b':
    '• Por prioridade: quantas tarefas há em cada nível.\n• Por responsável: a carga de cada pessoa da equipe.\n• Por projeto: avanço e volume de trabalho.\n• Vencidas: o que passou do prazo e precisa de atenção.',
  'help.a.reportes.s3.t': 'Para que serve',
  'help.a.reportes.s3.b':
    'Use nas suas reuniões para decidir onde focar, distribuir melhor a carga e detectar gargalos a tempo.',

  // --- Artigo: Faturamento: ativar seu plano --------------------------------
  'help.a.facturacion.title': 'Faturamento: ativar seu plano',
  'help.a.facturacion.summary':
    'Registre seu pagamento com comprovante e acompanhe o estado da sua assinatura.',
  'help.a.facturacion.s1.t': 'Quando ativar',
  'help.a.facturacion.s1.b':
    'Durante o teste de 30 dias você não paga nada. Para continuar depois do trial —ou para ampliar limites antes— ative um plano em Faturamento.',
  'help.a.facturacion.s2.t': 'Registrar um pagamento',
  'help.a.facturacion.s2.b':
    'Escolha “Registrar pagamento”, informe o valor e o plano, e anexe seu comprovante de transferência ou depósito. Nossa equipe valida e ativa sua assinatura.',
  'help.a.facturacion.s3.t': 'Estados da assinatura',
  'help.a.facturacion.s3.b':
    '• Teste: você usa tudo por 30 dias, sem custo.\n• Pagamento pendente: você registrou o pagamento e estamos validando.\n• Ativa: seu plano está em dia.\n• Vencida: o teste ou o período terminou; seu espaço fica somente leitura até regularizar.',
  'help.a.facturacion.c1':
    'Enquanto validamos seu comprovante você segue trabalhando normalmente. Se o trial venceu, assim que confirmarmos o pagamento a escrita é reativada.',

  // --- Artigo: Modelos por setor --------------------------------------------
  'help.a.plantillas-por-rubro.title': 'Modelos por setor',
  'help.a.plantillas-por-rubro.summary':
    'Exemplos de fluxo prontos para Tecnologia, Marketing, PMEs e Escritórios de advocacia.',
  'help.a.plantillas-por-rubro.fig1.alt':
    'Galeria de modelos de projeto agrupados por setor',
  'help.a.plantillas-por-rubro.fig1.cap':
    'Escolha o modelo do seu setor na galeria ao criar um projeto.',
  'help.a.plantillas-por-rubro.s1.t': 'Tecnologia',
  'help.a.plantillas-por-rubro.s1.b':
    'Pensado para times de software: um fluxo tipo Backlog → Em progresso → Em revisão → Concluído, com prioridade, responsável e estimativas. Ideal para sprints e para acompanhar features e bugs.',
  'help.a.plantillas-por-rubro.s2.t': 'Marketing',
  'help.a.plantillas-por-rubro.s2.b':
    'Para campanhas e conteúdo: Ideias → Em produção → Em aprovação → Publicado. Some datas de publicação e um responsável por peça para nada atrasar.',
  'help.a.plantillas-por-rubro.s3.t': 'PMEs — Pipeline de vendas',
  'help.a.plantillas-por-rubro.s3.b':
    'Acompanhamento comercial: Prospecto → Contatado → Proposta → Negociação → Ganho/Perdido. Cada card é uma oportunidade com seu valor e seu responsável.',
  'help.a.plantillas-por-rubro.s4.t': 'Advogados — Gestão de casos',
  'help.a.plantillas-por-rubro.s4.b':
    'Para escritórios de advocacia: acompanhe cada processo por seus estados processuais (Entrada → Em andamento → Instrução → Sentença → Arquivado), com prazos e vencimentos-chave para não perder nenhum termo, e os dados do processo sempre à mão.',
  'help.a.plantillas-por-rubro.c1':
    'Qualquer modelo é editável: pegue o mais próximo do seu processo e ajuste colunas, campos e fluxo ao seu gosto.',

  // --- Artigo: Renomear e excluir projetos ----------------------------------
  'help.a.gestionar-proyectos.title': 'Renomear e excluir projetos',
  'help.a.gestionar-proyectos.summary':
    'Altere o nome e a descrição de um projeto, ou exclua-o quando não precisar mais dele.',
  'help.a.gestionar-proyectos.s1.t': 'Renomear a partir de Projetos',
  'help.a.gestionar-proyectos.s1.b':
    'Na lista de Projetos, cada card tem um menu de três pontos (⋯). Abra e escolha “Renomear” para alterar o nome e a descrição do projeto. As mudanças são aplicadas na hora e não afetam as tarefas nem as colunas.',
  'help.a.gestionar-proyectos.s2.t': 'Renomear a partir do quadro',
  'help.a.gestionar-proyectos.s2.b':
    'Se você já está dentro de um projeto, tem o botão “Renomear” no cabeçalho do quadro. É o mesmo formulário: ajusta nome e descrição sem sair de onde está trabalhando.',
  'help.a.gestionar-proyectos.s3.t': 'Excluir um projeto',
  'help.a.gestionar-proyectos.s3.b':
    'A opção “Excluir” está no mesmo menu de três pontos (⋯), mas só os administradores da empresa a veem. Ao escolhê-la pedimos uma confirmação, e só então o projeto é apagado junto com todas as suas tarefas, colunas e comentários.',
  'help.a.gestionar-proyectos.c1':
    'Excluir um projeto é permanente: não dá para desfazer e as tarefas também são apagadas. Se tiver dúvida, renomeie-o ou faça uma cópia do conteúdo antes de excluir.',

  // --- Artigo: Subtarefas dentro de uma tarefa ------------------------------
  'help.a.subtareas.title': 'Subtarefas dentro de uma tarefa',
  'help.a.subtareas.summary':
    'Divida uma tarefa em passos, atribua responsáveis e marque os que são obrigatórios.',
  'help.a.subtareas.s1.t': 'Criar e concluir subtarefas',
  'help.a.subtareas.s1.b':
    'Abra uma tarefa e adicione subtarefas para dividir o trabalho em passos menores. Cada subtarefa pode ser marcada como feita com um clique, assim você vê num relance quanto falta para concluir a tarefa inteira.',
  'help.a.subtareas.s2.t': 'Responsável e subtarefas obrigatórias',
  'help.a.subtareas.s2.b':
    '• Responsável: você atribui cada subtarefa a uma pessoa da equipe, para deixar claro quem faz o quê.\n• Obrigatória: marque uma subtarefa como obrigatória quando for um passo que não pode ser pulado antes de a tarefa avançar.',
  'help.a.subtareas.s3.t': 'Como funciona o bloqueio',
  'help.a.subtareas.s3.b':
    'Uma subtarefa obrigatória bloqueia o avanço da tarefa até que você a conclua (ou ignore). Depois que a tarefa avançou, uma obrigatória já concluída não pode ser desmarcada; se precisar corrigir, mova a tarefa para trás e ali edite.',
  'help.a.subtareas.s4.t': 'Para qual passo é obrigatória',
  'help.a.subtareas.s4.b':
    'Ao marcar uma subtarefa como obrigatória, você escolhe “para passar para…” uma coluna (por padrão, a última — “Concluído”). Bloqueia entrar NESSA coluna e nas seguintes, mas NÃO nas anteriores. Assim, uma obrigatória “para finalizar” não impede começar a tarefa, só encerrá-la. Exemplo: “Aprovação do cliente” obrigatória para passar para “Concluído”.',
  'help.a.subtareas.s5.t': 'Se não der para concluir: ignorar',
  'help.a.subtareas.s5.b':
    'Se uma obrigatória travou e não dá para fazer, um administrador ou o responsável pela tarefa pode tocar em “Ignorar” e escrever um motivo. A tarefa é desbloqueada e fica registrado quem ignorou e por quê. Pode ser reativada depois.',
  'help.a.subtareas.c1':
    'Use as obrigatórias para seus passos de controle de qualidade: “revisado”, “aprovado”, “publicado”. Assim nada avança sem cumprir o que é importante.',

  // --- Artigo: O painel inicial ---------------------------------------------
  'help.a.panel-inicio.title': 'O painel inicial',
  'help.a.panel-inicio.summary':
    'Sua tela de partida: ações rápidas, novidades da equipe e contatos à mão.',
  'help.a.panel-inicio.s1.t': 'Ações rápidas',
  'help.a.panel-inicio.s1.b':
    'Ao entrar, você cai no painel inicial. Dali faz o mais comum com um toque: criar um novo projeto ou convidar alguém para a sua equipe, sem precisar procurar o menu.',
  'help.a.panel-inicio.s2.t': 'Novidades: comentários e mudanças de estado',
  'help.a.panel-inicio.s2.b':
    '• Últimos comentários: as mensagens mais recentes das suas tarefas, para retomar a conversa na hora.\n• Últimas mudanças de estado: quais tarefas mudaram de coluna e quem as moveu, assim você acompanha o avanço do trabalho sem abrir cada projeto.',
  'help.a.panel-inicio.s3.t': 'Sua equipe à vista',
  'help.a.panel-inicio.s3.b':
    'O card de equipe mostra seus colegas com foto e dados de contato. É a forma mais rápida de saber quem está na sua empresa e como encontrá-lo.',
  'help.a.panel-inicio.c1':
    'Volte ao início quando começar o dia: num relance você vê o que aconteceu por último e por onde continuar.',

  // --- Artigo: Mudar o idioma -----------------------------------------------
  'help.a.cambiar-idioma.title': 'Mudar o idioma',
  'help.a.cambiar-idioma.summary':
    'Escolha entre espanhol e português na barra superior. Sua preferência fica salva.',
  'help.a.cambiar-idioma.s1.t': 'Espanhol ou português',
  'help.a.cambiar-idioma.s1.b':
    'Na barra superior você tem o seletor de idioma. Escolha espanhol (ES) ou português (PT) e todo o aplicativo muda na hora. Sua escolha fica salva no seu perfil, então na próxima vez que entrar já estará no idioma que você prefere.',
  'help.a.cambiar-idioma.s2.t': 'A moeda é à parte',
  'help.a.cambiar-idioma.s2.b':
    'O idioma não muda a moeda dos preços. A moeda (₲ guaranis, R$ reais ou USD) é escolhida na seção de Planos, quando você olha ou ativa sua assinatura.',
  'help.a.cambiar-idioma.c1':
    'Cada pessoa da equipe escolhe seu próprio idioma: a mudança afeta só a sua conta, não a dos seus colegas.',

  // --- Artigo: Descontos de planos (admin) ----------------------------------
  'help.a.admin-descuentos.title': 'Descontos de planos',
  'help.a.admin-descuentos.summary':
    'Configure o desconto anual e uma promo de lançamento em Super Admin → Planos.',
  'help.a.admin-descuentos.s1.t': 'Desconto anual',
  'help.a.admin-descuentos.s1.b':
    'Em Super Admin → Planos você define um desconto anual global, em porcentagem. Ele é aplicado quando o cliente escolhe pagar por ano em vez de mês a mês, e é o clássico incentivo para que se comprometa por mais tempo.',
  'help.a.admin-descuentos.s2.t': 'Promo de lançamento',
  'help.a.admin-descuentos.s2.b':
    'Além disso, você pode ativar uma promo de lançamento por tempo limitado. Você configura:\n• Se está ativa ou não.\n• A porcentagem de desconto.\n• A data de término da promo.\n• Uma etiqueta (por exemplo “Lançamento” ou “Oferta”) que aparece junto ao preço.',
  'help.a.admin-descuentos.s3.t': 'Como se combinam e onde aparecem',
  'help.a.admin-descuentos.s3.b':
    'O desconto anual e a promo de lançamento se acumulam: se o cliente paga por ano durante a promo, recebe os dois. Os preços já reduzidos aparecem automaticamente na landing e na tela de cadastro, sem você precisar mexer em mais nada.',
  'help.a.admin-descuentos.c1':
    'Coloque sempre uma data de término na promo de lançamento: a urgência ajuda a converter, e você evita deixar uma oferta ligada mais do que o previsto.',

  // --- Artigo: SEO e campanhas (admin) --------------------------------------
  'help.a.admin-seo.title': 'SEO e campanhas (SEM)',
  'help.a.admin-seo.summary':
    'Metadados por idioma, verificação de buscadores e IDs de analytics, tudo no menu SEO.',
  'help.a.admin-seo.s1.t': 'Metadados do site',
  'help.a.admin-seo.s1.b':
    'No menu SEO você preenche os metadados com os quais seu site aparece nos buscadores e ao compartilhar links:\n• Título e descrição, por idioma (espanhol e português).\n• Imagem e textos para redes sociais (Open Graph).\n• URL canônica, para indicar o endereço oficial de cada página.',
  'help.a.admin-seo.s2.t': 'Verificação de buscadores',
  'help.a.admin-seo.s2.b':
    'Você pode colar os códigos de verificação do Google e do Bing para provar que é o dono do site. Isso habilita o uso dos painéis de webmaster deles (Search Console e Bing Webmaster Tools) e ver como encontram você.',
  'help.a.admin-seo.s3.t': 'Analytics e campanhas',
  'help.a.admin-seo.s3.b':
    'Para medir visitas e campanhas você preenche os identificadores das suas ferramentas: Google Analytics 4 (GA4), Google Tag Manager (GTM), Meta Pixel (Facebook/Instagram) e Google Ads. Uma vez preenchidos, o site os injeta sozinho, sem mexer em código.',
  'help.a.admin-seo.c1':
    'Se você for verificar o Search Console, vale fazer isso pelo GA4 ou GTM: se já preencheu esses IDs, a verificação é direta e você não precisa colar outro código à parte.',

  // --- Artigo: Anúncios do login (admin) ------------------------------------
  'help.a.admin-anuncios.title': 'Anúncios do login',
  'help.a.admin-anuncios.summary':
    'Mostre avisos na tela de entrada, com versão em espanhol e português.',
  'help.a.admin-anuncios.s1.t': 'O que são os anúncios do login',
  'help.a.admin-anuncios.s1.b':
    'Os anúncios são avisos que aparecem na tela de entrada, na central de anúncios ao lado do formulário de login. Servem para comunicar novidades, manutenções ou promoções a todos que entram, antes mesmo de iniciar a sessão.',
  'help.a.admin-anuncios.s2.t': 'Criar e editar avisos bilíngues',
  'help.a.admin-anuncios.s2.b':
    'Em Super Admin você cria ou edita cada anúncio com sua versão em espanhol e, opcionalmente, sua versão em português. Se você não preencher a versão em português, é mostrada a de espanhol como reserva.',
  'help.a.admin-anuncios.s3.t': 'Qual versão cada pessoa vê',
  'help.a.admin-anuncios.s3.b':
    'Cada visitante vê o anúncio no seu idioma: quem navega em português vê a versão PT, e o restante vê a versão em espanhol. Assim o mesmo aviso chega bem redigido aos dois mercados.',
  'help.a.admin-anuncios.c1':
    'Escreva sempre as duas versões para suas campanhas importantes: um aviso no idioma certo é muito mais lido do que um traduzido pela metade.',

  // --- Artigo: Mudar a senha do super admin (admin) -------------------------
  'help.a.admin-seguridad.title': 'Mudar a senha do super admin com PIN',
  'help.a.admin-seguridad.summary':
    'Uma troca de senha protegida por um PIN que chega ao WhatsApp de controle.',
  'help.a.admin-seguridad.s1.t': 'Iniciar a troca',
  'help.a.admin-seguridad.s1.b':
    'Como super admin, você entra na configuração de segurança e informa sua senha atual e a nova que quer usar. Por ser uma conta sensível, isso não basta: ativamos uma verificação extra antes de aplicar a mudança.',
  'help.a.admin-seguridad.s2.t': 'O PIN pelo WhatsApp',
  'help.a.admin-seguridad.s2.b':
    'Ao confirmar, enviamos um PIN para o número de WhatsApp de controle configurado para a plataforma. Esse PIN é a prova de que é você: ninguém que não tenha acesso a esse WhatsApp consegue concluir a troca.',
  'help.a.admin-seguridad.s3.t': 'Confirmar com o PIN',
  'help.a.admin-seguridad.s3.b':
    'Você informa o PIN que recebeu e só então salvamos a nova senha. A partir desse momento, seu próximo acesso é com a senha nova.',
  'help.a.admin-seguridad.c1':
    'O PIN expira em poucos minutos e você tem uma quantidade limitada de tentativas. Se ele venceu ou você errou várias vezes, comece a troca de novo para enviarmos um novo.',

  'help.a.varias-empresas.title': 'Trabalhar em várias empresas',
  'help.a.varias-empresas.summary':
    'Faça parte da empresa do seu cliente além da sua, escolha em qual trabalhar ao entrar e troque quando quiser.',
  'help.a.varias-empresas.s1.t': 'Como funciona?',
  'help.a.varias-empresas.s1.b':
    'Uma mesma pessoa pode pertencer a várias empresas: a sua e a de um cliente, por exemplo. Sua conta é uma só, mas cada empresa é independente e seus dados ficam isolados: nunca se misturam. Seu papel pode ser diferente em cada uma.',
  'help.a.varias-empresas.s2.t': 'Escolher empresa ao entrar',
  'help.a.varias-empresas.s2.b':
    'Se você pertence a mais de uma empresa, ao entrar perguntamos “Em qual empresa você vai trabalhar?” e você escolhe uma. Se estiver em só uma, entra direto como sempre.',
  'help.a.varias-empresas.s3.t': 'Trocar de empresa',
  'help.a.varias-empresas.s3.b':
    'No topo à esquerda, ao lado do logo, aparece “Trabalhando em: [Empresa] ▾”. Toque para trocar de empresa sem sair da conta: ao trocar, você vê os projetos, tarefas e equipe daquela empresa.',
  'help.a.varias-empresas.s4.t': 'Convidar alguém que já usa o Tembiapo',
  'help.a.varias-empresas.s4.b':
    'Você pode convidar por e-mail alguém que já tem conta no Tembiapo. Em vez de falhar, essa pessoa é adicionada à sua empresa: quando ela entra com a conta dela, a sua empresa aparece para escolher e trocar. Não precisa se cadastrar de novo.',
  'help.a.varias-empresas.s5.t': 'Remover um membro',
  'help.a.varias-empresas.s5.b':
    'Em Equipe, um administrador pode tocar em “Remover” ao lado de um membro para tirá-lo desta empresa. A conta e as outras empresas dele não são afetadas: ele só perde acesso a esta. Você não pode se remover nem remover o último administrador.',
  'help.a.varias-empresas.c1':
    'Seu papel é por empresa: você pode ser administrador na sua e membro na do seu cliente. Cada empresa vê apenas os próprios dados.',

  'help.a.equipo-permisos.title': 'Equipe e permissões do projeto',
  'help.a.equipo-permisos.summary':
    'Quem pode criar projetos, quem edita e quem só vê. Adicione pessoas à equipe de cada projeto.',
  'help.a.equipo-permisos.s1.t': 'A equipe do projeto',
  'help.a.equipo-permisos.s1.b':
    'Cada projeto tem sua própria equipe: as pessoas que podem trabalhar nele (criar e editar tarefas), sem que isso signifique ser responsável por nada. No botão “Equipe” do quadro, um administrador ou qualquer integrante pode adicionar ou remover pessoas da sua empresa.',
  'help.a.equipo-permisos.s2.t': 'Quem pode criar projetos',
  'help.a.equipo-permisos.s2.b':
    'Apenas os administradores da empresa criam projetos. Um membro da equipe não vê o botão “Novo projeto”. Se você é membro e ainda não tem projetos, verá o aviso para aguardar que um administrador atribua trabalho a você.',
  'help.a.equipo-permisos.s3.t': 'Quem edita e quem só vê',
  'help.a.equipo-permisos.s3.b':
    '• Administrador: tudo, em todos os projetos.\n• Integrante da equipe do projeto: cria, edita e move tarefas, atribui e comenta.\n• Não faz parte da equipe, mas o projeto é público: pode ver as tarefas e comentar, mas NÃO criar nem editar (você verá um aviso de “somente leitura”).',
  'help.a.equipo-permisos.s4.t': 'Público e privado',
  'help.a.equipo-permisos.s4.b':
    'Um projeto PÚBLICO é visto (e comentado) por todos os membros da empresa; só a equipe edita. Um projeto PRIVADO é visto apenas pela equipe do projeto e pelos administradores: os demais nem o encontram. Alternar entre público e privado é feito por um administrador ou pelo criador do projeto.',
  'help.a.equipo-permisos.s5.t': 'Atribuir adiciona à equipe',
  'help.a.equipo-permisos.s5.b':
    'Você pode atribuir uma tarefa a qualquer membro da sua empresa. Ao atribuí-la, essa pessoa entra automaticamente na equipe do projeto para poder trabalhar. Remover alguém da equipe não apaga suas tarefas: ela só perde o acesso de edição (e, em um privado, deixa de vê-lo).',
  'help.a.equipo-permisos.c1':
    'Pense assim: a equipe do projeto é “quem trabalha aqui”; público/privado é “quem pode olhar de fora”. Simples e sem permissões enroladas.',

  // Agenda jurídica — audiências, prazos e dados do caso (feature calendar_agenda)
  'help.a.agenda-legal.title': 'Agenda com horários e lembretes',
  'help.a.agenda-legal.summary':
    'Registre eventos com data, hora e local (audiências, reuniões, consultas, agendamentos), some dados do caso ou do cliente e veja tudo no calendário. Com lembrete 24 h antes e sincronização com o seu calendário pessoal.',
  'help.a.agenda-legal.s1.t': 'Onde se ativa',
  'help.a.agenda-legal.s1.b':
    'A agenda vem incluída no plano Advogados (Legal Solo e Legal Pro), mas pode ser ativada para empresas de QUALQUER ramo (reuniões, consultas, agendamentos): fale com o suporte e habilitamos para a sua conta.\n\nVocê reconhece porque, ao abrir qualquer tarefa, aparecem duas seções novas: “Agenda” e “Dados do caso”. Se não as vê, sua empresa ainda não a tem ativada — é só pedir.',
  'help.a.agenda-legal.s2.t': 'Registrar uma audiência ou evento',
  'help.a.agenda-legal.s2.b':
    'Abra a tarefa do processo e procure a seção “Agenda”. Preencha:\n\n• Data do evento e Hora (por exemplo, a audiência).\n• Até (opcional): o horário de término.\n• Local: vara, sala ou endereço.\n• Link da sala virtual: se a audiência for online.\n\nCom data e hora preenchidas, a tarefa vira um evento no calendário. Atenção à diferença: o “prazo” (data limite) é do dia inteiro e marca um vencimento; a “audiência” tem hora e local. Você pode usar os dois na mesma tarefa.',
  'help.a.agenda-legal.s3.t': 'Dados do caso (vara, processo, etc.)',
  'help.a.agenda-legal.s3.b':
    'Abaixo de “Agenda” está “Dados do caso”: os dados do processo que o modelo do ramo já traz (cliente, parte contrária, foro, vara, nº do processo, advogado responsável…). Preencha os que usar.\n\nCada dado tem uma caixa “Mostrar no calendário”. Marque os que quiser ver de relance na agenda (por exemplo Vara e nº do processo). Essa marcação vale para todo o projeto, então você não a repete tarefa por tarefa.',
  'help.a.agenda-legal.fig1.alt': 'Visão de calendário mostrando audiências com hora e local',
  'help.a.agenda-legal.fig1.cap':
    'Na visão Calendário cada audiência aparece no seu dia, com a hora, o local e os dados do caso que você marcou.',
  'help.a.agenda-legal.s4.t': 'Ver a agenda no calendário',
  'help.a.agenda-legal.s4.b':
    'Dentro do projeto, mude para a visão “Calendário”. Cada audiência aparece no seu dia com a hora, o local e os dados marcados. Você pode arrastar um evento para outro dia para reagendá-lo (mantém a hora).\n\nEm “Minhas tarefas” você tem sua agenda pessoal: todas as suas audiências e prazos de todos os projetos, juntos.',
  'help.a.agenda-legal.s5.t': 'Leve sua agenda para o Google, Apple ou Outlook',
  'help.a.agenda-legal.s5.b':
    'Em “Minhas tarefas” toque em “Assinar agenda”. Copie o endereço que aparece e cole no Google Calendar, Apple ou Outlook como “assinar uma agenda”. Suas audiências e prazos aparecem lá e se atualizam sozinhos sempre que mudam no Tembiapo. É somente leitura: a agenda é administrada pelo Tembiapo.',
  'help.a.agenda-legal.c1':
    'O Tembiapo avisa você 24 horas antes de cada audiência (notificação no app, e push se você ativou), para que nenhuma intimação passe batido.',
  'help.a.agenda-legal.c2':
    'A agenda vem incluída no plano Advogados (Legal Solo e Legal Pro) e pode ser ativada para empresas de qualquer ramo. Se tiver interesse, fale com a gente e habilitamos.',

  // Importar notas de reuniões (Plaud) — Fase 1: colar a nota como tarefa
  'help.a.importar-plaud.title': 'Importar notas de reuniões (Plaud)',
  'help.a.importar-plaud.summary':
    'Você grava uma reunião com o Plaud (ou qualquer gravador que gere resumo/transcrição) e traz para o Tembiapo como uma tarefa, com todo o texto dentro, no projeto ou caso que quiser.',
  'help.a.importar-plaud.s1.t': 'Para que serve',
  'help.a.importar-plaud.s1.b':
    'O Plaud é um gravador com IA que transcreve e resume suas reuniões. Com esta função, essa nota não fica solta em outro app: você a cola no Tembiapo e ela vira uma tarefa dentro do projeto ou caso, pesquisável e à mão para toda a equipe.',
  'help.a.importar-plaud.s2.t': 'Como importar uma nota',
  'help.a.importar-plaud.s2.b':
    'Dentro de um projeto, toque no botão “Importar do Plaud”. Abre um diálogo onde:\n\n• Você coloca um título (vem preenchido “Reunião — data”).\n• Escolhe a coluna onde a tarefa entra.\n• Cola a nota do Plaud (resumo e/ou transcrição, tudo junto).\n\nToque em “Criar tarefa” e pronto: cria-se com todo o texto na descrição.',
  'help.a.importar-plaud.s3.t': 'O que fica no Tembiapo',
  'help.a.importar-plaud.s3.b':
    'Uma tarefa normal, com a nota completa na descrição. Você pode atribuí-la, dar prazo, comentar, somar subtarefas ou movê-la de coluna como qualquer outra. O texto fica dentro do caso, não em um app separado.',
  'help.a.importar-plaud.c1':
    'Por enquanto a importação é colando a nota (não precisa configurar nada no Plaud). Mais adiante vamos permitir que a nota chegue sozinha e que a IA proponha as tarefas e audiências que a reunião menciona.',

  // Legais — comuns
  'legal.disclaimer':
    'Este é um documento modelo, fornecido a título informativo. Peça a revisão do seu assessor jurídico antes de usá-lo em produção.',
  'legal.lastUpdated': 'Última atualização: julho de 2026',
  'legal.eyebrow': 'Legal',

  // Política de Privacidade (/privacidad)
  'legal.privacy.title': 'Política de Privacidade',
  'legal.privacy.intro':
    'O Tembiapo é uma plataforma de gestão de projetos e tarefas da GRUPO EVOLUADO EAS (marca Evoluado), Paraguai. É formado por um aplicativo web (tembiapoapp.com) e um app móvel complementar para Android e iOS. Esta política explica, em linguagem clara, quais dados pessoais tratamos, para quê, com quem os compartilhamos e quais são os seus direitos. Responsável pelo tratamento: GRUPO EVOLUADO EAS. Contato: hola@evoluado.com · WhatsApp +595 992 954 499 · https://tembiapoapp.com/soporte.',
  'legal.privacy.s1.title': 'O Tembiapo e o app complementar',
  'legal.privacy.s1.body':
    'O Tembiapo é usado principalmente pela web (tembiapoapp.com), onde você cria, organiza e administra seus projetos. O app móvel é complementar: mostra as tarefas atribuídas a você e seus projetos e quadros em modo leitura, avisa por notificações e permite ações rápidas, como mudar o status de uma tarefa, marcar uma subtarefa ou deixar um comentário. A gestão completa (criar projetos, organizar equipes, administrar a conta) é feita na web.',
  'legal.privacy.s2.title': 'Quais dados coletamos',
  'legal.privacy.s2.body':
    '• Dados de conta: e-mail, nome e sobrenome, nome da sua empresa (tenant), papel, idioma e fuso horário. A senha é armazenada com hash Argon2id (não conseguimos lê-la). Você pode enviar um avatar opcional.\n• Conteúdo de trabalho: projetos, tarefas, subtarefas, comentários (com @menções), arquivos anexados e modelos. Tudo fica isolado por empresa (multi-tenant).\n• Token de notificações push (FCM): um identificador do seu dispositivo, guardado para podermos enviar avisos. É removido quando você sai da conta.\n• Dados operacionais e de segurança: tokens de sessão (guardados com hash), limites de tentativas de login (por IP e e-mail) e registros de auditoria (quem fez o quê). Se estiver configurado, usamos o Sentry para monitorar erros, higienizando os dados pessoais antes de enviá-los.',
  'legal.privacy.s3.title': 'Sua biometria nunca sai do seu celular',
  'legal.privacy.s3.body':
    'Se você ativar o desbloqueio por impressão digital ou Face ID no app móvel, esses dados biométricos NÃO são enviados aos nossos servidores nem são armazenados por nós. A biometria é processada apenas no seu dispositivo, por meio do keychain ou do armazenamento seguro do sistema, e serve somente para desbloquear localmente uma sessão que você já iniciou. Nós nunca recebemos sua digital nem seu rosto.',
  'legal.privacy.s4.title': 'Para que usamos seus dados',
  'legal.privacy.s4.body':
    'Usamos seus dados para prestar o serviço: criar e operar sua conta, mostrar seus projetos e tarefas, enviar as notificações que você escolheu receber, manter a segurança e prevenir abusos, e oferecer suporte. Não fazemos publicidade, não usamos analítica publicitária de terceiros e não vendemos seus dados. Dentro de uma empresa, o conteúdo de trabalho é visível para os membros dessa empresa conforme seus papéis.',
  'legal.privacy.s5.title': 'Base legal do tratamento',
  'legal.privacy.s5.body':
    'Tratamos seus dados sobre três bases: a execução do contrato (para prestar o serviço que você contratou), nosso legítimo interesse (manter a segurança da plataforma e prevenir abusos) e seu consentimento (por exemplo, para a permissão de notificações push, ou para entrar com o Google se você escolher essa opção).',
  'legal.privacy.s6.title': 'Com quem compartilhamos seus dados',
  'legal.privacy.s6.body':
    'Não vendemos seus dados. Só os compartilhamos com fornecedores que atuam como operadores de tratamento, para que o serviço funcione:\n\n• Google Firebase Cloud Messaging (FCM): entrega de notificações push; recebe o token do seu dispositivo.\n• Google OAuth / Google Sign-In: se você escolher entrar com o Google, o Google confirma sua identidade e nós recebemos seu e-mail e nome.\n• Hostinger (SMTP): envio de e-mails transacionais (convites, avisos de conta).\n• Provedor de hospedagem/VPS: onde ficam alojados os dados da plataforma.\n• Sentry: monitoramento de erros, apenas se estiver habilitado, com dados técnicos e com os dados pessoais higienizados.',
  'legal.privacy.s7.title': 'Transferências internacionais',
  'legal.privacy.s7.body':
    'Alguns dos nossos operadores (por exemplo, o Google) podem processar dados fora do Paraguai e do Brasil, sob seus próprios termos e salvaguardas de proteção. Ao usar o serviço, você entende que certos dados técnicos podem ser tratados no exterior com essa proteção.',
  'legal.privacy.s8.title': 'Por quanto tempo guardamos os dados',
  'legal.privacy.s8.body':
    'Guardamos seus dados enquanto sua conta e sua empresa estiverem ativas. Quando você exclui a conta, apagamos os dados associados (veja a seção seguinte). Os backups rotacionam e são sobrescritos com o tempo, e os registros de segurança são mantidos por um período limitado para podermos detectar e responder a incidentes.',
  'legal.privacy.s9.title': 'Seus direitos como titular',
  'legal.privacy.s9.body':
    'Você pode exercer seus direitos de acesso, retificação, exclusão, portabilidade e oposição sobre seus dados pessoais. A forma mais rápida é editar seu perfil diretamente no app ou na web. Você também pode escrever para hola@evoluado.com e nós ajudamos; podemos pedir que você verifique sua identidade antes de responder.',
  'legal.privacy.s10.title': 'Como excluir sua conta e todos os seus dados',
  'legal.privacy.s10.body':
    'Pela web você pode excluir sua conta por conta própria: acesse Preferências → "Zona de perigo" → Excluir conta, confirme, e sua empresa/conta é excluída junto com todos os seus dados associados. Você também pode pedir a exclusão escrevendo para hola@evoluado.com. O token de notificações é removido automaticamente quando você sai da conta.',
  'legal.privacy.s11.title': 'Segurança',
  'legal.privacy.s11.body':
    'Protegemos seus dados com criptografia em trânsito (HTTPS/TLS), senhas com hash Argon2id, tokens de sessão guardados com hash, credenciais sensíveis criptografadas (AES-GCM) e isolamento estrito por empresa, de modo que uma empresa nunca veja os dados de outra. Nenhum sistema é 100% infalível, mas trabalhamos para reduzir os riscos ao mínimo.',
  'legal.privacy.s12.title': 'Menores de idade',
  'legal.privacy.s12.body':
    'O Tembiapo é uma ferramenta de trabalho para empresas e equipes (B2B). Não é destinado a menores de idade nem coletamos dados deles conscientemente.',
  'legal.privacy.s13.title': 'Alterações e contato',
  'legal.privacy.s13.body':
    'Podemos atualizar esta política; quando o fizermos, publicaremos a versão vigente nesta página com a sua data e, se a mudança for importante, avisaremos pelos canais do produto. Última atualização: julho de 2026. Em caso de dúvida sobre seus dados, escreva para hola@evoluado.com ou acesse https://tembiapoapp.com/soporte.',

  // Termos de Uso (/terminos)
  'legal.terms.title': 'Termos de Uso',
  'legal.terms.intro':
    'Estes termos regulam o uso do Tembiapo, um serviço de gestão de projetos e tarefas da GRUPO EVOLUADO EAS (marca Evoluado), Paraguai, formado pela web tembiapoapp.com e um app móvel complementar para Android e iOS. Ao criar uma conta ou usar o serviço, você aceita estes termos.',
  'legal.terms.s1.title': 'O serviço',
  'legal.terms.s1.body':
    'O Tembiapo permite organizar projetos, tarefas e equipes. É usado principalmente pela web; o app móvel é complementar e mostra suas tarefas e quadros em modo leitura, envia avisos e permite ações rápidas, como mudar o status de uma tarefa, marcar uma subtarefa ou comentar. Podemos melhorar, alterar ou descontinuar funções para manter o serviço; se uma mudança for importante, procuramos avisar com antecedência.',
  'legal.terms.s2.title': 'Sua conta',
  'legal.terms.s2.body':
    'Você é responsável pelas informações que adiciona e por manter em sigilo a sua senha. Cada pessoa deve usar suas próprias credenciais. Avise-nos imediatamente em hola@evoluado.com se detectar um uso não autorizado da sua conta.',
  'legal.terms.s3.title': 'Uso aceitável',
  'legal.terms.s3.body':
    'Você se compromete a usar o Tembiapo conforme a lei e a não adicionar conteúdo ilícito, não violar a segurança, não interferir no serviço nem tentar acessar dados de outras empresas. Podemos suspender as contas que descumpram estas regras.',
  'legal.terms.s4.title': 'Teste grátis, planos e pagamentos',
  'legal.terms.s4.body':
    'Oferecemos um teste gratuito de 30 dias, sem cartão. Ao terminar, se você não ativar um plano pago, sua conta passa a somente leitura até que registre um pagamento. Hoje os pagamentos são feitos de forma manual (transferência ou depósito): você registra o comprovante na seção de Faturamento e nossa equipe valida para ativar ou renovar seu plano. Os preços podem ser atualizados; avisaremos antes de uma mudança afetar você.',
  'legal.terms.s5.title': 'Propriedade intelectual',
  'legal.terms.s5.body':
    'A plataforma, seu software, sua marca (Tembiapo / Tembiapp) e seu design são propriedade da GRUPO EVOLUADO EAS. Você não adquire direitos sobre eles além do uso do serviço conforme estes termos.',
  'legal.terms.s6.title': 'Seu conteúdo',
  'legal.terms.s6.body':
    'O conteúdo de trabalho que você adiciona (projetos, tarefas, comentários, arquivos) é e continua sendo seu. Para podermos prestar o serviço, você nos concede uma licença limitada para hospedar, processar e exibir esse conteúdo aos membros da sua empresa conforme seus papéis. Não usamos seu conteúdo para outros fins.',
  'legal.terms.s7.title': 'Garantias e limitação de responsabilidade',
  'legal.terms.s7.body':
    'Nos esforçamos para oferecer um serviço disponível e confiável, mas ele é fornecido "como está", sem garantias implícitas. Na medida permitida pela lei, não somos responsáveis por danos indiretos, por lucros cessantes nem por perda de dados causada por fatores fora do nosso controle razoável. Recomendamos manter suas próprias cópias das informações críticas.',
  'legal.terms.s8.title': 'Suspensão e encerramento',
  'legal.terms.s8.body':
    'Você pode parar de usar o serviço e excluir sua conta quando quiser, em Preferências → "Zona de perigo", ou entrando em contato conosco. Também podemos suspender ou encerrar contas em caso de descumprimentos graves destes termos.',
  'legal.terms.s9.title': 'Lei aplicável',
  'legal.terms.s9.body':
    'Estes termos são regidos pelas leis da República do Paraguai, em particular a Lei nº 6534/2020 de Proteção de Dados Pessoais. Para os usuários localizados no Brasil, aplicamos ainda práticas alinhadas à Lei Geral de Proteção de Dados (LGPD). Em caso de controvérsia, sem prejuízo dos direitos que a lei reconheça aos consumidores em seu local de residência, as partes se submetem aos tribunais competentes do Paraguai.',
  'legal.terms.s10.title': 'Alterações',
  'legal.terms.s10.body':
    'Podemos atualizar estes termos. Publicaremos a versão vigente nesta página com a sua data e, se a mudança for importante, avisaremos pelos canais do produto. Última atualização: julho de 2026.',
  'legal.terms.s11.title': 'Contato',
  'legal.terms.s11.body':
    'Para dúvidas sobre estes termos, escreva para hola@evoluado.com, fale pelo WhatsApp +595 992 954 499 ou acesse https://tembiapoapp.com/soporte.',

  // Política de Cookies (/cookies)
  'legal.cookies.title': 'Política de Cookies',
  'legal.cookies.intro':
    'O Tembiapo usa a menor quantidade possível de cookies e armazenamento local necessária para funcionar. Não usamos cookies de publicidade nem de rastreamento de terceiros por padrão. Responsável: GRUPO EVOLUADO EAS (Evoluado), hola@evoluado.com.',
  'legal.cookies.s1.title': 'O que é um cookie?',
  'legal.cookies.s1.body':
    'Um cookie é um pequeno arquivo que um site guarda no seu navegador. Também usamos o "armazenamento local" (localStorage), uma tecnologia parecida, para lembrar preferências. Juntos, eles nos permitem manter sua sessão e suas configurações.',
  'legal.cookies.s2.title': 'Cookies essenciais',
  'legal.cookies.s2.body':
    'São imprescindíveis para o app funcionar e não podem ser desativados. O principal é o cookie de sessão de renovação (tb_refresh), que mantém sua sessão iniciada de forma segura sob o caminho /tembiapo. Sem ele você não conseguiria permanecer logado.',
  'legal.cookies.s3.title': 'Preferências',
  'legal.cookies.s3.body':
    'Guardamos algumas preferências para melhorar sua experiência, como o tema claro/escuro e o idioma escolhido. Essa informação fica no seu navegador (localStorage) e não é usada para rastrear você.',
  'legal.cookies.s4.title': 'O que NÃO usamos',
  'legal.cookies.s4.body':
    'Por padrão não carregamos cookies de publicidade, nem pixels de redes sociais, nem rastreadores de terceiros para criar um perfil da sua navegação. Se no futuro incorporarmos ferramentas de análise, informaremos e pediremos seu consentimento quando cabível.',
  'legal.cookies.s5.title': 'Como gerenciá-los',
  'legal.cookies.s5.body':
    'Você pode apagar ou bloquear cookies nas configurações do seu navegador. Tenha em conta que, se bloquear os cookies essenciais, provavelmente não conseguirá fazer login nem usar o Tembiapo normalmente. O tema e o idioma serão redefinidos se você apagar o armazenamento local.',

  // Gerenciar empresa (Super Admin)
  'manageCompany.action': 'Gerenciar',
  'manageCompany.custom': 'Personalizado',
  'manageCompany.title': 'Gerenciar empresa',
  'manageCompany.loadError': 'Não foi possível carregar a empresa.',
  'manageCompany.summary.title': 'Resumo',
  'manageCompany.summary.plan': 'Plano base',
  'manageCompany.summary.status': 'Status',
  'manageCompany.summary.projects': 'Projetos',
  'manageCompany.summary.users': 'Usuários',
  'manageCompany.summary.usage': '{used} / {limit}',
  'manageCompany.summary.price': 'Preço efetivo',
  'manageCompany.summary.perMonth': '/mês',
  'manageCompany.summary.free': 'Grátis / demo',
  'manageCompany.summary.addons': 'Add-ons',
  'manageCompany.summary.created': 'Cadastro',
  'manageCompany.overrides.title': 'Plano personalizado',
  'manageCompany.overrides.hint':
    'Deixe um campo vazio para usar o valor do plano base. As mudanças valem só para esta empresa.',
  'manageCompany.features.title': 'Funções adicionais',
  'manageCompany.features.hint':
    'Ative funções extras para esta empresa, independente do plano ou do ramo (ex. a Agenda com horários para reuniões, consultas ou audiências).',
  'manageCompany.field.projects': 'Projetos (limite)',
  'manageCompany.field.users': 'Usuários (limite)',
  'manageCompany.field.price': 'Preço mensal',
  'manageCompany.field.priceHint': '0 = grátis / modo demo. Vazio = usa o preço do plano.',
  'manageCompany.field.planPlaceholder': 'Plano: {value}',
  'manageCompany.field.plan': 'Plano',
  'manageCompany.field.planHint':
    'Muda o plano da assinatura (sem rateio; o preço se ajusta com o override acima).',
  'manageCompany.field.status': 'Status',
  'manageCompany.field.trialEnds': 'Fim do trial / demo',
  'manageCompany.field.trialEndsHint':
    'Pode passar dos 30 dias para estender a demo. Vazio = sem data de fim.',
  'manageCompany.status.TRIAL': 'Trial',
  'manageCompany.status.ACTIVE': 'Ativa',
  'manageCompany.status.PAST_DUE': 'Pagamento pendente',
  'manageCompany.status.SUSPENDED': 'Suspensa',
  'manageCompany.status.CANCELED': 'Cancelada',
  'manageCompany.save': 'Salvar alterações',
  'manageCompany.saved': 'Alterações salvas.',
  'manageCompany.saveError': 'Não foi possível salvar as alterações.',
  'manageCompany.noChanges': 'Não há alterações para salvar.',
  'manageCompany.restore': 'Restaurar ao plano',
  'manageCompany.restoreConfirm':
    'Restaurar os limites e o preço ao plano base? Os valores personalizados serão removidos.',
  'manageCompany.restoreError': 'Não foi possível restaurar o plano.',

  // Histórico de alterações (auditoria) do modal "Gerenciar empresa"
  'audit.title': 'Histórico de alterações',
  'audit.empty': 'Nenhuma alteração registrada.',
  'audit.loadError': 'Não foi possível carregar o histórico.',
  'audit.superAdmin': 'Super admin',
  'audit.field.projects': 'Projetos',
  'audit.field.users': 'Usuários',
  'audit.field.price': 'Preço mensal',
  'audit.field.trialEnds': 'Fim do trial',
  'audit.field.status': 'Status',
  'audit.field.plan': 'Plano',
  'audit.value.plan': 'conforme o plano',
  'audit.value.free': 'grátis',
  'audit.value.none': 'sem definir',

  // Ações de suporte / perigo do modal "Gerenciar empresa"
  'companyAdmin.title': 'Ações',
  'companyAdmin.deactivate.suspendTitle': 'Desativar empresa',
  'companyAdmin.deactivate.suspendHint':
    'Suspende o acesso: a empresa fica somente leitura até ser reativada.',
  'companyAdmin.deactivate.suspendAction': 'Desativar',
  'companyAdmin.deactivate.suspendConfirm':
    'Desativar {company}? A equipe perderá o acesso de escrita até você reativá-la.',
  'companyAdmin.deactivate.reactivateTitle': 'Reativar empresa',
  'companyAdmin.deactivate.reactivateHint':
    'A empresa está suspensa. Reative-a para devolver o acesso.',
  'companyAdmin.deactivate.reactivateAction': 'Reativar',
  'companyAdmin.deactivate.reactivateConfirm': 'Reativar {company} e devolver o acesso?',
  'companyAdmin.deactivate.error': 'Não foi possível alterar o status da empresa.',
  'companyAdmin.impersonate.title': 'Ver como esta empresa',
  'companyAdmin.impersonate.hint':
    'Entre no app com a visão desta empresa para dar suporte. Não altera a sessão dela.',
  'companyAdmin.impersonate.action': 'Ver como esta empresa (suporte)',
  'companyAdmin.impersonate.error': 'Não foi possível iniciar a visão de suporte. Tente de novo.',
  'companyAdmin.impersonate.banner': 'Você está vendo como {company} (suporte)',
  'companyAdmin.impersonate.back': 'Voltar ao painel admin',
  'companyAdmin.delete.zone': 'Zona de perigo',
  'companyAdmin.delete.hint':
    'Excluir a empresa apaga permanentemente seus projetos, tarefas e usuários. Não pode ser desfeito.',
  'companyAdmin.delete.open': 'Excluir empresa',
  'companyAdmin.delete.confirmTitle': 'Excluir empresa',
  'companyAdmin.delete.warning':
    'Isto excluirá definitivamente {company} com TODOS os seus projetos, tarefas, usuários e dados. Esta ação é irreversível.',
  'companyAdmin.delete.typeToConfirm': 'Digite "{company}" para confirmar',
  'companyAdmin.delete.confirmAction': 'Excluir definitivamente',
  'companyAdmin.delete.error': 'Não foi possível excluir a empresa. Tente de novo.',

  // Tour de onboarding
  'tour.skip': 'Pular',
  'tour.back': 'Anterior',
  'tour.next': 'Próximo',
  'tour.cta': 'Criar meu primeiro projeto',
  'tour.close': 'Fechar tour',
  'tour.replay': 'Ver o tour de novo',
  'tour.welcome.title': 'Bem-vindo ao Tembiapo',
  'tour.welcome.body': 'Mostramos o básico em 30 segundos. Você pode pular quando quiser.',
  'tour.projects.title': 'Seus projetos',
  'tour.projects.body': 'Crie seu primeiro projeto a partir de um modelo do seu ramo ou comece do zero.',
  'tour.views.title': 'Visões do quadro',
  'tour.views.body':
    'Veja seu trabalho como Kanban, Lista, Calendário ou Timeline. Escolha a visão que preferir.',
  'tour.team.title': 'Convide sua equipe',
  'tour.team.body': 'Convide sua equipe por e-mail neste menu e trabalhem juntos.',
  'tour.notifications.title': 'Notificações',
  'tour.notifications.body': 'Fique por dentro de menções, atribuições e prazos pelo sininho.',
  'tour.help.title': 'Ajuda e suporte',
  'tour.help.body': 'Se travar, você tem a central de ajuda e o WhatsApp para falar com a gente.',

  // Seletor de moeda de exibição (₲ / R$ / US$)
  'currency.label': 'Moeda',
  'currency.name.PYG': 'Guaranis (₲)',
  'currency.name.BRL': 'Reais (R$)',
  'currency.name.USD': 'Dólares (US$)',
  'currency.billedInPyg': 'A cobrança é em guaranis (₲); sua moeda é apenas referência',

  // Painel Super Admin — Taxas de câmbio
  'adminRates.nav': 'Taxas de câmbio',
  'adminRates.title': 'Taxas de câmbio',
  'adminRates.subtitle':
    'Os preços são cobrados em ₲. Estas taxas só definem como aparecem em R$ e US$.',
  'adminRates.loadError': 'Não foi possível carregar as taxas de câmbio.',
  'adminRates.current.title': 'Taxas atuais',
  'adminRates.current.hint': 'Quantos guaranis vale 1 unidade de cada moeda.',
  'adminRates.updatedAt': 'Última atualização: {date}',
  'adminRates.updatedNever': 'Ainda não atualizado',
  'adminRates.perUsd': '₲ por 1 US$',
  'adminRates.perBrl': '₲ por 1 R$',
  'adminRates.refresh.title': 'Atualização automática',
  'adminRates.refresh.hint': 'Busque as taxas mais recentes na fonte configurada.',
  'adminRates.refresh.button': 'Atualizar automaticamente',
  'adminRates.refresh.success': 'Taxas atualizadas a partir da fonte.',
  'adminRates.refresh.unavailable':
    'A fonte de taxas está indisponível agora. Tente novamente em breve ou carregue manualmente.',
  'adminRates.refresh.error': 'Não foi possível atualizar as taxas. Tente novamente.',
  'adminRates.manual.title': 'Carga manual',
  'adminRates.manual.hint': 'Informe quantos guaranis vale 1 unidade de cada moeda.',
  'adminRates.manual.usd': '₲ por 1 US$',
  'adminRates.manual.brl': '₲ por 1 R$',
  'adminRates.manual.save': 'Salvar taxas',
  'adminRates.manual.success': 'Taxas salvas.',
  'adminRates.manual.error': 'Não foi possível salvar as taxas. Verifique os valores.',
  'adminRates.manual.invalid': 'Informe um valor maior que zero.',

  // Painel Super Admin — Segurança (troca de senha com PIN)
  'adminSecurity.nav': 'Segurança',
  'adminSecurity.title': 'Segurança',
  'adminSecurity.subtitle':
    'Altere sua senha de super admin com verificação por PIN no WhatsApp de controle.',
  'adminSecurity.changed': 'Senha alterada com sucesso.',
  'adminSecurity.card.title': 'Alterar senha',
  'adminSecurity.card.intro':
    'Informe sua senha atual e a nova. Enviaremos um PIN de 6 dígitos ao WhatsApp de controle para confirmar.',
  'adminSecurity.field.current': 'Senha atual',
  'adminSecurity.field.new': 'Nova senha',
  'adminSecurity.field.newHint': 'Mínimo de 8 caracteres.',
  'adminSecurity.field.repeat': 'Repetir nova senha',
  'adminSecurity.field.pin': 'PIN de verificação',
  'adminSecurity.pin.sentTo': 'Enviamos um PIN ao WhatsApp de controle {phone}.',
  'adminSecurity.action.sendPin': 'Enviar PIN',
  'adminSecurity.action.confirm': 'Confirmar alteração',
  'adminSecurity.action.back': 'Voltar',
  'adminSecurity.action.resend': 'Reenviar PIN',
  'adminSecurity.action.resendIn': 'Reenviar em {s}s',
  'adminSecurity.validation.tooShort': 'A senha deve ter pelo menos 8 caracteres.',
  'adminSecurity.validation.mismatch': 'As senhas não coincidem.',
  'adminSecurity.validation.pinFormat': 'O PIN deve ter 6 dígitos.',
  'adminSecurity.error.invalidCurrent': 'A senha atual está incorreta.',
  'adminSecurity.error.passwordSame': 'A nova senha deve ser diferente da atual.',
  'adminSecurity.error.pinExpired': 'O PIN expirou ou não existe. Solicite um novo.',
  'adminSecurity.error.invalidPin': 'O PIN está incorreto.',
  'adminSecurity.error.invalidPinLeft': 'O PIN está incorreto. Restam {left} tentativas.',
  'adminSecurity.error.pinTooMany': 'Muitas tentativas. Solicite um novo PIN.',
  'adminSecurity.error.resendTooSoon': 'Aguarde um momento antes de solicitar outro PIN.',
  'adminSecurity.error.generic': 'Não foi possível concluir a operação. Tente novamente.',

  // --- SEO por página (públicas) ---
  'seo.login.title': 'Entrar',
  'seo.login.desc': 'Entre no Tembiapo para gerenciar seus projetos, tarefas e equipe.',
  'seo.register.title': 'Crie sua empresa grátis',
  'seo.register.desc': 'Teste o Tembiapo 30 dias grátis. Escolha seu setor, seu plano e comece em minutos.',
  'seo.about.title': 'Sobre nós',
  'seo.about.desc': 'Conheça o Tembiapo: gestão de projetos e tarefas sem complicações para equipes do Paraguai e do Brasil.',
  'seo.contact.title': 'Contato',
  'seo.contact.desc': 'Fale com a gente por WhatsApp ou e-mail. Estamos aqui para ajudar com o Tembiapo.',
  'seo.support.title': 'Suporte',
  'seo.support.desc': 'Central de ajuda, guias e canais de suporte do Tembiapo.',
  'seo.help.title': 'Central de ajuda',
  'seo.help.desc': 'Guias passo a passo para aproveitar ao máximo o Tembiapo.',

  // --- Painel SEO + SEM (Super Admin) ---
  'adminSeo.title': 'SEO e campanhas',
  'adminSeo.subtitle': 'Metadados do site, verificação de buscadores e tags de analytics/campanhas.',
  'adminSeo.loadError': 'Não foi possível carregar a configuração de SEO.',
  'adminSeo.saveError': 'Não foi possível salvar as alterações. Tente novamente.',
  'adminSeo.saved': 'Alterações salvas.',
  'adminSeo.save': 'Salvar',
  'adminSeo.section.meta': 'Metadados do site',
  'adminSeo.section.metaNote': 'Título, descrição e imagem de compartilhamento. Deixe um campo vazio para voltar ao valor padrão.',
  'adminSeo.section.verify': 'Verificação de buscadores',
  'adminSeo.section.verifyNote': 'O ideal é verificar pelo Google Analytics ou Tag Manager: o robô de verificação não executa JavaScript, então estas metas são apenas um reforço.',
  'adminSeo.section.sem': 'Analytics e campanhas (SEM)',
  'adminSeo.section.semNote': 'Ao inserir um ID, seu script é injetado automaticamente no site. Deixe vazio para desativar.',
  'adminSeo.lang.es': 'Espanhol',
  'adminSeo.lang.pt': 'Português (BR)',
  'adminSeo.field.siteName': 'Nome do site',
  'adminSeo.hint.siteName': 'Usado no título e nos cartões de compartilhamento (og:site_name).',
  'adminSeo.field.canonical': 'URL canônica base',
  'adminSeo.hint.canonical': 'Domínio público de produção, ex. https://tembiapoapp.com. Base para as URLs canônicas.',
  'adminSeo.field.ogImage': 'Imagem de compartilhamento (Open Graph)',
  'adminSeo.hint.ogImage': 'URL absoluta da imagem exibida ao compartilhar nas redes (1200×630 px recomendado).',
  'adminSeo.field.twitter': 'Usuário do X/Twitter',
  'adminSeo.hint.twitter': 'Com arroba, ex. @suaempresa. Usado nos cartões do Twitter.',
  'adminSeo.field.robots': 'Permitir indexação',
  'adminSeo.hint.robots': 'Se desativar, pede-se aos buscadores para não indexar o site (noindex).',
  'adminSeo.field.titleEs': 'Título (ES)',
  'adminSeo.field.descEs': 'Descrição (ES)',
  'adminSeo.field.keywordsEs': 'Palavras-chave (ES)',
  'adminSeo.field.titlePt': 'Título (PT)',
  'adminSeo.field.descPt': 'Descrição (PT)',
  'adminSeo.field.keywordsPt': 'Palavras-chave (PT)',
  'adminSeo.hint.title': 'Título padrão da home. Até ~60 caracteres.',
  'adminSeo.hint.desc': 'Descrição padrão para buscadores e redes. Até ~160 caracteres.',
  'adminSeo.hint.keywords': 'Opcional, separadas por vírgulas. Pouco relevante para o Google hoje.',
  'adminSeo.field.verifyGoogle': 'Google Search Console',
  'adminSeo.hint.verifyGoogle': 'Conteúdo da meta google-site-verification (sem as aspas).',
  'adminSeo.field.verifyBing': 'Bing Webmaster',
  'adminSeo.hint.verifyBing': 'Conteúdo da meta msvalidate.01 do Bing.',
  'adminSeo.field.ga4': 'Google Analytics 4',
  'adminSeo.hint.ga4': 'ID de medição, ex. G-XXXXXXX. Obtido em Administrar → Fluxos de dados.',
  'adminSeo.field.gtm': 'Google Tag Manager',
  'adminSeo.hint.gtm': 'ID do contêiner, ex. GTM-XXXXXX.',
  'adminSeo.field.pixel': 'Meta Pixel (Facebook)',
  'adminSeo.hint.pixel': 'ID do pixel, obtido no Gerenciador de eventos da Meta.',
  'adminSeo.field.adsId': 'Google Ads',
  'adminSeo.hint.adsId': 'ID de conversão, ex. AW-XXXXXXXXX.',
  'adminSeo.field.adsLabel': 'Rótulo de conversão do Google Ads',
  'adminSeo.hint.adsLabel': 'Rótulo que acompanha o ID do Google Ads para medir conversões.',

  // Descontos — badge/preço (landing + cadastro)
  'discount.off': '-{pct}%',
  'discount.endsAt': 'termina em {date}',
  'discount.wasPrice': 'Preço sem desconto',

  // Descontos — painel Super Admin (dentro de Planos)
  'adminDiscount.title': 'Descontos',
  'adminDiscount.subtitle':
    'Desconto anual global e promoção de lançamento. Não se acumulam: vale o maior.',
  'adminDiscount.annualPercent': 'Desconto anual (%)',
  'adminDiscount.annualHint':
    'O preço anual é calculado: preço mensal × 12 com este desconto aplicado.',
  'adminDiscount.promoEnabled': 'Promoção de lançamento ativa',
  'adminDiscount.promoEnabledHint':
    'Aplica ao preço mensal e anual (vale o maior entre promoção e desconto anual).',
  'adminDiscount.promoPercent': 'Desconto da promoção (%)',
  'adminDiscount.promoEndsAt': 'Data de término',
  'adminDiscount.promoEndsAtHint': 'Opcional. Vazio = sem vencimento.',
  'adminDiscount.promoLabel': 'Rótulo',
  'adminDiscount.promoLabelHint': 'Texto curto, ex. "Lançamento".',
  'adminDiscount.saved': 'Descontos salvos.',
  'adminDiscount.saveError': 'Não foi possível salvar os descontos.',
  'adminDiscount.loadError': 'Não foi possível carregar os descontos.',

  // Zona de perigo: exclusão self-service da própria empresa (só COMPANY_ADMIN)
  'account.delete.zone': 'Zona de perigo',
  'account.delete.warning':
    'Excluir a conta apaga permanentemente sua empresa e TODOS os seus dados: projetos, tarefas, arquivos e membros. Esta ação é irreversível.',
  'account.delete.open': 'Excluir conta',
  'account.delete.confirmTitle': 'Excluir conta',
  'account.delete.confirmWarning':
    'Isto excluirá definitivamente {company} com TODOS os seus projetos, tarefas, arquivos e membros. Não é possível desfazer.',
  'account.delete.passwordLabel': 'Sua senha',
  'account.delete.passwordPlaceholder': 'Digite sua senha',
  'account.delete.typeToConfirm': 'Digite "{company}" para confirmar',
  'account.delete.confirmAction': 'Excluir definitivamente',
  'account.delete.invalidPassword': 'A senha está incorreta.',
  'account.delete.error': 'Não foi possível excluir a conta. Tente de novo.',

  // Associação a múltiplas empresas: seleção ao entrar + troca
  'company.select.title': 'Em qual empresa você vai trabalhar?',
  'company.select.subtitle': 'Você pertence a várias empresas. Escolha com qual continuar.',
  'company.select.back': 'Usar outra conta',
  'company.select.expired': 'A seleção expirou. Entre novamente.',
  'company.switch.title': 'Trocar de empresa',
  'company.switch.workingIn': 'Trabalhando em',
  'company.switch.done': 'Você mudou para {name}.',

  // Pastas (agrupar projetos)
  'folders.new': 'Nova pasta',
  'folders.create': 'Criar pasta',
  'folders.rename': 'Renomear pasta',
  'folders.delete': 'Excluir pasta',
  'folders.delete.confirm':
    'A pasta “{name}” será excluída. Os projetos NÃO são apagados: ficam em “Sem pasta”.',
  'folders.deleteError': 'Não foi possível excluir a pasta. Tente de novo.',
  'folders.name': 'Nome da pasta',
  'folders.namePlaceholder': 'Ex: Clientes ativos',
  'folders.name.required': 'O nome da pasta é obrigatório.',
  'folders.saveError': 'Não foi possível salvar a pasta. Tente de novo.',
  'folders.color': 'Cor',
  'folders.color.none': 'Sem cor',
  'folders.uncategorized': 'Sem pasta',
  'folders.moveTo': 'Mover para pasta',
  'folders.actions': 'Ações da pasta',
  'folders.projectCount': '{n} projetos',
  'folders.collapse': 'Recolher',
  'folders.expand': 'Expandir',
  'folders.field.label': 'Pasta',
  'folders.sectionTitle': 'Pastas',
  'folders.open': 'Abrir pasta',
  'folders.changeColor': 'Mudar cor',
  'folders.reorder': 'Reordenar pasta',
  'folders.notFound': 'Não encontramos esta pasta.',
  'folderView.empty.title': 'Esta pasta está vazia',
  'folderView.empty.body':
    'Ainda não há projetos nesta pasta. Crie um novo ou mova projetos existentes para cá pelo menu deles.',
  'convert.action': 'Converter em projeto',
  'convert.nameLabel': 'Nome do projeto',
  'convert.deleteOriginal': 'Excluir a tarefa original',
  'convert.hint':
    'A tarefa é movida para o novo projeto com seus comentários, anexos, descrição e responsável; suas subtarefas viram tarefas. Deixa de aparecer neste quadro.',
  'convert.submit': 'Converter',
  'convert.error': 'Não foi possível converter a tarefa. Tente de novo.',
  'view.asCards': 'Ver como cartões',
  'view.asList': 'Ver como lista',

  // Feedback de lançamento (banner + modal)
  'feedback.banner.title': 'Estamos em lançamento — conte o que podemos melhorar',
  'feedback.banner.cta': 'Enviar sugestão',
  'feedback.modal.title': 'O que você quer nos contar?',
  'feedback.type.suggestion': 'Sugestão',
  'feedback.type.bug': 'Relatar um bug',
  'feedback.type.vertical': 'Novo segmento/ramo',
  'feedback.type.template': 'Novo modelo',
  'feedback.type.other': 'Outro',
  'feedback.message.label': 'Mensagem',
  'feedback.message.placeholder': 'Conte com detalhes o que melhoraria ou o que encontrou…',
  'feedback.submit': 'Enviar',
  'feedback.disclaimer':
    'As sugestões são analisadas em equipe; não garantimos seu desenvolvimento. Os bugs recebem resposta.',
  'feedback.done.title': 'Recebido!',
  'feedback.done.bug':
    'Recebemos seu relato. Vamos analisá-lo e responder se ficou resolvido.',
  'feedback.done.idea':
    'Obrigado! Sua ideia será analisada pela equipe. Podemos desenvolvê-la ou não, mas todas as propostas nos ajudam a melhorar.',
  'feedback.error.tooMany':
    'Você enviou feedback há pouco. Aguarde um momento antes de enviar outro.',

  // Feedback — painel Super Admin
  'admin.feedback.title': 'Feedback',
  'admin.feedback.subtitle': 'Sugestões, bugs e pedidos das empresas.',
  'admin.feedback.filter.new': 'Novos',
  'admin.feedback.filter.reviewed': 'Revisados',
  'admin.feedback.filter.all': 'Todos',
  'admin.feedback.filter.allTypes': 'Todos os tipos',
  'admin.feedback.col.type': 'Tipo',
  'admin.feedback.col.message': 'Mensagem',
  'admin.feedback.col.company': 'Empresa / Autor',
  'admin.feedback.col.source': 'Origem',
  'admin.feedback.col.date': 'Data',
  'admin.feedback.col.status': 'Status',
  'admin.feedback.col.actions': 'Ações',
  'admin.feedback.status.new': 'Novo',
  'admin.feedback.status.reviewed': 'Revisado',
  'admin.feedback.markReviewed': 'Marcar como revisado',
  'admin.feedback.markNew': 'Marcar como novo',
  'admin.feedback.empty': 'Não há feedback para mostrar.',
  'admin.feedback.more': 'Ver mais',
  'admin.feedback.less': 'Ver menos',
};

const dictionaries: Record<Locale, Partial<Record<MessageKey, string>>> = { es, pt };

/** Clave de persistencia de la elección manual de idioma. */
const LANG_STORAGE_KEY = 'tembiapo:lang';

/**
 * Locale inicial (P3.3): preferencia guardada → idioma del navegador (refleja el
 * país/config del visitante) → 'es'. Así un visitante de Brasil (navegador pt-*)
 * ve pt-BR automáticamente al aterrizar desde un anuncio, sin tocar nada.
 */
function detectInitialLocale(): Locale {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (stored === 'es' || stored === 'pt') return stored;
  } catch {
    /* localStorage no disponible (modo privado): seguimos con detección */
  }
  try {
    const langs =
      typeof navigator !== 'undefined'
        ? navigator.languages && navigator.languages.length
          ? navigator.languages
          : [navigator.language]
        : [];
    for (const l of langs) {
      if (typeof l === 'string' && l.toLowerCase().startsWith('pt')) return 'pt';
    }
  } catch {
    /* sin navigator: default */
  }
  return 'es';
}

let currentLocale: Locale = detectInitialLocale();

// Suscriptores al cambio de locale. Necesario para reaccionar fuera del árbol
// de React (p.ej. el default de moneda por idioma en CurrencyProvider), ya que
// `t()` lee un global no-reactivo y el selector ES|PT solo re-renderiza su
// propio subárbol. Aditivo: no altera el comportamiento existente.
const localeListeners = new Set<(locale: Locale) => void>();

/** Se suscribe a cambios de locale. Devuelve la función para desuscribirse. */
export function subscribeLocale(cb: (locale: Locale) => void): () => void {
  localeListeners.add(cb);
  return () => localeListeners.delete(cb);
}

/**
 * Fija el locale activo. Con `persist=true` (elección MANUAL del selector) guarda
 * la preferencia para próximas visitas; el auto-sync por perfil de usuario no
 * persiste (para no pisar la detección por navegador de un visitante anónimo).
 */
export function setLocale(locale: Locale, persist = false): void {
  if (persist) {
    try {
      localStorage.setItem(LANG_STORAGE_KEY, locale);
    } catch {
      /* modo privado: la elección vale sólo para esta sesión */
    }
  }
  if (locale === currentLocale) return;
  currentLocale = locale;
  for (const cb of localeListeners) cb(locale);
}

export function getLocale(): Locale {
  return currentLocale;
}

/** Traduce una clave con interpolación opcional `{var}`. */
export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const dict = dictionaries[currentLocale];
  let msg = dict[key] ?? es[key] ?? key;
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      msg = msg.replaceAll(`{${name}}`, String(value));
    }
  }
  return msg;
}
